import { openCompanionPlus, refreshCompanionAccess } from "./companionUpdate";
import type { DiscoveryTag } from "./types";
import {
  collection,
  collectionGroup,
  doc,
  getDoc,
  getDocs,
  setDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  writeBatch,
  increment,
  deleteField,
  onSnapshot,
} from "firebase/firestore";
import type { DocumentReference } from "firebase/firestore";
import { db, storage } from "./firebase";
import { auth } from "./firebase";
import { Capacitor } from "@capacitor/core";
import { deleteObject, getDownloadURL, ref, uploadBytes } from "firebase/storage";
import {
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  signInWithCredential,
  getRedirectResult,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut,
  deleteUser,
} from "firebase/auth";
import { DAILY_PROMPT_FALLBACK, PRIVACY_VERSION, TERMS_VERSION } from "./constants";
import { state } from "./state";
import {
  timestampToDate,
  nowIsoDateInTimezone,
  showToast,
  makeId,
  normalizeJoinCode,
  shortUid,
  activeCommunityScore,
  dedupeIds,
  dedupeById,
} from "./utils";
import type {
  SharedPost,
  CommunityPost,
  Reply,
  ThreadScope,
  SimilarThought,
  ThreadInsight,
  NoteInsight,
  AIReflection,
  AIRiskSignal,
  SupportPattern,
  FriendSuggestion,
  DiscoveryDirection,
  DailyAnchor,
  CompanionMessage,
  CompanionMode,
  CompanionMemoryDraft,
  CompanionMemoryRecord,
  CompanionPortrait,
  CompanionRelatedPattern,
  CompanionJournalDraft,
  CompanionConversationIntent,
  ThinkingPattern,
  ThinkingTheme,
  ThinkingProfileSnapshot,
  ThinkingTensionArc,
  ThoughtMatchProjection,
  ThoughtMatchConversation,
  ThoughtMatchMessage,
  ThoughtMatchExcerptDraft,
} from "./types";

// ── Cache ──────────────────────────────────────────────────────────────────
export const usernameCache = new Map<string, string>();
const profileAvatarCache = new Map<string, string>();
const refreshSuggestionsURL = "https://us-central1-language-of-thoughts.cloudfunctions.net/refreshFriendSuggestions";
const noteAnalysisURL = "https://us-central1-language-of-thoughts.cloudfunctions.net/analyzeNoteAI";
const refreshThinkingTimelineURL = "https://us-central1-language-of-thoughts.cloudfunctions.net/refreshThinkingTimeline";
const createSharedPostReplyURL = "https://us-central1-language-of-thoughts.cloudfunctions.net/createSharedPostReply";
const createCommunityPostReplyURL = "https://us-central1-language-of-thoughts.cloudfunctions.net/createCommunityPostReply";
const resonanceTapURL = "https://us-central1-language-of-thoughts.cloudfunctions.net/recordResonanceTap";
const continueAIConversationURL = "https://us-central1-language-of-thoughts.cloudfunctions.net/continueAIConversation";
const summarizeCompanionMemoryURL = "https://us-central1-language-of-thoughts.cloudfunctions.net/summarizeCompanionMemory";
const generateCompanionJournalURL = "https://us-central1-language-of-thoughts.cloudfunctions.net/generateCompanionJournal";
const translateTextURL = "https://us-central1-language-of-thoughts.cloudfunctions.net/translateText";
const discoveryFeedURL = "https://us-central1-language-of-thoughts.cloudfunctions.net/getDiscoveryFeed?limit=120";
const discoveryDirectionsURL = "https://us-central1-language-of-thoughts.cloudfunctions.net/getDiscoveryDirections";
const thoughtMatchingBaseURL = "https://us-central1-language-of-thoughts.cloudfunctions.net/";
const crossPlatformKeyDocId = "cross_platform_v2";
const crossPlatformIterations = 200000;
const crossPlatformSessionPrefix = "lot.crossplatform.noteKey.";
const crossPlatformPersistentPrefix = "lot.crossplatform.rememberedNoteKey.";
const noteSimilarRefreshWindowMs = 1000 * 60 * 60 * 6;
const publicNoteFallbackCacheWindowMs = 1000 * 60 * 10;
const currentReflectionSchemaVersion = "language-v2";
const standaloneCompanionEntryId = "__standalone__";

let stopThoughtMatchProjectionListener: (() => void) | null = null;
let stopThoughtMatchConversationListener: (() => void) | null = null;
let stopThoughtMatchMessageListener: (() => void) | null = null;
// Guards against concurrent startThoughtMatching() calls (e.g. background
// sign-in hydration racing a Matches-tab click) orphaning listeners — see
// the staleness check inside startThoughtMatching().
let thoughtMatchStartToken = 0;

function currentAnalysisLanguage(): string {
  const raw = String(
    (Array.isArray(navigator.languages) && navigator.languages[0]) ||
    navigator.language ||
    "en",
  ).trim().replace("_", "-").toLowerCase();
  if (raw.startsWith("zh-hant") || raw === "zh-tw" || raw === "zh-hk" || raw === "zh-mo") return "zh-Hant";
  if (raw.startsWith("zh-hans") || raw === "zh" || raw === "zh-cn" || raw === "zh-sg") return "zh-Hans";
  if (raw.startsWith("ja")) return "ja";
  if (raw.startsWith("es")) return "es";
  if (raw.startsWith("en")) return "en";
  return raw ? raw.slice(0, 16) : "en";
}

function reflectionContentMatchesLanguage(reflection: AIReflection | undefined, language: string): boolean {
  if (!reflection) return false;
  const userFacingText = [
    reflection.summary,
    reflection.tone,
    reflection.tension,
    reflection.logic,
    ...(reflection.themes || []),
  ].map((value) => String(value || "")).join(" ");
  const normalized = currentAnalysisLanguageFromRaw(language);
  if (normalized === "zh-Hant" || normalized === "zh-Hans") return /[\u3400-\u9fff]/.test(userFacingText);
  if (normalized === "ja") return /[\u3040-\u30ff\u3400-\u9fff]/.test(userFacingText);
  return true;
}

function insightMatchesCurrentAnalysisLanguage(insight: typeof state.noteInsights[string] | undefined): boolean {
  if (insight?.reflectionSchemaVersion !== currentReflectionSchemaVersion) return false;
  const language = String(insight?.requestedOutputLanguage || insight?.reflection?.language || "").trim();
  return Boolean(language) &&
    currentAnalysisLanguage() === currentAnalysisLanguageFromRaw(language) &&
    reflectionContentMatchesLanguage(insight?.reflection, language);
}

function currentAnalysisLanguageFromRaw(language: string): string {
  const raw = language.trim().replace("_", "-").toLowerCase();
  if (raw.startsWith("zh-hant") || raw === "zh-tw" || raw === "zh-hk" || raw === "zh-mo") return "zh-Hant";
  if (raw.startsWith("zh-hans") || raw === "zh" || raw === "zh-cn" || raw === "zh-sg") return "zh-Hans";
  if (raw.startsWith("ja")) return "ja";
  if (raw.startsWith("es")) return "es";
  if (raw.startsWith("en")) return "en";
  return raw ? raw.slice(0, 16) : "en";
}

type PublicAIRecord = {
  postId: string;
  textPreview: string;
  replyCount: number;
  createdAt: Date;
  reflection?: {
    themes?: string[];
    tension?: string;
  };
};

let recentPublicAIRecordsCache: { fetchedAt: number; records: PublicAIRecord[] } | null = null;

const blockedContentTerms = [
  "kill yourself",
  "kys",
  "nazi",
  "white power",
  "terrorist",
  "rape",
  "pedo",
  "doxx",
  "swatting",
];

// Sexually explicit content. Crude, unambiguous terms only: people must be
// able to write about desire, bodies, and relationships. Mirrors the iOS
// ContentSafetyFilter and the Discovery generator's EXPLICIT_PATTERNS —
// keep the three in step.
const explicitContentPatterns: RegExp[] = [
  /\bbeat(ed|ing)?\s+my\s+meat\b/iu,
  /\b(jerk|jack)(ed|ing)?\s+off\b/iu,
  /\bgooning\b/iu,
  /\bgoon(ed)?\s+to\b/iu,
  /\bblow\s?job\b/iu,
  /\bhand\s?job\b/iu,
  /\bsuck(ed|ing)?\s+(my|his|your|a)\s+(dick|cock)\b/iu,
  /\bdick\s+pic\b/iu,
  /\bcum\s?shot\b/iu,
  /\bcumming\b/iu,
  /\bcreampie\b/iu,
  /\bporn\w*\b/iu,
  /\bhentai\b/iu,
  /\bonlyfans\b/iu,
  /\bfuck(ed|ing)?\s+(me|her|him)\b/iu,
  /打飞机|撸管|口交|做爱视频/u,
];

export function containsObjectionableContent(text: string): boolean {
  const normalized = String(text || "").toLowerCase();
  return blockedContentTerms.some((term) => normalized.includes(term))
    || explicitContentPatterns.some((pattern) => pattern.test(normalized));
}

function hiddenFeedStorageKey(uid: string): string {
  return `lot.hiddenFeedPostIds.${uid}`;
}

function loadHiddenFeedPostIds(uid: string): string[] {
  try {
    const raw = window.localStorage.getItem(hiddenFeedStorageKey(uid));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.map((id) => String(id)).filter(Boolean) : [];
  } catch {
    return [];
  }
}

function saveHiddenFeedPostIds(uid: string, ids: string[]): void {
  try {
    window.localStorage.setItem(hiddenFeedStorageKey(uid), JSON.stringify([...new Set(ids.filter(Boolean))]));
  } catch {
    // Local hiding is best-effort; never block safety/report flows on storage failure.
  }
}

function savedPostsStorageKey(uid: string): string {
  return `lot.savedPostIds.${uid}`;
}

function loadLocalSavedPostIds(uid: string): string[] {
  try {
    const raw = window.localStorage.getItem(savedPostsStorageKey(uid));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.map((id) => String(id)).filter(Boolean) : [];
  } catch {
    return [];
  }
}

function saveLocalSavedPostIds(uid: string, ids: string[]): void {
  try {
    window.localStorage.setItem(savedPostsStorageKey(uid), JSON.stringify([...new Set(ids.filter(Boolean))]));
  } catch {
    // Saved thoughts remain best-effort locally when storage is unavailable.
  }
}

function removePostFromFeedState(postId: string): void {
  state.feedPosts = state.feedPosts.filter((post) => post.id !== postId);
  (["all", "friends"] as const).forEach((filter) => {
    state.feedSectionIds[filter] = {
      active: state.feedSectionIds[filter].active.filter((post) => post.id !== postId),
      newThoughts: state.feedSectionIds[filter].newThoughts.filter((post) => post.id !== postId),
      discovery: state.feedSectionIds[filter].discovery.filter((post) => post.id !== postId),
    };
  });
}

// ── Render callback (injected from app.ts to avoid circular imports) ───────
let _render: () => void = () => {};
let _setBusy: (key: string, busy: boolean) => void = () => {};

export function registerServices(renderFn: () => void, setBusyFn: (key: string, busy: boolean) => void): void {
  _render = renderFn;
  _setBusy = setBusyFn;
}

// ── Helper ─────────────────────────────────────────────────────────────────
async function safeRun<T>(task: () => Promise<T>, fallbackMessage?: string): Promise<T | null> {
  try {
    return await task();
  } catch (err) {
    console.error(err);
    if (fallbackMessage) showToast(fallbackMessage);
    return null;
  }
}

function runInBackground(task: () => Promise<void>): void {
  void task().catch((err) => {
    console.error(err);
  });
}

function normalizedAIString(value: unknown): string {
  return String(value || "").trim().toLowerCase();
}

function sharedThemes(sourceThemes: string[] = [], candidateThemes: string[] = []): string[] {
  const source = new Set(sourceThemes.map(normalizedAIString).filter(Boolean));
  const candidate = candidateThemes
    .map((theme) => String(theme || "").trim())
    .filter((theme) => theme.length > 0);
  return candidate.filter((theme) => source.has(normalizedAIString(theme))).slice(0, 2);
}

function significantTokens(text: string): Set<string> {
  return new Set(
    String(text || "")
      .toLowerCase()
      .split(/[^a-z0-9]+/i)
      .map((token) => token.trim())
      .filter((token) => token.length >= 4),
  );
}

function sharedTokenCount(lhs: string, rhs: string): number {
  const left = significantTokens(lhs);
  if (!left.size) return 0;
  const right = significantTokens(rhs);
  let count = 0;
  left.forEach((token) => {
    if (right.has(token)) count += 1;
  });
  return count;
}

function buildClientMatchReason(
  sourceReflection: { themes?: string[]; canonicalThemes?: string[]; tension?: string },
  candidateReflection: { themes?: string[]; canonicalThemes?: string[]; tension?: string },
): string {
  const sourceTension = normalizedAIString(sourceReflection?.tension);
  const candidateTension = normalizedAIString(candidateReflection?.tension);
  if (sourceTension && candidateTension && sourceTension === candidateTension) {
    return `Shared tension: ${candidateReflection?.tension || sourceReflection?.tension || ""}`.trim();
  }
  const themes = sharedThemes(
    reflectionThemesForMatching(sourceReflection),
    reflectionThemesForMatching(candidateReflection),
  );
  if (themes.length > 0) {
    return `Shared theme: ${themes.join(" • ")}`;
  }
  if (candidateReflection?.tension) {
    return `Shared tension: ${candidateReflection.tension}`;
  }
  return "A similar line of thinking";
}

function displayMatchReason(reason: string | undefined): string {
  const trimmed = String(reason || "").trim();
  if (/^shared tension:/i.test(trimmed) || /^shared theme:/i.test(trimmed)) return trimmed;
  if (/^similar tension:/i.test(trimmed)) {
    const value = trimmed.replace(/^similar tension:/i, "").trim();
    return value ? `Shared tension: ${value}` : "A similar line of thinking";
  }
  return "A similar line of thinking";
}

// Mirrors iOS AIInsightService.isStrongResonance: a reflection-backed match
// (shared tension/theme) is "strong" at a low bar, while a plain token-overlap
// or recency match must clear a much higher score before it counts.
function isStrongResonance(thought: { matchReason?: string; score?: number }): boolean {
  const reason = displayMatchReason(thought.matchReason).toLowerCase();
  if (reason.startsWith("shared tension:") || reason.startsWith("shared theme:")) {
    return (thought.score || 0) >= 0.28;
  }
  return (thought.score || 0) >= 0.42;
}

function immediateResonanceThoughts(sourceText: string, strongOnly: boolean): SimilarThought[] {
  const sourceTokens = significantTokens(sourceText);
  const pool = (state.feedPosts.length > 0
    ? state.feedPosts
        .filter((post) => post.visibility === "public" && post.text.trim())
        .map((post) => ({
          postId: post.id,
          scope: "sharedPost",
          textPreview: post.text.slice(0, 140),
          replyCount: post.replyCount || 0,
          createdAt: post.sharedAt || post.createdAt,
        }))
    : state.publicThoughtPool.map((post) => ({
        postId: post.postId,
        scope: "sharedPost",
        textPreview: post.textPreview,
        replyCount: post.replyCount || 0,
        createdAt: post.createdAt,
      })))
    .filter((post) => post.textPreview.trim());

  return pool
    .map((post) => {
      const overlap = sharedTokenCount(sourceText, post.textPreview);
      const ageDays = Math.max(0, (Date.now() - timestampToDate(post.createdAt).getTime()) / 86400000);
      const recencyScore = 1 / (1 + ageDays / 10);
      const conversationScore = Math.min((post.replyCount || 0) / 8, 1);
      // Mirror iOS AIInsightService.buildFallbackThoughtCards weighting exactly.
      const score = overlap * 0.22 + conversationScore * 0.12 + recencyScore * 0.08;
      const reason = overlap > 0 && sourceTokens.size > 0
        ? "Related public thought"
        : "Recent public thought";
      return {
        postId: post.postId,
        scope: post.scope,
        textPreview: post.textPreview,
        replyCount: post.replyCount,
        matchReason: reason,
        score,
      };
    })
    // iOS publicThoughtShelf ranks the full pool then takes the top (limit*2)
    // candidates BEFORE applying the strongOnly gate, then trims to limit.
    .sort((a, b) => {
      const diff = (b.score || 0) - (a.score || 0);
      if (diff !== 0) return diff;
      return (b.replyCount || 0) - (a.replyCount || 0);
    })
    .slice(0, 6)
    .filter((thought) => !strongOnly || isStrongResonance(thought))
    .slice(0, 3);
}

async function fetchRecentPublicAIRecords(limitCount = 200): Promise<PublicAIRecord[]> {
  const now = Date.now();
  if (recentPublicAIRecordsCache && (now - recentPublicAIRecordsCache.fetchedAt) < publicNoteFallbackCacheWindowMs) {
    return recentPublicAIRecordsCache.records;
  }
  const snap = await safeRun(async () => getDocs(query(collection(db, "sharedPosts"), orderBy("sharedAt", "desc"), limit(limitCount))));
  if (!snap || snap.empty) {
    recentPublicAIRecordsCache = { fetchedAt: now, records: [] };
    return [];
  }
  const rawRecords = await Promise.all(snap.docs.map(async (postDoc) => {
    const data = postDoc.data() as Record<string, unknown>;
    if (String(data.visibility || "public") !== "public") return null;
    const fallbackPreview = String(data.text || "").trim().slice(0, 140);
    const aiSnap = await safeRun(async () => getDoc(doc(db, "sharedPosts", postDoc.id, "ai", "v1")));
    const aiData = aiSnap?.exists() ? aiSnap.data() as Record<string, unknown> : {};
    const aiReady = String(aiData.status || "") === "ready";
    const textPreview = String(aiData.textPreview || "").trim() || fallbackPreview;
    if (!textPreview) return null;
    return {
      postId: postDoc.id,
      textPreview,
      replyCount: Number(data.replyCount || 0),
      createdAt: timestampToDate((data.sharedAt || data.createdAt) as never) || new Date(0),
      reflection: aiReady ? aiData.reflection as { themes?: string[]; tension?: string } | undefined : undefined,
    } as PublicAIRecord;
  }));
  const records = rawRecords.filter((record): record is PublicAIRecord => record !== null);
  state.publicThoughtPool = records.map((record) => ({
    postId: record.postId,
    textPreview: record.textPreview,
    replyCount: record.replyCount,
    createdAt: record.createdAt,
  }));
  recentPublicAIRecordsCache = { fetchedAt: now, records };
  return records;
}

async function augmentNoteInsightWithFallbackSimilarThoughts(
  insight: typeof state.noteInsights[string] | null | undefined,
  sourceText = "",
): Promise<typeof state.noteInsights[string] | null | undefined> {
  if (!insight) return insight;
  if (Array.isArray(insight.similarThoughts) && insight.similarThoughts.length > 0) return insight;
  if (!insight.reflection) return insight;

  const records = await fetchRecentPublicAIRecords();
  if (!records.length) return insight;
  const sourceReflection = insight.reflection;
  const derived = records
    .map((record) => {
      const candidateReflection = record.reflection || {};
      const sourceTension = normalizedAIString(sourceReflection.tension);
      const candidateTension = normalizedAIString(candidateReflection.tension);
      const overlapThemes = sharedThemes(
        reflectionThemesForMatching(sourceReflection),
        reflectionThemesForMatching(candidateReflection),
      );
      const sameTension = Boolean(sourceTension && candidateTension && sourceTension === candidateTension);
      const tokenOverlap = sharedTokenCount(sourceText, record.textPreview);
      if (!sameTension && overlapThemes.length === 0 && tokenOverlap < 2) return null;
      const ageDays = Math.max(0, (Date.now() - record.createdAt.getTime()) / 86400000);
      const recencyScore = 1 / (1 + ageDays / 10);
      const conversationScore = Math.min(record.replyCount / 8, 1);
      const overlapScore = sameTension
        ? 0.64
        : overlapThemes.length > 0
          ? 0.44 + overlapThemes.length * 0.08
          : 0.34 + Math.min(tokenOverlap * 0.05, 0.12);
      const score = Math.min(0.82, Number((overlapScore + conversationScore * 0.08 + recencyScore * 0.06).toFixed(4)));
      return {
        postId: record.postId,
        scope: "sharedPost",
        textPreview: record.textPreview,
        replyCount: record.replyCount,
        matchReason: buildClientMatchReason(sourceReflection, candidateReflection),
        score,
      };
    })
    .filter((item): item is { postId: string; scope: string; textPreview: string; replyCount: number; matchReason: string; score: number } => item !== null)
    .sort((a, b) => b.score - a.score)
    .slice(0, 3);

  const fallback = records
    .slice()
    .sort((a, b) => {
      const left = Math.min(a.replyCount / 8, 1) + (1 / (1 + Math.max(0, (Date.now() - a.createdAt.getTime()) / 86400000) / 10)) * 0.4;
      const right = Math.min(b.replyCount / 8, 1) + (1 / (1 + Math.max(0, (Date.now() - b.createdAt.getTime()) / 86400000) / 10)) * 0.4;
      return right - left;
    })
    .slice(0, 3)
    .map((record) => ({
      postId: record.postId,
      scope: "sharedPost",
      textPreview: record.textPreview,
      replyCount: record.replyCount,
      matchReason: "Related public thought",
      score: 0.31,
    }));

  const finalThoughts = applySimilarThoughtFeedback((derived.length > 0 ? derived : fallback).map(({ score: _score, ...thought }) => thought));
  return finalThoughts.length > 0 ? { ...insight, similarThoughts: finalThoughts } : insight;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

async function refreshFriendSuggestionsNow(): Promise<void> {
  const currentUser = auth.currentUser;
  if (!currentUser) return;
  const token = await currentUser.getIdToken();
  await fetch(refreshSuggestionsURL, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({}),
  });
}

function crossPlatformSessionKey(uid: string): string {
  return `${crossPlatformSessionPrefix}${uid}`;
}

function crossPlatformPersistentKey(uid: string): string {
  return `${crossPlatformPersistentPrefix}${uid}`;
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = "";
  bytes.forEach((byte) => { binary += String.fromCharCode(byte); });
  return btoa(binary);
}

function base64ToBytes(value: string): Uint8Array {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return bytes;
}

function concatBytes(...parts: Uint8Array[]): Uint8Array {
  const total = parts.reduce((sum, part) => sum + part.length, 0);
  const combined = new Uint8Array(total);
  let offset = 0;
  for (const part of parts) {
    combined.set(part, offset);
    offset += part.length;
  }
  return combined;
}

function splitCombinedGCM(bytes: Uint8Array): { iv: Uint8Array; ciphertext: Uint8Array } {
  return { iv: bytes.slice(0, 12), ciphertext: bytes.slice(12) };
}

function toBufferSource(bytes: Uint8Array): ArrayBuffer {
  const buffer = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(buffer).set(bytes);
  return buffer;
}

function readFirestoreBytes(value: unknown): Uint8Array | null {
  if (!value) return null;
  if (value instanceof Uint8Array) return value;
  if (Array.isArray(value)) return Uint8Array.from(value);
  if (typeof value === "string") return base64ToBytes(value);
  const maybe = value as { toUint8Array?: () => Uint8Array; toBase64?: () => string };
  if (typeof maybe.toUint8Array === "function") return maybe.toUint8Array();
  if (typeof maybe.toBase64 === "function") return base64ToBytes(maybe.toBase64());
  return null;
}

async function deriveWrappingKey(passphrase: string, salt: Uint8Array, iterations = crossPlatformIterations): Promise<CryptoKey> {
  const material = await crypto.subtle.importKey("raw", new TextEncoder().encode(passphrase), "PBKDF2", false, ["deriveKey"]);
  return crypto.subtle.deriveKey(
    { name: "PBKDF2", salt: toBufferSource(salt), iterations, hash: "SHA-256" },
    material,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"]
  );
}

async function importContentKey(rawKey: Uint8Array): Promise<CryptoKey> {
  return crypto.subtle.importKey("raw", toBufferSource(rawKey), { name: "AES-GCM" }, false, ["encrypt", "decrypt"]);
}

async function encryptCombinedString(text: string, rawKey: Uint8Array): Promise<string> {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const key = await importContentKey(rawKey);
  const encrypted = new Uint8Array(await crypto.subtle.encrypt({ name: "AES-GCM", iv: toBufferSource(iv) }, key, new TextEncoder().encode(text)));
  return bytesToBase64(concatBytes(iv, encrypted));
}

async function decryptCombinedString(data: Uint8Array, rawKey: Uint8Array): Promise<string> {
  const { iv, ciphertext } = splitCombinedGCM(data);
  const key = await importContentKey(rawKey);
  const decrypted = await crypto.subtle.decrypt({ name: "AES-GCM", iv: toBufferSource(iv) }, key, toBufferSource(ciphertext));
  return new TextDecoder().decode(decrypted);
}

async function wrapContentKey(rawKey: Uint8Array, passphrase: string): Promise<{ wrappedKey: string; salt: string }> {
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const wrappingKey = await deriveWrappingKey(passphrase, salt);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encrypted = new Uint8Array(await crypto.subtle.encrypt({ name: "AES-GCM", iv: toBufferSource(iv) }, wrappingKey, toBufferSource(rawKey)));
  return { wrappedKey: bytesToBase64(concatBytes(iv, encrypted)), salt: bytesToBase64(salt) };
}

async function unwrapContentKey(wrappedKeyBase64: string, saltBase64: string, passphrase: string, iterations = crossPlatformIterations): Promise<Uint8Array> {
  const wrappingKey = await deriveWrappingKey(passphrase, base64ToBytes(saltBase64), iterations);
  const combined = base64ToBytes(wrappedKeyBase64);
  const { iv, ciphertext } = splitCombinedGCM(combined);
  const raw = await crypto.subtle.decrypt({ name: "AES-GCM", iv: toBufferSource(iv) }, wrappingKey, toBufferSource(ciphertext));
  return new Uint8Array(raw);
}

function getUnlockedCrossPlatformKey(): Uint8Array | null {
  if (!state.user) return null;
  const encoded = sessionStorage.getItem(crossPlatformSessionKey(state.user.uid));
  if (encoded) return base64ToBytes(encoded);
  let remembered: string | null = null;
  try {
    remembered = window.localStorage.getItem(crossPlatformPersistentKey(state.user.uid));
  } catch {
    remembered = null;
  }
  if (!remembered) return null;
  sessionStorage.setItem(crossPlatformSessionKey(state.user.uid), remembered);
  return base64ToBytes(remembered);
}

function canWriteWithEncryption(): boolean {
  return Boolean(getUnlockedCrossPlatformKey());
}

function showEncryptionWriteGate(): void {
  showToast("Set up or unlock Note Encryption in Settings before writing.");
}

async function encryptedEntryPayload(
  text: string,
  rawKey: Uint8Array,
  visibility: "private" | "public" | "friends",
  attribution: "anonymous" | "username" = "anonymous"
): Promise<Record<string, unknown>> {
  const now = serverTimestamp();
  return {
    ciphertextBase64: await encryptCombinedString(text, rawKey),
    encryptionVersion: 2,
    keyMode: "cross_platform",
    schemaVersion: 2,
    deleted: false,
    visibility,
    attribution,
    createdAt: now,
    updatedAt: now,
    ...(visibility === "private" ? {} : { sharedAt: now }),
  };
}

function hasRememberedCrossPlatformKey(): boolean {
  if (!state.user) return false;
  try {
    return Boolean(window.localStorage.getItem(crossPlatformPersistentKey(state.user.uid)));
  } catch {
    return false;
  }
}

function storeUnlockedCrossPlatformKey(rawKey: Uint8Array, rememberBrowser = false): void {
  if (!state.user) return;
  const encoded = bytesToBase64(rawKey);
  sessionStorage.setItem(crossPlatformSessionKey(state.user.uid), encoded);
  if (rememberBrowser) {
    try {
      window.localStorage.setItem(crossPlatformPersistentKey(state.user.uid), encoded);
    } catch {
      showToast("This browser could not save the unlock. Notes are unlocked for this session.");
    }
  }
  state.noteEncryption.unlocked = true;
  state.noteEncryption.remembered = hasRememberedCrossPlatformKey();
}

export function forgetRememberedCrossPlatformEncryption(): void {
  if (!state.user) return;
  try {
    window.localStorage.removeItem(crossPlatformPersistentKey(state.user.uid));
  } catch {
    // Best-effort browser cleanup.
  }
  state.noteEncryption.remembered = false;
  showToast("Saved unlock removed from this browser.");
  _render();
}

function clearUnlockedCrossPlatformKey(clearRemembered = false): void {
  if (state.user) {
    sessionStorage.removeItem(crossPlatformSessionKey(state.user.uid));
    if (clearRemembered) {
      try {
        window.localStorage.removeItem(crossPlatformPersistentKey(state.user.uid));
      } catch {
        // Best-effort browser cleanup.
      }
    }
  }
  state.noteEncryption.unlocked = false;
  state.noteEncryption.remembered = clearRemembered ? false : hasRememberedCrossPlatformKey();
}

async function fetchNoteEncryptionState(): Promise<void> {
  if (!state.user) return;
  const prefSnap = await getDoc(doc(db, "users", state.user.uid, "preferences", "encryption"));
  const prefData = prefSnap.exists() ? prefSnap.data() : {};
  const keySnap = await getDoc(doc(db, "users", state.user.uid, "keys", crossPlatformKeyDocId));
  state.noteEncryption = {
    hasEnvelope: keySnap.exists(),
    unlocked: getUnlockedCrossPlatformKey() !== null,
    remembered: hasRememberedCrossPlatformKey(),
    preferredMode: (prefData.mode === "legacy_ios" ? "legacy_ios" : "cross_platform"),
    hasLegacyIOSNotes: state.noteEncryption.hasLegacyIOSNotes,
  };
  state.noteEncryptionResolved = true;
}

export async function setupCrossPlatformEncryption(passphrase: string, confirmation: string, rememberBrowser = false): Promise<void> {
  if (!state.user) return;
  const normalized = passphrase.trim();
  if (normalized.length < 8) { showToast("Use a passphrase with at least 8 characters."); return; }
  if (normalized !== confirmation.trim()) { showToast("Passphrases do not match."); return; }
  if (state.noteEncryption.hasLegacyIOSNotes && !state.noteEncryption.hasEnvelope) {
    showToast("Set this up on iPhone first to migrate older iPhone-only notes.");
    return;
  }
  const rawKey = crypto.getRandomValues(new Uint8Array(32));
  const wrapped = await wrapContentKey(rawKey, normalized);
  await setDoc(doc(db, "users", state.user.uid, "keys", crossPlatformKeyDocId), {
    version: 2,
    wrappedKey: wrapped.wrappedKey,
    salt: wrapped.salt,
    kdf: "pbkdf2_sha256",
    kdfIterations: crossPlatformIterations,
    source: "web_created",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  }, { merge: true });
  await setDoc(doc(db, "users", state.user.uid, "preferences", "encryption"), { mode: "cross_platform", updatedAt: serverTimestamp() }, { merge: true });
  storeUnlockedCrossPlatformKey(rawKey, rememberBrowser);
  await fetchNoteEncryptionState();
  showToast("Cross-platform encryption is ready in this browser.");
  await fetchNotes();
  state.companionContext.loaded = false;
  await loadCompanionContext(true);
}

export async function unlockCrossPlatformEncryption(passphrase: string, rememberBrowser = false): Promise<void> {
  if (!state.user) return;
  const snap = await getDoc(doc(db, "users", state.user.uid, "keys", crossPlatformKeyDocId));
  if (!snap.exists()) { showToast("No cross-platform encryption setup was found."); return; }
  try {
    const data = snap.data();
    const rawKey = await unwrapContentKey(String(data.wrappedKey || ""), String(data.salt || ""), passphrase.trim(), Number(data.kdfIterations || crossPlatformIterations));
    storeUnlockedCrossPlatformKey(rawKey, rememberBrowser);
    await setDoc(doc(db, "users", state.user.uid, "preferences", "encryption"), { mode: "cross_platform", updatedAt: serverTimestamp() }, { merge: true });
    await fetchNoteEncryptionState();
    await fetchNotes();
    state.companionContext.loaded = false;
    await loadCompanionContext(true);
    showToast("Encrypted notes unlocked.");
  } catch {
    showToast("Could not unlock encrypted notes.");
  }
}

export async function changeCrossPlatformEncryptionPassphrase(
  currentPassphrase: string,
  newPassphrase: string,
  confirmation: string,
  rememberBrowser = false
): Promise<void> {
  if (!state.user) return;
  const current = currentPassphrase.trim();
  const next = newPassphrase.trim();
  if (current.length < 8 || next.length < 8) { showToast("Use a passphrase with at least 8 characters."); return; }
  if (next !== confirmation.trim()) { showToast("Passphrases do not match."); return; }
  const keyRef = doc(db, "users", state.user.uid, "keys", crossPlatformKeyDocId);
  const snap = await getDoc(keyRef);
  if (!snap.exists()) { showToast("No cross-platform encryption setup was found."); return; }
  try {
    const data = snap.data();
    const rawKey = await unwrapContentKey(
      String(data.wrappedKey || ""),
      String(data.salt || ""),
      current,
      Number(data.kdfIterations || crossPlatformIterations)
    );
    const wrapped = await wrapContentKey(rawKey, next);
    await setDoc(keyRef, {
      version: 2,
      wrappedKey: wrapped.wrappedKey,
      salt: wrapped.salt,
      kdf: "pbkdf2_sha256",
      kdfIterations: crossPlatformIterations,
      source: "passphrase_changed_web",
      updatedAt: serverTimestamp(),
    }, { merge: true });
    storeUnlockedCrossPlatformKey(rawKey, rememberBrowser);
    await setDoc(doc(db, "users", state.user.uid, "preferences", "encryption"), { mode: "cross_platform", updatedAt: serverTimestamp() }, { merge: true });
    await fetchNoteEncryptionState();
    await fetchNotes();
    state.companionContext.loaded = false;
    await loadCompanionContext(true);
    showToast("Encryption passphrase updated.");
  } catch {
    showToast("Could not change encryption passphrase.");
  }
}

// ── Username hydration ─────────────────────────────────────────────────────
export async function hydrateUsernamesForPosts(posts: SharedPost[]): Promise<SharedPost[]> {
  const toFetch = new Set<string>();
  for (const post of posts) {
    if (post.attribution === "username" && !post.authorUsername && post.authorUid && !usernameCache.has(post.authorUid)) {
      toFetch.add(post.authorUid);
    }
  }
  // Same rule as replies: names are decoration. Guests can't read
  // users/{uid}; signed-in lookups may still fail individually. Neither
  // may take the posts down with them.
  if (state.user && toFetch.size) {
    await Promise.all([...toFetch].map(async (uid) => {
      try {
        const snap = await getDoc(doc(db, "users", uid));
        if (snap.exists()) {
          const username = snap.data().username as string | undefined;
          if (username) usernameCache.set(uid, username);
        }
      } catch { /* fall back to whatever the card carried */ }
    }));
  }
  return posts.map((post) => {
    if (post.authorUsername) return post;
    if (post.attribution === "username" && usernameCache.has(post.authorUid)) {
      return { ...post, authorUsername: usernameCache.get(post.authorUid)! };
    }
    return post;
  });
}

export async function hydrateUsernamesForCommunityPosts(posts: CommunityPost[]): Promise<CommunityPost[]> {
  const toFetch = new Set<string>();
  for (const post of posts) {
    if (!post.authorUsername && post.authorUid && !usernameCache.has(post.authorUid)) {
      toFetch.add(post.authorUid);
    }
  }
  if (state.user && toFetch.size) {
    await Promise.all([...toFetch].map(async (uid) => {
      try {
        const snap = await getDoc(doc(db, "users", uid));
        if (snap.exists()) {
          const username = snap.data().username as string | undefined;
          if (username) usernameCache.set(uid, username);
        }
      } catch { /* fall back below */ }
    }));
  }
  return posts.map((post) => {
    if (post.authorUsername) return post;
    return { ...post, authorUsername: usernameCache.get(post.authorUid) || (state.user ? shortUid(post.authorUid) : "Member") };
  });
}

export async function hydrateUsernamesForReplies(replies: Reply[], forceUsername = false): Promise<Reply[]> {
  const toFetch = new Set<string>();
  for (const reply of replies) {
    const shouldShowProfile = forceUsername || reply.attribution === "username";
    const needsUsername = !reply.authorUsername && !usernameCache.has(reply.authorUid);
    const needsAvatar = !reply.authorAvatarURL && !profileAvatarCache.has(reply.authorUid);
    if (shouldShowProfile && reply.authorUid && (needsUsername || needsAvatar)) {
      toFetch.add(reply.authorUid);
    }
  }
  // Names and avatars are decoration on top of the replies, never a
  // precondition for them. `users/{uid}` is readable only when signed in, so
  // a guest's lookups would reject — and an unguarded Promise.all here used
  // to throw the whole conversation away ("Could not load responses") for
  // exactly the visitor a shared link brings in. Guests skip the lookups;
  // everyone else tolerates individual failures.
  if (state.user && toFetch.size) {
    await Promise.all([...toFetch].map(async (uid) => {
      try {
        const snap = await getDoc(doc(db, "users", uid));
        if (snap.exists()) {
          const data = snap.data() as Record<string, unknown>;
          const username = data.username as string | undefined;
          const profile = profileSummaryFromData(data);
          if (username) usernameCache.set(uid, username);
          if (profile.avatarURL) profileAvatarCache.set(uid, profile.avatarURL);
        }
      } catch {
        // Unresolved names fall back below; the reply still renders.
      }
    }));
  }
  return replies.map((reply) => {
    if (!forceUsername && reply.attribution !== "username") {
      return { ...reply, authorUsername: "", authorAvatarURL: undefined };
    }
    return {
      ...reply,
      attribution: "username",
      // A signed-out reader can't resolve profiles; "Member" reads as a
      // person, where a uid fragment reads as a bug.
      authorUsername: reply.authorUsername || usernameCache.get(reply.authorUid) || (state.user ? shortUid(reply.authorUid) : "Member"),
      authorAvatarURL: reply.authorAvatarURL || profileAvatarCache.get(reply.authorUid),
    };
  });
}

function parseSharedPostFromCard(card: Record<string, unknown>): SharedPost | null {
  const id = String(card?.postId || card?.id || "").trim();
  const text = String(card?.text || card?.textPreview || "").trim();
  if (!id || !card?.authorUid || !text) return null;
  const meta = card?.cardMeta as Record<string, unknown> | undefined;
  return {
    id,
    authorUid: card.authorUid as string,
    authorUsername: "",
    text,
    visibility: (card.visibility as string) || "public",
    attribution: ((card.attribution as string) || "anonymous") as "anonymous" | "username",
    createdAt: timestampToDate(card.createdAt as never),
    sharedAt: timestampToDate((card.sharedAt || card.createdAt) as never),
    replyCount: Number(card.replyCount || 0),
    resonanceCount: Number(card.resonanceCount || 0),
    uniqueReplierCount: Number(card.uniqueReplierCount || 0),
    uniqueViewerCount: Number(card.uniqueViewerCount || 0),
    maxReplyDepth: Number(card.maxReplyDepth || 0),
    heatScore: Number(card.heatScore || 0),
    replyRate: Number(card.replyRate || 0),
    cardResponseLabel: (meta?.responseLabel as string) || null,
    cardDiscussingText: (meta?.discussingText as string) || null,
    allowsAnonymousReplies: card.allowsAnonymousReplies === true || ((card.attribution as string) || "anonymous") === "anonymous",
    anonymousColorIndex: card.anonymousColorIndex == null ? null : Number(card.anonymousColorIndex),
  };
}

function parseDailyAnchor(data: Record<string, unknown>): DailyAnchor | null {
  const postId = String(data.postId || "").trim();
  const text = String(data.text || data.textPreview || "").trim();
  if (!postId || !text) return null;
  // selectedAt is a Firestore Timestamp — convert to JS Date for display.
  const rawTs = data.selectedAt as { toDate?: () => Date } | null | undefined;
  const selectedAt = rawTs?.toDate?.() ?? null;
  return {
    postId,
    text,
    originalText: text,
    isTranslated: false,
    showOriginal: false,
    date: String(data.date || ""),
    selectedAt,
    attribution: String(data.attribution || "anonymous"),
    authorUid: String(data.authorUid || "").trim() || undefined,
  };
}

export async function loadDailyAnchor(): Promise<void> {
  let snap;
  try {
    snap = await getDoc(doc(db, "config", "dailyAnchor"));
  } catch (error) {
    // Daily Anchor is optional for guests. Some production rule versions
    // restrict this document to signed-in users, which should quietly omit
    // the card rather than surface a console error during ordinary browsing.
    if (state.user) console.warn("Daily Anchor unavailable", error);
    return;
  }
  if (!snap.exists()) return;
  const parsed = parseDailyAnchor(snap.data() as Record<string, unknown>);
  if (!parsed) return;
  const targetLanguage = currentAnalysisLanguage();
  if (targetLanguage !== "en" && auth.currentUser) {
    const cacheKey = `lot.anchorTranslation.v1.${targetLanguage}.${parsed.postId}.${parsed.date}`;
    let translatedText = "";
    try {
      translatedText = String(window.localStorage.getItem(cacheKey) || "").trim();
    } catch {
      translatedText = "";
    }
    if (!translatedText) {
      try {
        const token = await auth.currentUser.getIdToken();
        const response = await fetch(translateTextURL, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            text: parsed.originalText,
            outputLanguage: targetLanguage,
          }),
        });
        if (response.ok) {
          const json = await response.json() as { translatedText?: string };
          translatedText = String(json.translatedText || "").trim();
          if (translatedText) {
            try {
              window.localStorage.setItem(cacheKey, translatedText);
            } catch {
              // Translation still works when local storage is unavailable.
            }
          }
        }
      } catch {
        translatedText = "";
      }
    }
    if (translatedText) {
      parsed.text = translatedText;
      parsed.isTranslated = translatedText !== parsed.originalText;
    }
  }
  state.dailyAnchor = parsed;
  _render();
}

export async function loadResonancedPosts(): Promise<void> {
  if (!state.user) return;
  const snap = await safeRun(async () => getDocs(collection(db, "users", state.user!.uid, "resonanceTaps")));
  if (!snap) return;
  state.resonatedPostIds = new Set(snap.docs.map((docSnap) => docSnap.id));
  _render();
}

export async function loadSavedPosts(renderAfterLoad = true): Promise<void> {
  if (!state.user) return;
  const uid = state.user.uid;
  state.savedPostIds = new Set(loadLocalSavedPostIds(uid));

  const snap = await safeRun(async () => getDocs(collection(db, "users", uid, "savedPosts")));
  if (snap) {
    const ids = snap.docs.map((docSnap) => docSnap.id).filter(Boolean);
    state.savedPostIds = new Set(ids);
    saveLocalSavedPostIds(uid, ids);
  }
  if (renderAfterLoad) _render();
}

export async function toggleSavedPost(postId: string): Promise<void> {
  if (!state.user) {
    showToast("Sign in to save thoughts.");
    return;
  }

  const uid = state.user.uid;
  const nextSet = new Set(state.savedPostIds);
  const wasSaved = nextSet.has(postId);
  if (wasSaved) nextSet.delete(postId);
  else nextSet.add(postId);

  state.savedPostIds = nextSet;
  saveLocalSavedPostIds(uid, Array.from(nextSet));
  _render();

  try {
    const savedRef = doc(db, "users", uid, "savedPosts", postId);
    if (wasSaved) {
      await deleteDoc(savedRef);
      showToast("Removed from saved thoughts.");
    } else {
      await setDoc(savedRef, { postId, savedAt: serverTimestamp() }, { merge: true });
      showToast("Saved.");
    }
  } catch (error) {
    // Keep the local save state rather than punishing the tap. Sync can catch
    // up once rules/network are healthy, and the user still gets the benefit.
    console.warn("Saved thought sync failed", error);
    showToast("Saved on this device.");
  }
}

function currentResonanceCount(postId: string, baseCount = 0): number {
  return Math.max(Number(state.resonanceCountOverrides[postId] ?? baseCount ?? 0), 0);
}

function updateResonanceCountInPost(post: SharedPost | CommunityPost, postId: string, nextCount: number): SharedPost | CommunityPost {
  if (post.id !== postId || !("resonanceCount" in post)) return post;
  return { ...post, resonanceCount: nextCount };
}

function applyResonanceDelta(postId: string, delta: number): void {
  const feedPost = state.feedPosts.find((post) => post.id === postId);
  const base = currentResonanceCount(postId, feedPost?.resonanceCount || 0);
  const next = Math.max(base + delta, 0);
  state.resonanceCountOverrides = { ...state.resonanceCountOverrides, [postId]: next };
  state.feedPosts = state.feedPosts.map((post) => updateResonanceCountInPost(post, postId, next) as SharedPost);
  if (state.publicProfile) {
    state.publicProfile = {
      ...state.publicProfile,
      posts: state.publicProfile.posts.map((post) => updateResonanceCountInPost(post, postId, next) as SharedPost),
    };
  }
  for (const sectionKey of ["all", "friends"] as const) {
    state.feedSectionIds[sectionKey] = {
      active: state.feedSectionIds[sectionKey].active.map((post) => updateResonanceCountInPost(post, postId, next) as SharedPost),
      newThoughts: state.feedSectionIds[sectionKey].newThoughts.map((post) => updateResonanceCountInPost(post, postId, next) as SharedPost),
      discovery: state.feedSectionIds[sectionKey].discovery.map((post) => updateResonanceCountInPost(post, postId, next) as SharedPost),
    };
  }
  if (state.threadModal?.scope === "shared") {
    state.threadModal = {
      ...state.threadModal,
      post: updateResonanceCountInPost(state.threadModal.post, postId, next),
    };
  }
}

function revertResonanceTap(postId: string, wasResonated: boolean, delta: number): void {
  const nextSet = new Set(state.resonatedPostIds);
  if (wasResonated) nextSet.add(postId);
  else nextSet.delete(postId);
  state.resonatedPostIds = nextSet;
  applyResonanceDelta(postId, -delta);
}

export async function tapResonance(postId: string): Promise<void> {
  if (!state.user) {
    showToast("Sign in to say you felt it.");
    return;
  }
  const post = state.feedPosts.find((item) => item.id === postId)
    || (state.threadModal?.scope === "shared" && state.threadModal.post.id === postId ? state.threadModal.post as SharedPost : null)
    || state.publicProfile?.posts.find((item) => item.id === postId)
    || null;
  if (!post || post.authorUid === state.user.uid) return;

  const wasResonated = state.resonatedPostIds.has(postId);
  const delta = wasResonated ? -1 : 1;
  const nextSet = new Set(state.resonatedPostIds);
  if (wasResonated) nextSet.delete(postId);
  else nextSet.add(postId);
  state.resonatedPostIds = nextSet;
  applyResonanceDelta(postId, delta);
  _render();

  const token = await auth.currentUser?.getIdToken().catch(() => null);
  if (!token) {
    revertResonanceTap(postId, wasResonated, delta);
    _render();
    return;
  }

  try {
    const response = await fetch(resonanceTapURL, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ postId }),
    });

    if (response.status >= 400 && response.status < 500) {
      revertResonanceTap(postId, wasResonated, delta);
      showToast("Could not mark this thought.");
      _render();
      return;
    }

    if (response.ok) {
      const json = await response.json().catch(() => null) as { resonated?: boolean } | null;
      if (typeof json?.resonated === "boolean" && json.resonated !== !wasResonated) {
        revertResonanceTap(postId, wasResonated, delta);
        _render();
      }
    }
  } catch {
    // Keep optimistic state on network errors, matching iOS. Firestore catches up later.
  }
}

function parseCommunityPostFromCard(card: Record<string, unknown>): CommunityPost | null {
  const id = String(card?.postId || card?.id || "").trim();
  const text = String(card?.text || card?.textPreview || "").trim();
  if (!id || !card?.authorUid || !text) return null;
  const meta = card?.cardMeta as Record<string, unknown> | undefined;
  return {
    id,
    authorUid: card.authorUid as string,
    authorUsername: "",
    text,
    attribution: ((card.attribution as string) || "anonymous") as "anonymous" | "username",
    visibility: "community",
    createdAt: timestampToDate(card.createdAt as never),
    sharedAt: timestampToDate(card.createdAt as never),
    replyCount: Number(card.replyCount || 0),
    uniqueReplierCount: Number(card.uniqueReplierCount || 0),
    uniqueViewerCount: Number(card.uniqueViewerCount || 0),
    maxReplyDepth: Number(card.maxReplyDepth || 0),
    heatScore: Number(card.heatScore || 0),
    replyRate: Number(card.replyRate || 0),
    cardResponseLabel: (meta?.responseLabel as string) || null,
    cardDiscussingText: (meta?.discussingText as string) || null,
    allowsAnonymousReplies: card.allowsAnonymousReplies === true || ((card.attribution as string) || "anonymous") === "anonymous",
    anonymousColorIndex: card.anonymousColorIndex == null ? null : Number(card.anonymousColorIndex),
  };
}

// ── Auth helpers ───────────────────────────────────────────────────────────
function shouldFallbackToRedirect(error: unknown): boolean {
  const code = String((error as { code?: string })?.code || "");
  return (
    code === "auth/popup-blocked" ||
    code === "auth/popup-closed-by-user" ||
    code === "auth/cancelled-popup-request" ||
    code === "auth/operation-not-supported-in-this-environment"
  );
}

function currentAuthHandlerUrl(): string {
  const domain = auth?.config?.authDomain || "language-of-thoughts.firebaseapp.com";
  return `https://${domain}/__/auth/handler`;
}

function authFailureMessage(error: unknown, providerName: string): string {
  const code = String((error as { code?: string })?.code || "");
  const message = String((error as { message?: string })?.message || "").toLowerCase();
  if (code === "auth/unauthorized-domain")
    return `${providerName} sign-in is blocked for ${window.location.host}. Add this host in Firebase Auth authorized domains.`;
  if (code === "auth/operation-not-allowed")
    return `${providerName} sign-in is not enabled in Firebase Auth.`;
  if (code === "auth/popup-closed-by-user")
    return `${providerName} sign-in was closed before completing.`;
  if (code === "auth/popup-blocked")
    return `${providerName} popup was blocked by your browser.`;
  if (
    providerName === "Apple" &&
    (code === "auth/invalid-credential" ||
      code === "auth/invalid-app-credential" ||
      code === "auth/invalid-oauth-client-id" ||
      message.includes("invalid_request") ||
      message.includes("invalid web redirect url") ||
      message.includes("web redirect url"))
  ) {
    return `Apple sign-in redirect mismatch. Set Return URL to ${currentAuthHandlerUrl()} in Apple Developer.`;
  }
  if (providerName === "Apple")
    return `Apple sign-in config issue. Verify Service ID + Return URL ${currentAuthHandlerUrl()}.`;
  return `${providerName} sign-in failed.`;
}

function shouldPreferRedirect(providerName: string): boolean {
  if (Capacitor.isNativePlatform()) return true;
  if (providerName !== "Apple") return false;
  return /iphone|ipad|ipod|android/.test(navigator.userAgent.toLowerCase());
}

async function runProviderAuth(provider: GoogleAuthProvider | OAuthProvider, providerName: string, busyKey: string): Promise<void> {
  _setBusy(busyKey, true);
  try {
    if (providerName === "Apple" && window.location.protocol !== "https:" && window.location.hostname !== "localhost") {
      showToast("Apple sign-in requires HTTPS.");
      return;
    }
    if (shouldPreferRedirect(providerName)) {
      showToast(`Opening ${providerName} sign-in...`);
      await signInWithRedirect(auth, provider);
      return;
    }
    await signInWithPopup(auth, provider);
  } catch (error) {
    if (shouldFallbackToRedirect(error)) {
      try {
        await signInWithRedirect(auth, provider);
        return;
      } catch (redirectError) {
        showToast(authFailureMessage(redirectError, providerName));
        return;
      }
    }
    showToast(authFailureMessage(error, providerName));
  } finally {
    _setBusy(busyKey, false);
  }
}

export async function signInWithGoogle(): Promise<void> {
  // On Android, Google blocks OAuth inside a WebView (Error 403 disallowed_useragent).
  // Use the native Google Sign-In SDK via @codetrix-studio/capacitor-google-auth which
  // opens Chrome Custom Tabs / the system account picker instead.
  if (Capacitor.getPlatform() === "android") {
    _setBusy("auth-google", true);
    try {
      const { GoogleAuth } = await import("@codetrix-studio/capacitor-google-auth");
      await GoogleAuth.initialize();
      const googleUser = await GoogleAuth.signIn();
      const idToken = googleUser?.authentication?.idToken;
      if (!idToken) throw new Error("Google sign-in returned no ID token.");
      const credential = GoogleAuthProvider.credential(idToken);
      await signInWithCredential(auth, credential);
    } catch (error) {
      showToast(authFailureMessage(error, "Google"));
    } finally {
      _setBusy("auth-google", false);
    }
    return;
  }
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: "select_account" });
  await runProviderAuth(provider, "Google", "auth-google");
}

export async function signInWithApple(): Promise<void> {
  const provider = new OAuthProvider("apple.com");
  provider.addScope("email");
  provider.addScope("name");
  const locale = String(navigator.language || "").trim();
  if (locale) provider.setCustomParameters({ locale });
  await runProviderAuth(provider, "Apple", "auth-apple");
}

export async function signInWithEmail(email: string, password: string): Promise<void> {
  _setBusy("auth-email", true);
  try {
    if (state.authMode === "signin") {
      await signInWithEmailAndPassword(auth, email, password);
    } else {
      await createUserWithEmailAndPassword(auth, email, password);
    }
  } catch (error) {
    console.error(error);
    showToast((error as { message?: string })?.message || "Could not complete email auth.");
  } finally {
    _setBusy("auth-email", false);
  }
}

export async function resetEmailPassword(email: string): Promise<void> {
  if (!email) { showToast("Enter your email in the form first."); return; }
  _setBusy("auth-reset", true);
  try {
    await sendPasswordResetEmail(auth, email);
    showToast("Password reset email sent.");
  } catch {
    showToast("Could not send reset email.");
  } finally {
    _setBusy("auth-reset", false);
  }
}

export async function runSignOut(): Promise<void> {
  clearUnlockedCrossPlatformKey();
  if (Capacitor.isNativePlatform()) {
    await deactivateNativePushToken().catch(() => {});
  }
  try { await signOut(auth); }
  catch { showToast("Sign out failed."); }
}

export { getRedirectResult, auth as authInstance };

// ── Native app integration ─────────────────────────────────────────────────
const nativePushDeviceStorageKey = "lot.nativePushDeviceId";

function getOrCreateNativePushDeviceId(): string {
  try {
    const existing = window.localStorage.getItem(nativePushDeviceStorageKey);
    if (existing) return existing;
    const next = `android-${makeId()}-${makeId()}`;
    window.localStorage.setItem(nativePushDeviceStorageKey, next);
    return next;
  } catch {
    return `android-${shortUid(state.user?.uid || "device")}`;
  }
}

export async function saveNativePushToken(token: string, platform = "android"): Promise<void> {
  if (!state.user || !token.trim()) return;
  const deviceId = getOrCreateNativePushDeviceId();
  const ref = doc(db, "fcmTokens", state.user.uid, "devices", deviceId);
  const existing = await safeRun(() => getDoc(ref));
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  await setDoc(ref, {
    token: token.trim(),
    fcmToken: token.trim(),
    platform,
    provider: "capacitor",
    appId: "net.superable.lot",
    active: true,
    timezone,
    updatedAt: serverTimestamp(),
    ...(existing?.exists() ? {} : { createdAt: serverTimestamp() }),
  }, { merge: true });
}

export async function deactivateNativePushToken(): Promise<void> {
  if (!state.user) return;
  const deviceId = getOrCreateNativePushDeviceId();
  await setDoc(doc(db, "fcmTokens", state.user.uid, "devices", deviceId), {
    active: false,
    updatedAt: serverTimestamp(),
  }, { merge: true });
}

// ── Profile ────────────────────────────────────────────────────────────────
async function resolveUsernameFromIndex(uid: string): Promise<string> {
  const snap = await safeRun(async () => {
    const q = query(collection(db, "usernames"), where("uid", "==", uid), limit(1));
    return getDocs(q);
  });
  if (!snap || snap.empty) return "";
  return (snap.docs[0]?.id || "").trim();
}

export async function fetchProfile(): Promise<void> {
  if (!state.user) return;
  const uid = state.user.uid;
  const profileRef = doc(db, "users", uid);
  const snap = await getDoc(profileRef);

  let profileData = snap.exists() ? snap.data() : {};
  let username = ((profileData.username || profileData.profile?.username || "") as string).trim();
  let usernameLower = ((profileData.usernameLower || username.toLowerCase()) as string).trim();

  if (!username) {
    const fallback = await resolveUsernameFromIndex(uid);
    if (fallback) {
      username = fallback;
      usernameLower = fallback.toLowerCase();
      await setDoc(profileRef, { uid, username, usernameLower, createdAt: profileData.createdAt || serverTimestamp(), updatedAt: serverTimestamp() }, { merge: true });
      profileData = { ...profileData, username, usernameLower };
    }
  }

  state.profile = {
    uid,
    username,
    usernameLower,
    profile: (profileData.profile as {
      displayName?: string;
      bio?: string;
      avatarURL?: string;
      avatarStoragePath?: string;
      avatarUpdatedAt?: typeof profileData.avatarUpdatedAt;
    }) || {},
    acceptedTermsVersion: (profileData.acceptedTermsVersion as string) || null,
    acceptedPrivacyVersion: (profileData.acceptedPrivacyVersion as string) || null,
  };

  if (state.profile.username) usernameCache.set(uid, state.profile.username);
  const currentAvatarURL = state.profile.profile?.avatarURL;
  if (currentAvatarURL) profileAvatarCache.set(uid, currentAvatarURL);
}

export async function saveUsername(usernameRaw: string): Promise<void> {
  if (!state.user) { showToast("Please sign in first."); return; }
  const username = usernameRaw.trim();
  if (!/^[A-Za-z0-9][A-Za-z0-9_]{2,19}$/.test(username)) {
    showToast("Username must be 3-20 chars, letters/numbers/underscore.");
    return;
  }
  const uid = state.user.uid;
  const lower = username.toLowerCase();
  const currentLower = state.profile?.usernameLower || "";
  _setBusy("username", true);
  try {
    const usernameRef = doc(db, "usernames", lower);
    const usernameSnap = await getDoc(usernameRef);
    if (usernameSnap.exists() && (usernameSnap.data().uid as string) !== uid) {
      showToast("That username is already taken.");
      return;
    }
    const batch = writeBatch(db);
    batch.set(usernameRef, { uid, reservedAt: serverTimestamp() }, { merge: true });
    const profileRef = doc(db, "users", uid);
    batch.set(profileRef, { uid, username, usernameLower: lower, createdAt: serverTimestamp(), updatedAt: serverTimestamp(), acceptedTermsVersion: TERMS_VERSION, acceptedPrivacyVersion: PRIVACY_VERSION, legalAcceptedAt: serverTimestamp() }, { merge: true });
    if (currentLower && currentLower !== lower) batch.delete(doc(db, "usernames", currentLower));
    await batch.commit();
    state.profile = { ...(state.profile || { uid, username: "", usernameLower: "", profile: {}, acceptedTermsVersion: null, acceptedPrivacyVersion: null }), uid, username, usernameLower: lower, acceptedTermsVersion: TERMS_VERSION, acceptedPrivacyVersion: PRIVACY_VERSION };
    usernameCache.set(uid, username);
    showToast("Username saved.");
    await hydrateAppData();
  } catch { showToast("Could not save username."); }
  finally { _setBusy("username", false); }
}

export async function fetchNotificationPreferences(): Promise<void> {
  if (!state.user) return;
  const prefRef = doc(db, "users", state.user.uid, "preferences", "notifications");
  const legacyRef = doc(db, "users", state.user.uid, "preferences", "default");
  const [snap, legacySnap] = await Promise.all([getDoc(prefRef), getDoc(legacyRef)]);
  const d = snap.exists() ? snap.data() : {};
  const legacy = legacySnap.exists() ? legacySnap.data() : {};
  state.notificationPrefs = {
    friendPost: d.friendPostsEnabled ?? legacy.friendPost ?? true,
    friendRequest: d.friendRequestsEnabled ?? legacy.friendRequest ?? true,
    communityInvite: d.communityInvitesEnabled ?? legacy.communityInvite ?? true,
    communityPost: d.communityPostsEnabled ?? legacy.communityPost ?? true,
    dailyPrompt: d.morningPromptEnabled ?? legacy.dailyPrompt ?? true,
  };
}

export async function saveNotificationPreference(key: string, value: boolean): Promise<void> {
  if (!state.user) return;
  const fieldMap: Record<string, string> = {
    friendPost: "friendPostsEnabled",
    friendRequest: "friendRequestsEnabled",
    communityInvite: "communityInvitesEnabled",
    communityPost: "communityPostsEnabled",
    dailyPrompt: "morningPromptEnabled",
  };
  const canonicalKey = fieldMap[key];
  if (!canonicalKey) return;

  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  const utcOffsetMinutes = -new Date().getTimezoneOffset();
  const payload = {
    [canonicalKey]: value,
    timezone,
    timezoneSource: "web_device",
    timezoneUpdatedAt: serverTimestamp(),
    utcOffsetMinutes,
    updatedAt: serverTimestamp(),
  };

  const prefRef = doc(db, "users", state.user.uid, "preferences", "notifications");
  const effectiveRef = doc(db, "users", state.user.uid, "notificationEffective", "policy");
  await Promise.all([
    setDoc(prefRef, payload, { merge: true }),
    setDoc(effectiveRef, payload, { merge: true }),
  ]);
  state.notificationPrefs = { ...(state.notificationPrefs || { friendPost: true, friendRequest: true, communityInvite: true, communityPost: true, dailyPrompt: true }), [key]: value };
  _render();
}

export async function fetchAIPreferences(): Promise<void> {
  if (!state.user) return;
  const prefRef = doc(db, "users", state.user.uid, "preferences", "ai");
  const snap = await getDoc(prefRef);
  const d = snap.exists() ? snap.data() : {};
  state.aiPrefs = {
    notesAiEnabled: d.notesAiEnabled === true,
    notesAIDisclosureAccepted: d.notesAIDisclosureAccepted === true,
    helpfulSimilarThoughtPostIds: Array.isArray(d.helpfulSimilarThoughtPostIds) ? d.helpfulSimilarThoughtPostIds.filter(Boolean) : [],
    hiddenSimilarThoughtPostIds: Array.isArray(d.hiddenSimilarThoughtPostIds) ? d.hiddenSimilarThoughtPostIds.filter(Boolean) : [],
  };
}

export async function saveAIPreferences(nextPrefs: Partial<typeof state.aiPrefs>): Promise<void> {
  if (!state.user) return;
  const prefRef = doc(db, "users", state.user.uid, "preferences", "ai");
  await setDoc(prefRef, { ...nextPrefs, updatedAt: serverTimestamp() }, { merge: true });
  state.aiPrefs = { ...state.aiPrefs, ...nextPrefs };
  _render();
  if (state.aiPrefs.notesAiEnabled && state.aiPrefs.notesAIDisclosureAccepted) {
    runInBackground(async () => {
      await backfillRecentNoteInsights();
    });
  }
}

function applySimilarThoughtFeedback<T extends { postId?: string }>(thoughts: T[]): T[] {
  const hidden = new Set(state.aiPrefs.hiddenSimilarThoughtPostIds || []);
  const helpful = new Set(state.aiPrefs.helpfulSimilarThoughtPostIds || []);
  return thoughts
    .filter((thought) => !hidden.has(String(thought.postId || "")))
    .sort((lhs, rhs) => {
      const leftHelpful = helpful.has(String(lhs.postId || ""));
      const rightHelpful = helpful.has(String(rhs.postId || ""));
      if (leftHelpful === rightHelpful) return 0;
      return leftHelpful ? -1 : 1;
    });
}

export async function saveSimilarThoughtFeedback(postId: string, verdict: "helpful" | "hidden"): Promise<void> {
  const helpful = new Set(state.aiPrefs.helpfulSimilarThoughtPostIds || []);
  const hidden = new Set(state.aiPrefs.hiddenSimilarThoughtPostIds || []);
  if (verdict === "helpful") {
    helpful.add(postId);
    hidden.delete(postId);
  } else {
    hidden.add(postId);
    helpful.delete(postId);
  }
  await saveAIPreferences({
    helpfulSimilarThoughtPostIds: Array.from(helpful),
    hiddenSimilarThoughtPostIds: Array.from(hidden),
  });
  Object.keys(state.noteInsights).forEach((entryId) => {
    const insight = state.noteInsights[entryId];
    if (insight?.similarThoughts) {
      state.noteInsights[entryId] = { ...insight, similarThoughts: applySimilarThoughtFeedback(insight.similarThoughts) };
    }
  });
  if (state.threadInsight?.similarThoughts) {
    state.threadInsight = { ...state.threadInsight, similarThoughts: applySimilarThoughtFeedback(state.threadInsight.similarThoughts) };
  }
  _render();
}

// ── Anonymous thought matching ───────────────────────────────────────────────
async function postThoughtMatching(
  endpoint: string,
  body: Record<string, unknown> = {},
): Promise<Record<string, unknown>> {
  const currentUser = auth.currentUser;
  if (!currentUser) throw new Error("Sign in again to continue.");
  const token = await currentUser.getIdToken();
  const response = await fetch(`${thoughtMatchingBaseURL}${endpoint}`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
  const json = await response.json().catch(() => ({})) as Record<string, unknown>;
  if (!response.ok) {
    const code = String(json.error || "thought_matching_failed");
    const messages: Record<string, string> = {
      excerpt_not_safe: "Remove names or contact details before sharing this excerpt.",
      source_contains_identifying_info: "Remove names or contact details before sharing this excerpt.",
      entry_not_found: "This thought is still syncing. Wait a moment, then try again.",
      invalid_excerpt: "Keep the anonymous excerpt between 40 and 400 characters.",
      thought_not_eligible: "This thought cannot be shared for matching.",
      thought_matching_disabled: "Thought matching is not available right now.",
      message_not_allowed: "Keep this conversation anonymous and do not share contact details.",
      sending_too_quickly: "Give it a moment before sending again.",
    };
    throw new Error(messages[code] || "That did not go through. Try again.");
  }
  return json;
}

function parseThoughtMatchProjection(id: string, raw: Record<string, unknown>): ThoughtMatchProjection | null {
  const sourceId = String(raw.sourceId || "").trim();
  const stateValue = String(raw.state || "") as ThoughtMatchProjection["state"];
  const excerpt = String(raw.excerpt || "").trim();
  if (!sourceId || !stateValue || !excerpt) return null;
  return {
    id,
    sourceId,
    sourceType: String(raw.sourceType || "entry"),
    visibility: String(raw.visibility || "private"),
    state: stateValue,
    excerpt,
    otherExcerpt: String(raw.otherExcerpt || "").trim() || null,
    explanation: String(raw.explanation || "").trim() || null,
    proposalId: String(raw.proposalId || "").trim() || null,
    conversationId: String(raw.conversationId || "").trim() || null,
    expiresAt: raw.expiresAt ? timestampToDate(raw.expiresAt as never) : null,
    retryAfter: raw.retryAfter ? timestampToDate(raw.retryAfter as never) : null,
    updatedAt: timestampToDate(raw.updatedAt as never),
  };
}

function parseThoughtMatchConversation(id: string, raw: Record<string, unknown>): ThoughtMatchConversation | null {
  const candidateId = String(raw.candidateId || "").trim();
  const mySlot = String(raw.mySlot || "").trim();
  const myExcerpt = String(raw.myExcerpt || "").trim();
  const otherExcerpt = String(raw.otherExcerpt || "").trim();
  if (!candidateId || !mySlot || !myExcerpt || !otherExcerpt) return null;
  return {
    id,
    candidateId,
    mySlot,
    status: String(raw.status || "active"),
    myExcerpt,
    otherExcerpt,
    explanation: String(raw.explanation || ""),
    lastMessagePreview: String(raw.lastMessagePreview || "").trim() || null,
    lastMessageAt: raw.lastMessageAt ? timestampToDate(raw.lastMessageAt as never) : null,
    unreadCount: Number(raw.unreadCount || 0),
    createdAt: timestampToDate(raw.createdAt as never),
    updatedAt: timestampToDate(raw.updatedAt as never),
  };
}

export async function startThoughtMatching(): Promise<void> {
  const startToken = ++thoughtMatchStartToken;
  stopThoughtMatching();
  if (!state.user) return;
  state.thoughtMatchLoading = true;
  _render();
  try {
    const config = await postThoughtMatching("getThoughtMatchingConfig");
    state.thoughtMatchConfig = {
      thoughtMatchingEnabled: config.thoughtMatchingEnabled === true,
      matchConversationsEnabled: config.matchConversationsEnabled === true,
    };
  } catch {
    state.thoughtMatchConfig = {
      thoughtMatchingEnabled: false,
      matchConversationsEnabled: false,
    };
  }
  // A newer call started while we were awaiting config (e.g. the user opened
  // the Matches tab while background sign-in hydration was still in
  // flight) — let that call own the listeners. Proceeding here would
  // silently orphan its subscriptions by overwriting the module-level
  // unsubscribe references below without ever calling them.
  if (startToken !== thoughtMatchStartToken) return;
  const uid = state.user.uid;
  stopThoughtMatchProjectionListener = onSnapshot(
    query(collection(db, "users", uid, "thoughtMatches"), orderBy("updatedAt", "desc")),
    (snapshot) => {
      state.thoughtMatches = snapshot.docs
        .map((item) => parseThoughtMatchProjection(item.id, item.data()))
        .filter((item): item is ThoughtMatchProjection => Boolean(item));
      state.thoughtMatchLoading = false;
      state.thoughtMatchError = null;
      _render();
    },
    (error) => {
      state.thoughtMatchLoading = false;
      state.thoughtMatchError = error.message;
      _render();
    },
  );
  stopThoughtMatchConversationListener = onSnapshot(
    query(
      collection(db, "users", uid, "matchConversations"),
      where("status", "==", "active"),
    ),
    (snapshot) => {
      state.thoughtMatchConversations = snapshot.docs
        .map((item) => parseThoughtMatchConversation(item.id, item.data()))
        .filter((item): item is ThoughtMatchConversation => Boolean(item))
        .sort((left, right) => {
          const leftDate = left.lastMessageAt || left.updatedAt;
          const rightDate = right.lastMessageAt || right.updatedAt;
          return rightDate.getTime() - leftDate.getTime();
        });
      _render();
    },
    (error) => {
      state.thoughtMatchError = error.message;
      _render();
    },
  );
}

export function stopThoughtMatching(): void {
  stopThoughtMatchProjectionListener?.();
  stopThoughtMatchConversationListener?.();
  stopThoughtMatchMessageListener?.();
  stopThoughtMatchProjectionListener = null;
  stopThoughtMatchConversationListener = null;
  stopThoughtMatchMessageListener = null;
  state.thoughtMatches = [];
  state.thoughtMatchConversations = [];
  state.thoughtMatchMessages = [];
  state.selectedMatchConversationId = null;
}

export async function prepareThoughtMatchExcerpt(
  sourceId: string,
  visibility: string,
  sourceText: string,
): Promise<void> {
  state.thoughtMatchExcerptError = null;
  const json = await postThoughtMatching("prepareThoughtMatchExcerpt", {
    sourceId,
    visibility,
    sourceText,
  });
  state.thoughtMatchExcerptDraft = {
    sourceId,
    visibility,
    sourceText,
    excerpt: String(json.excerpt || ""),
    wasAnonymized: json.wasAnonymized === true,
    containsIdentifyingInfo: json.containsIdentifyingInfo === true,
  };
  _render();
}

export async function approveThoughtMatchExcerpt(): Promise<void> {
  const draft = state.thoughtMatchExcerptDraft;
  if (!draft) return;
  const excerpt = draft.excerpt.trim();
  if (excerpt.length < 40 || excerpt.length > 400) {
    state.thoughtMatchExcerptError = "Keep the anonymous excerpt between 40 and 400 characters.";
    _render();
    return;
  }
  _setBusy("approve-match-excerpt", true);
  state.thoughtMatchExcerptError = null;
  try {
    await postThoughtMatching("approveThoughtMatchExcerpt", { ...draft, excerpt });
    state.thoughtMatchExcerptDraft = null;
    showToast("Your anonymous thought is looking for a match.");
    state.activeTab = "matches";
  } catch (error) {
    state.thoughtMatchExcerptError = (error as Error).message;
  } finally {
    _setBusy("approve-match-excerpt", false);
    _render();
  }
}

export function dismissThoughtMatchExcerpt(): void {
  state.thoughtMatchExcerptDraft = null;
  state.thoughtMatchExcerptError = null;
  _render();
}

export async function respondToThoughtMatch(proposalId: string, accept: boolean): Promise<void> {
  _setBusy(`match-proposal-${proposalId}`, true);
  try {
    await postThoughtMatching("respondToThoughtMatch", { proposalId, accept });
  } catch (error) {
    showToast((error as Error).message);
  } finally {
    _setBusy(`match-proposal-${proposalId}`, false);
  }
}

export function openThoughtMatchConversation(conversationId: string): void {
  const conversation = state.thoughtMatchConversations.find((item) => item.id === conversationId);
  if (!conversation || !state.user) return;
  stopThoughtMatchMessageListener?.();
  state.selectedMatchConversationId = conversationId;
  state.thoughtMatchMessages = [];
  state.thoughtMatchInput = "";
  stopThoughtMatchMessageListener = onSnapshot(
    query(
      collection(db, "thoughtMatchConversations", conversationId, "messages"),
      orderBy("createdAt"),
      limit(200),
    ),
    (snapshot) => {
      state.thoughtMatchMessages = snapshot.docs.map((item) => ({
        id: item.id,
        senderSlot: String(item.data().senderSlot || ""),
        text: String(item.data().text || ""),
        createdAt: timestampToDate(item.data().createdAt as never),
      } satisfies ThoughtMatchMessage));
      _render();
    },
  );
  void postThoughtMatching("markThoughtMatchConversationRead", { conversationId });
  _render();
}

export function closeThoughtMatchConversation(): void {
  stopThoughtMatchMessageListener?.();
  stopThoughtMatchMessageListener = null;
  state.selectedMatchConversationId = null;
  state.thoughtMatchMessages = [];
  state.thoughtMatchInput = "";
  _render();
}

export async function sendThoughtMatchMessage(): Promise<void> {
  const conversationId = state.selectedMatchConversationId;
  const text = state.thoughtMatchInput.trim();
  if (!conversationId || !text) return;
  state.thoughtMatchInput = "";
  _setBusy("send-match-message", true);
  try {
    await postThoughtMatching("sendThoughtMatchMessage", { conversationId, text });
  } catch (error) {
    state.thoughtMatchInput = text;
    showToast((error as Error).message);
  } finally {
    _setBusy("send-match-message", false);
  }
}

export async function leaveThoughtMatchConversation(block: boolean): Promise<void> {
  const conversationId = state.selectedMatchConversationId;
  if (!conversationId) return;
  try {
    await postThoughtMatching("leaveThoughtMatchConversation", { conversationId, block });
    closeThoughtMatchConversation();
  } catch (error) {
    showToast((error as Error).message);
  }
}

export async function reportThoughtMatchConversation(): Promise<void> {
  const conversationId = state.selectedMatchConversationId;
  if (!conversationId) return;
  try {
    await postThoughtMatching("reportThoughtMatchConversation", {
      conversationId,
      reason: "objectionable_content",
    });
    showToast("Report submitted.");
  } catch (error) {
    showToast((error as Error).message);
  }
}

// ── Daily prompt ───────────────────────────────────────────────────────────
function fetchPromptFallback(): { prompt: string; suggestions: string[] } {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  const todayKey = nowIsoDateInTimezone(timezone);
  const seed = todayKey.split("-").join("").split("").reduce((acc, char) => acc + Number(char), 0);
  const prompt = DAILY_PROMPT_FALLBACK[seed % DAILY_PROMPT_FALLBACK.length];
  return { prompt, suggestions: [prompt, ...DAILY_PROMPT_FALLBACK.filter((p) => p !== prompt)].slice(0, 3) };
}

export async function fetchDailyPrompt(): Promise<void> {
  state.dailyPrompt = fetchPromptFallback();
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  const key = nowIsoDateInTimezone(timezone);
  const safeTimezone = timezone.replace(/[^A-Za-z0-9_-]/g, "_");
  const docId = `dailyPrompt_${safeTimezone}_${key}`;
  try {
    const snap = await getDoc(doc(db, "appConfig", docId));
    if (snap.exists()) {
      const prompt = ((snap.data().prompt || "") as string).trim();
      const suggestions = Array.isArray(snap.data().suggestions) ? (snap.data().suggestions as string[]).filter(Boolean) : [];
      if (prompt) { state.dailyPrompt = { prompt, suggestions: [prompt, ...suggestions.filter((s) => s !== prompt)].slice(0, 3) }; return; }
    }
    const dict = await getDoc(doc(db, "appConfig", "promptDictionary"));
    if (dict.exists()) {
      const prompts = Array.isArray(dict.data().prompts) ? (dict.data().prompts as unknown[]).map((p) => String(p || "").trim()).filter(Boolean) : [];
      if (prompts.length > 0) {
        const seed = key.split("-").join("").split("").reduce((acc, char) => acc + Number(char), 0);
        const prompt = prompts[seed % prompts.length];
        state.dailyPrompt = { prompt, suggestions: [prompt, ...prompts.filter((p) => p !== prompt)].slice(0, 3) };
      }
    }
  } catch { /* fallback already set */ }
}

// ── AI / insights ──────────────────────────────────────────────────────────
export async function fetchThreadInsight(scope: ThreadScope, postId: string, communityId: string | null = null) {
  try {
    const ref = scope === "shared"
      ? doc(db, "sharedPosts", postId, "ai", "v1")
      : doc(db, "groups", communityId!, "posts", postId, "ai", "v1");
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;
    return await sanitizeInsight({ id: snap.id, ...snap.data() });
  } catch { return null; }
}

export async function fetchUserMatches(): Promise<void> {
  if (!state.user) return;
  try {
    const snap = await getDoc(doc(db, "users", state.user.uid, "matches", "v1"));
    state.userMatches = snap.exists() && Array.isArray(snap.data().users) ? snap.data().users as typeof state.userMatches : [];
  } catch { state.userMatches = []; }
}

export async function fetchEchoItems(): Promise<void> {
  if (!state.user) return;
  try {
    const snap = await getDoc(doc(db, "users", state.user.uid, "echo", "v1"));
    state.echoItems = snap.exists() && Array.isArray(snap.data().items) ? snap.data().items as typeof state.echoItems : [];
  } catch { state.echoItems = []; }
}

function normalizeThinkingText(value: string): string {
  return String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9 ]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function sentenceCase(value: string): string {
  const trimmed = String(value || "").trim();
  return trimmed ? `${trimmed.charAt(0).toUpperCase()}${trimmed.slice(1)}` : "";
}

function thinkingTokens(value: string): string[] {
  const stop = new Set([
    "about", "again", "also", "and", "are", "because", "been", "but", "can", "could",
    "does", "from", "have", "into", "just", "like", "more", "not", "now", "over",
    "really", "should", "some", "that", "their", "them", "then", "there", "this",
    "thought", "through", "what", "when", "where", "with", "you", "your",
  ]);
  return normalizeThinkingText(value).split(" ").filter((token) => token.length >= 4 && !stop.has(token));
}

function thinkingOverlap(left: string, right: string): number {
  const rhs = new Set(thinkingTokens(right));
  return thinkingTokens(left).filter((token) => rhs.has(token)).length;
}

type LocalThinkingDoc = {
  sourceId: string;
  text: string;
  createdAt: Date;
  reflection?: Record<string, unknown>;
  embedding: number[];
};

function reflectionThemesForMatching(reflection: AIReflection | Record<string, unknown> | undefined): string[] {
  const canonicalThemes = Array.isArray(reflection?.canonicalThemes) ? reflection!.canonicalThemes as string[] : [];
  if (canonicalThemes.length > 0) return canonicalThemes;
  return Array.isArray(reflection?.themes) ? reflection!.themes as string[] : [];
}

function cosineSimilarity(left: number[], right: number[]): number {
  if (!left.length || left.length !== right.length) return 0;
  let dot = 0;
  let leftMagnitude = 0;
  let rightMagnitude = 0;
  left.forEach((value, index) => {
    dot += value * right[index];
    leftMagnitude += value * value;
    rightMagnitude += right[index] * right[index];
  });
  if (leftMagnitude <= 0 || rightMagnitude <= 0) return 0;
  return dot / (Math.sqrt(leftMagnitude) * Math.sqrt(rightMagnitude));
}

function recencyWeightForThinking(date: Date, now = new Date()): number {
  const days = Math.max(0, (now.getTime() - date.getTime()) / 86400000);
  if (days <= 30) return 1;
  return Math.max(0.25, 1 / (1 + (days - 30) / 30));
}

function localThinkingPairScore(left: LocalThinkingDoc, right: LocalThinkingDoc, now = new Date()): number {
  const cosine = cosineSimilarity(left.embedding || [], right.embedding || []);
  const hasEmbeddingScore = cosine > 0;
  const leftThemes = reflectionThemesForMatching(left.reflection);
  const rightThemes = reflectionThemesForMatching(right.reflection);
  const rightThemeSet = new Set(rightThemes.map(normalizeThinkingText));
  const exactThemes = leftThemes.filter((theme) => rightThemeSet.has(normalizeThinkingText(theme))).length;
  const themeOverlap = thinkingOverlap(leftThemes.join(" "), rightThemes.join(" "));
  const leftTension = String(left.reflection?.tension || "");
  const rightTension = String(right.reflection?.tension || "");
  const sameTension = Boolean(normalizeThinkingText(leftTension) && normalizeThinkingText(leftTension) === normalizeThinkingText(rightTension));
  const tensionOverlap = thinkingOverlap(leftTension, rightTension);
  const previewOverlap = thinkingOverlap(left.text, right.text);
  const recency = (recencyWeightForThinking(left.createdAt, now) + recencyWeightForThinking(right.createdAt, now)) / 2;

  let semanticBase = 0.18;
  if (hasEmbeddingScore) {
    semanticBase = Math.max(cosine * 0.72, cosine > 0.50 ? 0.36 : 0.18);
  } else if (exactThemes > 0 || sameTension) {
    semanticBase = 0.50;
  } else if (themeOverlap >= 2 || tensionOverlap >= 2) {
    semanticBase = 0.42;
  } else if (themeOverlap >= 1 || tensionOverlap >= 1 || previewOverlap >= 1) {
    semanticBase = 0.32;
  }

  const themeBoost = Math.min(exactThemes * 0.13 + themeOverlap * 0.03, 0.24);
  const tensionBoost = sameTension ? 0.18 : Math.min(tensionOverlap * 0.045, 0.14);
  const previewBoost = Math.min(previewOverlap * 0.03, 0.14);
  const recencyBoost = recency * 0.05;
  return Math.min(Math.max(semanticBase + themeBoost + tensionBoost + previewBoost + recencyBoost, 0), 1);
}

function thinkingConfidenceForDocs(docs: LocalThinkingDoc[], now = new Date()): number {
  if (docs.length < 3) return 0;
  const scores: number[] = [];
  for (let i = 0; i < docs.length; i += 1) {
    for (let j = i + 1; j < docs.length; j += 1) {
      scores.push(localThinkingPairScore(docs[i], docs[j], now));
    }
  }
  if (!scores.length) return 0;
  const average = scores.reduce((sum, value) => sum + value, 0) / scores.length;
  const strongestCount = Math.max(2, docs.length - 1);
  const strongest = [...scores].sort((a, b) => b - a).slice(0, strongestCount);
  const strongestAverage = strongest.reduce((sum, value) => sum + value, 0) / Math.max(strongest.length, 1);
  return Math.min(Math.max((average * 0.62) + (strongestAverage * 0.38), 0), 1);
}

function localThinkingBucketKeys(docItem: LocalThinkingDoc): string[] {
  const keys = new Set<string>();
  const reflection = docItem.reflection || {};
  const themes = reflectionThemesForMatching(reflection);
  themes.forEach((theme) => {
    const normalized = normalizeThinkingText(theme);
    if (normalized) keys.add(`theme:${normalized}`);
    thinkingTokens(theme).forEach((token) => keys.add(`token:${token}`));
  });
  const tension = String(reflection.tension || "");
  const normalizedTension = normalizeThinkingText(tension);
  if (normalizedTension) keys.add(`tension:${normalizedTension}`);
  thinkingTokens(tension).forEach((token) => keys.add(`token:${token}`));
  if (keys.size === 0) {
    thinkingTokens(docItem.text).slice(0, 5).forEach((token) => keys.add(`text:${token}`));
  }
  return Array.from(keys);
}

function uniqueThinkingDocs(docs: LocalThinkingDoc[]): LocalThinkingDoc[] {
  return Array.from(new Map(docs.map((docItem) => [docItem.sourceId, docItem])).values());
}

function localThinkingCandidateGroups(aiReady: LocalThinkingDoc[], now = new Date()): LocalThinkingDoc[][] {
  const groups: LocalThinkingDoc[][] = [];
  const buckets = new Map<string, LocalThinkingDoc[]>();
  aiReady.forEach((docItem) => {
    localThinkingBucketKeys(docItem).forEach((key) => {
      const bucket = buckets.get(key) || [];
      bucket.push(docItem);
      buckets.set(key, bucket);
    });
  });
  groups.push(...Array.from(buckets.values()).filter((bucket) => bucket.length >= 3));

  const visited = new Set<string>();
  aiReady.forEach((entry) => {
    if (visited.has(entry.sourceId)) return;
    const component: LocalThinkingDoc[] = [];
    const queue = [entry];
    visited.add(entry.sourceId);
    while (queue.length) {
      const current = queue.pop()!;
      component.push(current);
      aiReady.forEach((candidate) => {
        if (visited.has(candidate.sourceId)) return;
        if (localThinkingPairScore(current, candidate, now) >= 0.48) {
          visited.add(candidate.sourceId);
          queue.push(candidate);
        }
      });
    }
    if (component.length >= 3) groups.push(component);
  });

  const sorted = [...aiReady].sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  sorted.forEach((seed) => {
    const nearest = sorted
      .filter((candidate) => candidate.sourceId !== seed.sourceId)
      .map((candidate) => ({ candidate, score: localThinkingPairScore(seed, candidate, now) }))
      .sort((a, b) => b.score - a.score)
      .slice(0, 5)
      .filter((item) => item.score >= 0.30)
      .map((item) => item.candidate);
    if (nearest.length >= 2) groups.push(uniqueThinkingDocs([seed, ...nearest]));
  });

  if (groups.length === 0 && sorted.length >= 3) {
    groups.push(sorted.slice(0, Math.min(5, sorted.length)));
  }
  return groups;
}

function topThinkingString(values: string[]): string {
  const counts = new Map<string, { value: string; count: number }>();
  values.map(String).map((value) => value.trim()).filter(Boolean).forEach((value) => {
    const key = normalizeThinkingText(value);
    if (!key) return;
    const current = counts.get(key) || { value, count: 0 };
    current.count += 1;
    if (value.length > current.value.length) current.value = value;
    counts.set(key, current);
  });
  return Array.from(counts.values()).sort((a, b) => b.count - a.count || b.value.length - a.value.length)[0]?.value || "";
}

function buildLocalTensionArc(docs: Array<{ reflection?: Record<string, unknown>; text: string; createdAt: Date }>): ThinkingTensionArc {
  const sorted = [...docs].sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
  const intensity = (doc: typeof sorted[number]) => {
    const text = [
      doc.reflection?.tone || "",
      doc.reflection?.tension || "",
      doc.reflection?.summary || "",
      doc.text || "",
    ].join(" ").toLowerCase();
    const sharper = ["hard", "stuck", "angry", "fear", "anxious", "lost", "pressure", "tired", "doubt", "worse", "failure"];
    const softer = ["calm", "clear", "settled", "better", "hope", "accept", "understand", "resolved", "lighter", "okay", "stronger"];
    return 0.5 + sharper.filter((word) => text.includes(word)).length * 0.06 - softer.filter((word) => text.includes(word)).length * 0.05;
  };
  if (sorted.length < 3) return { state: "unresolved", copy: "This tension keeps returning without resolution", confidenceScore: 0.45 };
  const scores = sorted.map(intensity);
  const split = Math.max(1, Math.floor(scores.length / 2));
  const early = scores.slice(0, split).reduce((sum, value) => sum + value, 0) / split;
  const recentSlice = scores.slice(split);
  const recent = recentSlice.reduce((sum, value) => sum + value, 0) / Math.max(recentSlice.length, 1);
  const delta = recent - early;
  const confidence = Math.min(0.82, 0.5 + sorted.length * 0.05);
  if (delta >= 0.12) return { state: "intensifying", copy: "This has been feeling sharper lately", confidenceScore: confidence };
  if (delta <= -0.12) return { state: "softening", copy: "This tension seems to be softening", confidenceScore: confidence };
  return { state: "unresolved", copy: "This tension keeps returning without resolution", confidenceScore: confidence };
}

function buildLocalThinkingTimeline(): void {
  const now = new Date();
  const aiReady: LocalThinkingDoc[] = state.notes
    .map((note) => ({ note, insight: state.noteInsights[note.id] }))
    .filter((item) => Boolean(item.insight?.reflection) || Boolean(item.insight?.embedding?.length))
    .map((item) => ({
      sourceId: item.note.id,
      text: item.note.text,
      createdAt: item.note.createdAt,
      reflection: item.insight?.reflection as Record<string, unknown> | undefined,
      embedding: Array.isArray(item.insight?.embedding) ? item.insight!.embedding!.map(Number).filter(Number.isFinite) : [],
    }));

  const patterns: ThinkingPattern[] = localThinkingCandidateGroups(aiReady, now)
    .map((docs, index) => {
      const unique = uniqueThinkingDocs(docs)
        .sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
      if (unique.length < 3) return null;
      const confidence = thinkingConfidenceForDocs(unique, now);
      const isEmerging = aiReady.length < 6;
      if (confidence < (isEmerging ? 0.48 : 0.6)) return null;
      const first = unique[0];
      const last = unique[unique.length - 1];
      const allThemes = unique.flatMap((docItem) => reflectionThemesForMatching(docItem.reflection));
      const themes = Array.from(new Map(allThemes
        .map((theme) => [normalizeThinkingText(theme), sentenceCase(theme)] as const)
        .filter(([key]) => Boolean(key))).values()).slice(0, 5);
      const tension = sentenceCase(topThinkingString(unique.map((docItem) => String(docItem.reflection?.tension || ""))));
      const title = tension || themes[0] || "A returning thought";
      const idKey = `${normalizeThinkingText(title) || "pattern"}:${unique.map((docItem) => docItem.sourceId).sort().join(":")}`;
      return {
        id: `local-${index}-${idKey}`,
        title,
        themes,
        tension,
        entryIds: unique.map((docItem) => docItem.sourceId),
        firstSeenAt: first.createdAt,
        lastSeenAt: last.createdAt,
        daysActive: Math.max(1, Math.round((last.createdAt.getTime() - first.createdAt.getTime()) / 86400000)),
        entryCount: unique.length,
        confidenceScore: Number(confidence.toFixed(4)),
        publicConversationCount: 0,
        publicSimilarityScore: 0,
        publicMatches: [],
        isEmerging,
        tensionArc: buildLocalTensionArc(unique),
      } as ThinkingPattern;
    })
    .filter(Boolean) as ThinkingPattern[];

  const deduped: ThinkingPattern[] = [];
  patterns.sort((a, b) => b.confidenceScore - a.confidenceScore).forEach((pattern) => {
    const ids = new Set(pattern.entryIds);
    const duplicate = deduped.some((existing) => {
      const existingIds = new Set(existing.entryIds);
      let overlap = 0;
      ids.forEach((id) => { if (existingIds.has(id)) overlap += 1; });
      return overlap / Math.max(new Set([...ids, ...existingIds]).size, 1) > 0.70;
    });
    if (!duplicate) deduped.push(pattern);
  });

  const confirmed = deduped.filter((pattern) => !pattern.isEmerging && pattern.entryCount >= 3 && pattern.confidenceScore >= 0.6);
  state.thinkingTimeline = {
    ...state.thinkingTimeline,
    aiReadyEntryCount: aiReady.length,
    patterns: deduped.sort((a, b) => timestampToDate(b.lastSeenAt).getTime() - timestampToDate(a.lastSeenAt).getTime()).slice(0, 18),
    themes: buildLocalThinkingThemes(confirmed),
    profile: buildLocalThinkingProfile(confirmed, aiReady.length),
    updatedAt: new Date(),
    source: aiReady.length > 0 ? "local" : "empty",
    loading: false,
    error: null,
  };
}

function buildLocalThinkingThemes(patterns: ThinkingPattern[]): ThinkingTheme[] {
  const buckets = new Map<string, { label: string; ids: Set<string>; scores: number[] }>();
  patterns.forEach((pattern) => {
    pattern.themes.forEach((theme) => {
      const key = normalizeThinkingText(theme);
      if (!key) return;
      const bucket = buckets.get(key) || { label: sentenceCase(theme), ids: new Set<string>(), scores: [] };
      pattern.entryIds.forEach((id) => bucket.ids.add(id));
      bucket.scores.push(pattern.confidenceScore);
      buckets.set(key, bucket);
    });
  });
  return Array.from(buckets.values())
    .map((bucket) => ({
      label: bucket.label,
      entryCount: bucket.ids.size,
      confidenceScore: bucket.scores.reduce((sum, value) => sum + value, 0) / Math.max(bucket.scores.length, 1),
    }))
    .filter((theme) => theme.entryCount >= 3 && theme.confidenceScore >= 0.55)
    .sort((a, b) => b.entryCount - a.entryCount || b.confidenceScore - a.confidenceScore)
    .slice(0, 5);
}

function buildLocalThinkingProfile(patterns: ThinkingPattern[], aiReadyCount: number): ThinkingProfileSnapshot | null {
  if (aiReadyCount < 8 || patterns.length === 0) return null;
  const top = [...patterns].sort((a, b) => b.confidenceScore - a.confidenceScore)[0];
  if (!top || top.confidenceScore < 0.66) return null;
  const lines = [`You often revisit ${top.title.toLowerCase()}`];
  const theme = top.themes[0];
  if (theme) lines.push(`${sentenceCase(theme)} appears across your notes`);
  if (top.tensionArc?.copy) lines.push(top.tensionArc.copy);
  return {
    lines: lines.slice(0, 3),
    basedOnEntryCount: aiReadyCount,
    confidenceScore: top.confidenceScore,
    generatedAt: new Date(),
  };
}

function normalizeThinkingPattern(raw: Record<string, unknown>): ThinkingPattern | null {
  const id = String(raw.id || "").trim();
  const title = String(raw.title || "").trim();
  const entryIds = Array.isArray(raw.entryIds) ? raw.entryIds.map(String).filter(Boolean) : [];
  if (!id || !title || entryIds.length < 1) return null;
  const rawArc = raw.tensionArc as Record<string, unknown> | undefined;
  const tensionArc = rawArc ? {
    state: String(rawArc.state || "unresolved") as ThinkingTensionArc["state"],
    copy: String(rawArc.copy || "This tension keeps returning without resolution"),
    confidenceScore: Number(rawArc.confidenceScore || 0),
  } : null;
  return {
    id,
    title,
    displayTitle: String(raw.displayTitle || "").trim() || undefined,
    observation: String(raw.observation || "").trim() || undefined,
    themes: Array.isArray(raw.themes) ? raw.themes.map(String).filter(Boolean) : [],
    tension: String(raw.tension || ""),
    entryIds,
    firstSeenAt: raw.firstSeenAt as never,
    lastSeenAt: raw.lastSeenAt as never,
    daysActive: Number(raw.daysActive || 1),
    entryCount: Number(raw.entryCount || entryIds.length),
    confidenceScore: Number(raw.confidenceScore || 0),
    publicConversationCount: Number(raw.publicConversationCount || 0),
    publicSimilarityScore: Number(raw.publicSimilarityScore || 0),
    publicMatches: Array.isArray(raw.publicMatches) ? raw.publicMatches as SimilarThought[] : [],
    isEmerging: raw.isEmerging === true,
    tensionArc,
  };
}

function hasActiveThinkingPattern(patterns: ThinkingPattern[]): boolean {
  return patterns.some((pattern) => {
    const lastSeen = timestampToDate(pattern.lastSeenAt);
    const days = Math.floor((Date.now() - lastSeen.getTime()) / 86400000);
    return days < 21 && pattern.entryCount >= 3;
  });
}

async function warmThinkingInsightCache(): Promise<void> {
  const desired = Math.min(Math.max(state.notes.length, 24), 80);
  const known = state.notes.filter((note) => {
    const insight = state.noteInsights[note.id];
    return Boolean(insight?.reflection) || Boolean(insight?.embedding?.length);
  }).length;
  if (known >= Math.min(3, state.notes.length)) return;
  await fetchNoteInsights(desired);
}

export async function fetchThinkingTimeline(options: { refresh?: boolean } = {}): Promise<void> {
  if (!state.user) return;
  state.thinkingTimeline = { ...state.thinkingTimeline, loading: true, error: null };
  _render();
  try {
    if (options.refresh && state.aiPrefs.notesAiEnabled && state.aiPrefs.notesAIDisclosureAccepted) {
      const token = await auth.currentUser?.getIdToken();
      if (token) {
        await fetch(refreshThinkingTimelineURL, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body: JSON.stringify({ source: "web", outputLanguage: currentAnalysisLanguage() }),
        }).catch(() => undefined);
      }
    }
    const snap = await getDoc(doc(db, "users", state.user.uid, "thinking", "v1"));
    if (!snap.exists()) {
      await warmThinkingInsightCache();
      buildLocalThinkingTimeline();
      return;
    }
    const data = snap.data() as Record<string, unknown>;
    if (String(data.language || "").trim() &&
      currentAnalysisLanguageFromRaw(String(data.language || "")) !== currentAnalysisLanguage()) {
      await warmThinkingInsightCache();
      buildLocalThinkingTimeline();
      return;
    }
    const patterns = Array.isArray(data.patterns)
      ? (data.patterns as Record<string, unknown>[]).map(normalizeThinkingPattern).filter(Boolean) as ThinkingPattern[]
      : [];
    state.thinkingTimeline = {
      ...state.thinkingTimeline,
      aiReadyEntryCount: Number(data.aiReadyEntryCount || 0),
      patterns,
      themes: Array.isArray(data.themes) ? data.themes as ThinkingTheme[] : [],
      profile: (data.profile as ThinkingProfileSnapshot | null) || null,
      updatedAt: data.updatedAt as never,
      source: "backend",
      loading: false,
      error: null,
    };
    if (!hasActiveThinkingPattern(patterns) && state.notes.length > 0) {
      await warmThinkingInsightCache();
      buildLocalThinkingTimeline();
    }
  } catch {
    await warmThinkingInsightCache();
    buildLocalThinkingTimeline();
    state.thinkingTimeline = {
      ...state.thinkingTimeline,
      loading: false,
      error: "Could not refresh Thinking over time. Showing local patterns.",
    };
  } finally {
    _render();
  }
}

export async function fetchNoteInsights(limitCount = 24): Promise<void> {
  if (!state.user || state.notes.length === 0) { state.noteInsights = {}; return; }
  const next: typeof state.noteInsights = { ...state.noteInsights };
  await Promise.all(state.notes.slice(0, limitCount).map(async (note) => {
    try {
      const snap = await getDoc(doc(db, "users", state.user!.uid, "entries", note.id, "ai", "v1"));
      if (snap.exists()) {
        const sanitized = await sanitizeInsight({ id: snap.id, ...snap.data() });
        const insight = await augmentNoteInsightWithFallbackSimilarThoughts(
          sanitized as typeof state.noteInsights[string] | undefined,
          note.text,
        );
        if (insight) next[note.id] = insight as typeof state.noteInsights[string];
      }
    } catch { /* ignore */ }
  }));
  state.noteInsights = next;
}

function noteInsightVersionMs(insight: typeof state.noteInsights[string] | undefined): number {
  if (!insight) return 0;
  const stamp = timestampToDate(insight.lastSimilarRefreshAt || insight.updatedAt);
  return stamp ? stamp.getTime() : 0;
}

async function fetchCurrentNoteInsightVersionMs(entryId: string): Promise<number> {
  const localVersion = noteInsightVersionMs(state.noteInsights[entryId]);
  if (!state.user) return localVersion;
  try {
    const snap = await getDoc(doc(db, "users", state.user.uid, "entries", entryId, "ai", "v1"));
    if (!snap.exists()) return localVersion;
    const data = snap.data() || {};
    const stamp = timestampToDate((data.lastSimilarRefreshAt || data.updatedAt) as never);
    return stamp ? Math.max(localVersion, stamp.getTime()) : localVersion;
  } catch {
    return localVersion;
  }
}

async function requestNoteAnalysis(entryId: string, text: string): Promise<number> {
  const currentUser = auth.currentUser;
  if (!currentUser) return 0;
  const baselineMs = await fetchCurrentNoteInsightVersionMs(entryId);
  state.noteInsights[entryId] = {
    ...(state.noteInsights[entryId] || { id: "v1" }),
    status: "pending",
  };
  _render();
  const token = await currentUser.getIdToken();
  const response = await fetch(noteAnalysisURL, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ entryId, text, outputLanguage: currentAnalysisLanguage() }),
  });
  if (!response.ok) {
    throw new Error(`note_ai_${response.status}`);
  }
  const json = await response.json().catch(() => null) as { companionMessage?: string } | null;
  const companionMessage = String(json?.companionMessage || "").trim();
  if (companionMessage) {
    state.noteInsights[entryId] = {
      ...(state.noteInsights[entryId] || { id: "v1" }),
      companionMessage,
    };
    _render();
  }
  return baselineMs;
}

async function refreshSingleNoteInsight(entryId: string, baselineMs = 0): Promise<boolean> {
  if (!state.user) return false;
  const snap = await getDoc(doc(db, "users", state.user.uid, "entries", entryId, "ai", "v1"));
  if (!snap.exists()) return false;
  const sanitized = await sanitizeInsight({ id: snap.id, ...snap.data() });
  const noteText = state.notes.find((note) => note.id === entryId)?.text || "";
  const insight = await augmentNoteInsightWithFallbackSimilarThoughts(
    sanitized as typeof state.noteInsights[string] | undefined,
    noteText,
  );
  if (!insight) return false;
  state.noteInsights[entryId] = insight as typeof state.noteInsights[string];
  _render();
  const currentVersionMs = noteInsightVersionMs(insight as typeof state.noteInsights[string]);
  if (baselineMs > 0 && currentVersionMs <= baselineMs) {
    return false;
  }
  return insight.status === "ready" || Boolean(
    (insight.reflection && (insight.reflection.summary || insight.reflection.tension || insight.reflection.tone || insight.reflection.logic || (insight.reflection.themes || []).length)) ||
    (Array.isArray(insight.similarThoughts) && insight.similarThoughts.length > 0) ||
    insight.companionMessage
  );
}

function markNoteInsightUnavailable(entryId: string): void {
  state.noteInsights[entryId] = {
    ...(state.noteInsights[entryId] || { id: "v1" }),
    status: "unavailable",
  };
  _render();
}

async function waitForNoteInsight(entryId: string, baselineMs = 0, attempts = 6, delayMs = 1200): Promise<void> {
  for (let index = 0; index < attempts; index += 1) {
    await delay(delayMs);
    if (await refreshSingleNoteInsight(entryId, baselineMs)) return;
  }
  if (!state.noteInsights[entryId] || state.noteInsights[entryId]?.status === "pending") {
    markNoteInsightUnavailable(entryId);
  } else {
    _render();
  }
}

function noteInsightNeedsRefresh(insight: typeof state.noteInsights[string] | undefined): boolean {
  if (!insight) return true;
  if (!insightMatchesCurrentAnalysisLanguage(insight)) return true;
  const refreshedAt = timestampToDate(insight.lastSimilarRefreshAt || insight.updatedAt);
  if (!refreshedAt) return true;
  return (Date.now() - refreshedAt.getTime()) > noteSimilarRefreshWindowMs;
}

function visibleNoteCandidates(limitCount = 24): typeof state.notes {
  return state.notes
    .filter((note) => String(note.text || "").trim())
    .slice(0, limitCount);
}

async function backfillRecentNoteInsights(limitCount = 18): Promise<void> {
  if (!state.user || !state.aiPrefs.notesAiEnabled || !state.aiPrefs.notesAIDisclosureAccepted) return;
  const candidates = visibleNoteCandidates(Math.max(limitCount, 24))
    .filter((note) => !note.isEncrypted)
    .filter((note) => {
      const insight = state.noteInsights[note.id];
      const hasReflection = Boolean(insight?.reflection && (insight.reflection.summary || insight.reflection.tension || insight.reflection.tone || insight.reflection.logic || (insight.reflection.themes || []).length));
      return !hasReflection || noteInsightNeedsRefresh(insight);
    })
    .slice(0, limitCount);

  for (const note of candidates) {
    try {
      const baselineMs = await requestNoteAnalysis(note.id, String(note.text || "").trim());
      await waitForNoteInsight(note.id, baselineMs, 5, 1500);
    } catch {
      markNoteInsightUnavailable(note.id);
      // Explicit note AI is best-effort and should never block notes UX.
    }
  }
  await Promise.all([fetchUserMatches(), fetchEchoItems()]);
  _render();
}

export async function analyzeNoteIfEnabled(note: typeof state.notes[0] | undefined): Promise<void> {
  if (!state.user || !state.aiPrefs.notesAiEnabled || !state.aiPrefs.notesAIDisclosureAccepted) return;
  if (!note || note.isEncrypted || !String(note.text || "").trim()) return;
  try {
    const baselineMs = await requestNoteAnalysis(note.id, String(note.text || "").trim());
    await waitForNoteInsight(note.id, baselineMs, 6, 1200);
    runInBackground(async () => {
      await Promise.all([fetchUserMatches(), fetchEchoItems()]);
      _render();
    });
    _render();
  } catch {
    markNoteInsightUnavailable(note.id);
    // Explicit note AI is intentionally non-blocking.
  }
}

export async function refreshVisibleNoteInsights(limitCount = 24): Promise<void> {
  if (!state.user) return;
  await fetchNoteInsights(limitCount);
  if (state.aiPrefs.notesAiEnabled && state.aiPrefs.notesAIDisclosureAccepted) {
    await backfillRecentNoteInsights(Math.min(limitCount, 24));
  }
  await fetchThinkingTimeline();
}

export async function ensureNoteSimilarThoughts(noteId: string): Promise<void> {
  if (!state.user) return;
  const note = state.notes.find((entry) => entry.id === noteId);
  if (!note || note.isEncrypted) return;
  const insight = state.noteInsights[noteId];
  if (insight?.status === "pending") return;
  if (Array.isArray(insight?.similarThoughts) && insight!.similarThoughts!.length > 0) return;
  if (!state.aiPrefs.notesAiEnabled || !state.aiPrefs.notesAIDisclosureAccepted) return;
  try {
    const baselineMs = await requestNoteAnalysis(noteId, String(note.text || "").trim());
    await waitForNoteInsight(noteId, baselineMs, 5, 1200);
  } catch {
    if (!state.noteInsights[noteId]) markNoteInsightUnavailable(noteId);
  }
}

async function sanitizeInsight(
  insight: ThreadInsight | NoteInsight | null
): Promise<ThreadInsight | NoteInsight | null> {
  if (!insight) return insight;
  const similarThoughts = Array.isArray(insight.similarThoughts) ? insight.similarThoughts : [];
  const filtered = similarThoughts.length > 0 ? await filterValidSimilarThoughts(similarThoughts) : [];
  const reflectionQuality = Number(insight.reflectionQuality || 0);
  const reflection = reflectionQuality >= 0.6 ? insight.reflection : undefined;
  const companionMessage = String((insight as NoteInsight).companionMessage || "").trim() || null;
  return { ...insight, reflection, similarThoughts: applySimilarThoughtFeedback(filtered), companionMessage };
}

function companionRelationshipStage(sourceCount: number): CompanionPortrait["relationshipStage"] {
  if (sourceCount <= 1) return "new";
  if (sourceCount <= 5) return "becomingFamiliar";
  return "established";
}

function firstCompanionQuestion(): string {
  const questions = [
    "How are you actually doing today?",
    "What has been taking up space in your mind lately?",
    "Is there something you understand logically but still cannot settle?",
    "Has anything felt harder than it should lately?",
    "Is there someone or something you keep coming back to?",
  ];
  const uidSeed = [...String(state.user?.uid || "lot")]
    .reduce((sum, character) => sum + character.charCodeAt(0), 0);
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 0);
  const day = Math.floor((now.getTime() - start.getTime()) / 86400000);
  return questions[(uidSeed + day) % questions.length];
}

function suggestedCompanionMode(
  pattern: SupportPattern | null | undefined,
  risk: AIRiskSignal | null | undefined,
): CompanionMode {
  if (risk?.level === "high") return "ground";
  const rememberedMode = state.companionContext.memories
    .find((memory) => memory.suggestedMode)?.suggestedMode;
  if (rememberedMode) return rememberedMode;
  if (!pattern || Number(pattern.confidence || 0) < 0.45) return "reflect";
  switch (String(pattern.id || "").toLowerCase()) {
    case "anxiety_loop":
    case "burnout":
    case "rumination":
    case "catastrophizing":
      return "ground";
    case "self_worth":
    case "identity_conflict":
    case "hopelessness":
    case "unclear":
      return "clarify";
    case "avoidance":
    case "direction_confusion":
    case "transition_stress":
      return "question";
    case "boundary_strain":
      return "pattern";
    default:
      return "reflect";
  }
}

function companionRelatedPatterns(): CompanionRelatedPattern[] {
  return state.thinkingTimeline.patterns
    .filter((pattern) => pattern.entryCount >= 2)
    .sort((left, right) => {
      return timestampToDate(right.lastSeenAt).getTime() -
        timestampToDate(left.lastSeenAt).getTime();
    })
    .slice(0, 3)
    .map((pattern) => ({
      title: pattern.title,
      summary: pattern.tension || pattern.tensionArc?.copy || "",
      entryCount: pattern.entryCount,
      spanText: `${Math.max(1, pattern.daysActive)} days`,
      lastSeenText: timestampToDate(pattern.lastSeenAt).toLocaleDateString(),
      arcState: pattern.tensionArc?.state || "",
      arcCopy: pattern.tensionArc?.copy || "",
    }));
}

function companionPortraitPayload(): Record<string, unknown> {
  const portrait = state.companionContext.portrait;
  const memories = state.companionContext.memories;
  const relatedPatterns = companionRelatedPatterns();
  const recurringPatterns = [
    ...memories.map((memory) => memory.supportPatternLabel || ""),
    ...relatedPatterns.map((pattern) => pattern.title),
  ].filter((label, index, all) => {
    const normalized = label.trim().toLowerCase();
    return normalized && all.findIndex((candidate) => candidate.trim().toLowerCase() === normalized) === index;
  });
  return {
    summary: String(portrait?.summary || "").slice(0, 2200),
    userGuidance: String(portrait?.userGuidance || "").slice(0, 600),
    relationshipStage: portrait?.relationshipStage || "new",
    rememberedContext: memories.slice(0, 8).map((memory) => memory.summary.slice(0, 220)),
    recurringPatterns: recurringPatterns.slice(0, 6),
    openQuestions: memories
      .map((memory) => String(memory.nextQuestion || "").trim())
      .filter(Boolean)
      .slice(0, 3)
      .map((question) => question.slice(0, 160)),
  };
}

export async function loadCompanionContext(force = false): Promise<void> {
  const currentUser = auth.currentUser;
  const rawKey = getUnlockedCrossPlatformKey();
  if (!currentUser || !rawKey) {
    state.companionContext = {
      memories: [],
      portrait: null,
      loaded: true,
      loading: false,
      error: rawKey ? null : "Unlock note encryption to use private Companion memory.",
    };
    return;
  }
  if (!force && state.companionContext.loaded) return;
  if (!force && state.companionContext.loading) {
    for (let attempt = 0; attempt < 40 && state.companionContext.loading; attempt += 1) {
      await delay(100);
    }
    return;
  }

  state.companionContext.loading = true;
  state.companionContext.error = null;
  _render();
  try {
    const memorySnap = await getDocs(query(
      collection(db, "users", currentUser.uid, "companionMemories"),
      orderBy("updatedAt", "desc"),
      limit(8),
    ));
    const memories: CompanionMemoryRecord[] = [];
    for (const memoryDoc of memorySnap.docs) {
      const data = memoryDoc.data();
      try {
        const summary = await decryptCombinedString(
          base64ToBytes(String(data.encryptedSummary || "")),
          rawKey,
        );
        if (!summary.trim()) continue;
        const decryptOptional = async (value: unknown): Promise<string | null> => {
          const encoded = String(value || "").trim();
          if (!encoded) return null;
          return (await decryptCombinedString(base64ToBytes(encoded), rawKey)).trim() || null;
        };
        const suggestedModeRaw = String(data.suggestedMode || "");
        const suggestedMode = (
          suggestedModeRaw === "reflect" ||
          suggestedModeRaw === "clarify" ||
          suggestedModeRaw === "ground" ||
          suggestedModeRaw === "pattern" ||
          suggestedModeRaw === "question"
        ) ? suggestedModeRaw : null;
        memories.push({
          id: memoryDoc.id,
          summary: summary.trim(),
          afterglow: await decryptOptional(data.encryptedAfterglow),
          nextQuestion: await decryptOptional(data.encryptedNextQuestion),
          suggestedMode,
          supportPatternId: String(data.supportPatternId || "") || null,
          supportPatternLabel: String(data.supportPatternLabel || "") || null,
          sourceEntryId: String(data.sourceEntryId || "") || null,
          createdAt: timestampToDate(data.createdAt),
          updatedAt: timestampToDate(data.updatedAt || data.createdAt),
        });
      } catch {
        // One unreadable memory should not hide the rest.
      }
    }

    let portrait: CompanionPortrait | null = null;
    const portraitSnap = await getDoc(
      doc(db, "users", currentUser.uid, "companionMemory", "portrait"),
    );
    if (portraitSnap.exists()) {
      const data = portraitSnap.data();
      try {
        const summary = await decryptCombinedString(
          base64ToBytes(String(data.encryptedSummary || "")),
          rawKey,
        );
        const guidanceEncoded = String(data.encryptedUserGuidance || "");
        const userGuidance = guidanceEncoded
          ? (await decryptCombinedString(base64ToBytes(guidanceEncoded), rawKey)).trim()
          : null;
        const sourceEntryIDs = Array.isArray(data.sourceEntryIDs)
          ? data.sourceEntryIDs.map(String).filter(Boolean)
          : [];
        portrait = {
          summary: summary.trim(),
          userGuidance: userGuidance || null,
          version: Number(data.version || 1),
          sourceEntryIDs,
          updatedAt: timestampToDate(data.updatedAt),
          relationshipStage: companionRelationshipStage(sourceEntryIDs.length),
        };
      } catch {
        portrait = null;
      }
    }

    if (auth.currentUser?.uid !== currentUser.uid) return;
    state.companionContext = {
      memories,
      portrait,
      loaded: true,
      loading: false,
      error: null,
    };
  } catch {
    if (auth.currentUser?.uid !== currentUser.uid) return;
    state.companionContext = {
      ...state.companionContext,
      loaded: true,
      loading: false,
      error: "Private Companion context is unavailable right now.",
    };
  }
  if (auth.currentUser?.uid !== currentUser.uid) return;
  if (state.companionSession) {
    state.companionSession.contextLoaded = true;
    state.companionSession.relatedPatterns = companionRelatedPatterns();
    if (
      state.companionSession.entryId === standaloneCompanionEntryId &&
      state.companionSession.messages.length === 0 &&
      !state.companionSession.explicitOpening &&
      state.companionSession.conversationIntent === "talk"
    ) {
      const waitingQuestion = state.companionContext.memories
        .map((memory) => String(memory.nextQuestion || "").trim())
        .find(Boolean);
      if (waitingQuestion) state.companionSession.openingMessage = waitingQuestion;
    }
  }
  _render();
}

export async function openCompanionSession(
  entryId = standaloneCompanionEntryId,
  openingOverride?: string,
): Promise<void> {
  const isStandalone = entryId === standaloneCompanionEntryId;
  const note = isStandalone ? null : state.notes.find((item) => item.id === entryId);
  if (!isStandalone && !note) return;
  const insight = isStandalone ? undefined : state.noteInsights[entryId];
  const rememberedQuestion = state.companionContext.memories
    .map((memory) => String(memory.nextQuestion || "").trim())
    .find(Boolean);
  const openingMessage = openingOverride?.trim()
    || (isStandalone
      ? (rememberedQuestion || firstCompanionQuestion())
      : (
        String(insight?.companionMessage || "").trim() ||
        "I’m here. What part of this would you like to talk through?"
      ));
  state.companionSession = {
    sessionId: makeId(),
    requestId: makeId(),
    finishing: false,
    explicitOpening: Boolean(openingOverride?.trim()),
    entryId,
    entryText: note?.text || "",
    openingMessage,
    selectedMode: suggestedCompanionMode(
      insight?.supportPattern,
      insight?.risk,
    ),
    conversationIntent: "talk",
    supportPattern: insight?.supportPattern || null,
    risk: insight?.risk || null,
    messages: [],
    relatedPatterns: companionRelatedPatterns(),
    deepInsight: "",
    isDeepening: false,
    lastDeepenedUserTurnCount: 0,
    shouldOfferJournalDraft: false,
    shouldOfferMemory: false,
    memoryDraft: null,
    journalDraft: null,
    isPreparingMemory: false,
    isPreparingJournal: false,
    isListening: false,
    contextLoaded: state.companionContext.loaded,
    input: "",
    isSending: false,
    error: null,
  };
  _render();
  await loadCompanionContext();
}

export async function finishCompanionSession(): Promise<void> {
  const session = state.companionSession;
  if (!session || session.isSending || session.isPreparingMemory) return;
  if (!session.messages.some(m => m.role === "user")) { closeCompanionSession(); return; }
  state.companionSession = { ...session, finishing: true };
  _render();
  if (!session.memoryDraft) await prepareCompanionMemory();
}

export function editCompanionMemory(field: "summary" | "nextQuestion", value: string): void {
  const session = state.companionSession;
  if (!session?.memoryDraft) return;
  session.memoryDraft = { ...session.memoryDraft, [field]: value.slice(0, field === "summary" ? 700 : 220) };
}

export function keepTalking(): void {
  if (state.companionSession) { state.companionSession.finishing = false; state.companionSession.memoryDraft = null; }
  _render();
}

export function closeCompanionSession(): void {
  state.companionSession = null;
  _render();
}

async function companionResponseFailure(response: Response, session: NonNullable<typeof state.companionSession>): Promise<Error> {
  if (response.status === 402) {
    await refreshCompanionAccess();
    if (session.selectedMode !== "ground" && (!session.risk || session.risk.level === "none")) openCompanionPlus();
  }
  const body = await response.json().catch(() => ({})) as { error?: string };
  return new Error(body.error || `companion_${response.status}`);
}

export async function sendCompanionMessage(): Promise<void> {
  if (!state.companionContext.loaded) await loadCompanionContext();
  const session = state.companionSession;
  const currentUser = auth.currentUser;
  if (!session || !currentUser || session.isSending) return;
  const text = session.input.trim();
  if (!text) return;
  const userMessage: CompanionMessage = {
    id: makeId(),
    role: "user",
    content: text,
    createdAt: new Date(),
  };
  state.companionSession = {
    ...session,
    input: "",
    messages: [...session.messages, userMessage],
    isSending: true,
    error: null,
  };
  _render();

  try {
    const token = await currentUser.getIdToken();
    const history = session.messages
      .map((message) => ({ role: message.role, content: message.content }));
    const memories = state.companionContext.memories.slice(0, 3).map((memory) => ({
      summary: memory.summary.slice(0, 360),
      afterglow: String(memory.afterglow || "").slice(0, 220),
      nextQuestion: String(memory.nextQuestion || "").slice(0, 180),
      suggestedMode: memory.suggestedMode || "",
      supportPatternLabel: memory.supportPatternLabel || "",
    }));
    const response = await fetch(continueAIConversationURL, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({
        entryId: session.entryId,
        sessionId: session.sessionId,
        requestId: session.requestId,
        userMessage: text,
        outputLanguage: currentAnalysisLanguage(),
        companionMode: session.selectedMode || "reflect",
        conversationIntent: session.conversationIntent,
        supportPattern: session.supportPattern || undefined,
        history,
        entryText: session.entryText.slice(0, 4000),
        openingMessage: session.openingMessage.slice(0, 1000),
        companionMemories: memories,
        relatedPatterns: session.relatedPatterns.slice(0, 3),
        userPortrait: companionPortraitPayload(),
      }),
    });
    if (auth.currentUser?.uid !== currentUser.uid) throw new Error("Account changed.");
    if (!response.ok) throw await companionResponseFailure(response, session);
    const json = await response.json() as {
      message?: string;
      messages?: string[];
      suggestedMode?: CompanionMode;
      shouldOfferJournalDraft?: boolean;
      shouldOfferMemory?: boolean;
      risk?: AIRiskSignal;
    };
    const content = String(json.message || "").trim();
    if (!content) throw new Error("empty_companion_message");
    // Texting cadence: the server may split a reply into up to three
    // bubbles. Render each as its own message, like iOS.
    const parts = (Array.isArray(json.messages) ? json.messages : [])
      .map((part) => String(part || "").trim())
      .filter(Boolean);
    const bubbleTexts = parts.length > 0 ? parts : [content];
    const aiMessages: CompanionMessage[] = bubbleTexts.map((text) => ({
      id: makeId(),
      role: "ai",
      content: text,
      createdAt: new Date(),
    }));
    if (state.companionSession?.sessionId === session.sessionId) {
      state.companionSession = {
        ...state.companionSession,
        messages: [...state.companionSession.messages, ...aiMessages],
        requestId: makeId(),
        selectedMode: json.suggestedMode || state.companionSession.selectedMode,
        shouldOfferJournalDraft: json.shouldOfferJournalDraft === true,
        shouldOfferMemory: json.shouldOfferMemory === true,
        risk: json.risk || state.companionSession.risk,
        isSending: false,
      };
      await refreshCompanionAccess();
      _render();
      const userTurnCount = state.companionSession.messages
        .filter((message) => message.role === "user").length;
      if (
        state.companionSession.conversationIntent === "deeper" &&
        userTurnCount - state.companionSession.lastDeepenedUserTurnCount >= 2
      ) {
        runInBackground(requestDeeperCompanionInsight);
      }
    }
  } catch (failure) {
    if (state.companionSession?.sessionId === session.sessionId) {
      const code = failure instanceof Error ? failure.message : "";
      const message = code === "companion_session_complete"
        ? "This session has reached its limit. Finish for now, then start a new conversation."
        : code === "companion_request_already_processed"
          ? "That request was already processed. Send a new request to continue."
          : code === "companion_allowance_exhausted"
            ? "Your allowance is used for now. You can return when it resets."
            : "Couldn't send — please try again.";
      state.companionSession = {
        ...state.companionSession,
        requestId: code === "companion_request_already_processed" ? makeId() : state.companionSession.requestId,
        isSending: false,
        messages: state.companionSession.messages.filter(m => m.id !== userMessage.id),
        input: text,
        error: message,
      };
      _render();
    }
  }
}

export function setCompanionMode(mode: CompanionMode): void {
  if (!state.companionSession) return;
  state.companionSession = {
    ...state.companionSession,
    selectedMode: mode,
    error: null,
  };
  _render();
}

export async function setCompanionIntent(intent: CompanionConversationIntent): Promise<void> {
  if (!state.companionSession) return;
  state.companionSession = {
    ...state.companionSession,
    conversationIntent: intent,
    error: null,
  };
  _render();
  if (intent === "deeper" && state.companionSession.messages.some((message) => message.role === "user")) {
    await requestDeeperCompanionInsight();
  }
}

export async function requestDeeperCompanionInsight(): Promise<void> {
  if (!state.companionSession || state.companionSession.isDeepening) return;
  // Standalone conversations have no note to conclude about — the deepening
  // scaffold would make the model analyze the scaffold prompt itself and
  // produce a hollow meta-observation. Look closer still shifts the talking
  // register; it just never fabricates a conclusion card.
  if (state.companionSession.entryId === standaloneCompanionEntryId) return;
  if (!state.companionContext.loaded) await loadCompanionContext();
  const session = state.companionSession;
  const currentUser = auth.currentUser;
  if (!currentUser) return;
  state.companionSession = { ...session, isDeepening: true, error: null };
  _render();
  try {
    const token = await currentUser.getIdToken();
    const response = await fetch(continueAIConversationURL, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({
        entryId: session.entryId,
        entryText: session.entryText.slice(0, 4000),
        openingMessage: session.openingMessage.slice(0, 1000),
        userMessage: [
          "Analyze this directly and go deeper. Use what I said in this session,",
          "the note I opened, recurring patterns, and only the memories I approved.",
          "Offer one grounded psychological formulation and clearly separate",
          "what my words support from what is only a possibility.",
        ].join(" "),
        outputLanguage: currentAnalysisLanguage(),
        companionMode: session.selectedMode === "ground" ? "ground" : "pattern",
        conversationIntent: "deeper",
        history: session.messages.map((message) => ({
          role: message.role,
          content: message.content,
        })),
        companionMemories: state.companionContext.memories.slice(0, 3).map((memory) => ({
          summary: memory.summary,
          afterglow: memory.afterglow || "",
          nextQuestion: memory.nextQuestion || "",
          suggestedMode: memory.suggestedMode || "",
          supportPatternLabel: memory.supportPatternLabel || "",
        })),
        relatedPatterns: session.relatedPatterns,
        userPortrait: companionPortraitPayload(),
      }),
    });
    if (auth.currentUser?.uid !== currentUser.uid) throw new Error("Account changed.");
    if (!response.ok) throw await companionResponseFailure(response, session);
    const json = await response.json() as { message?: string };
    const insight = String(json.message || "").trim();
    if (!insight) throw new Error("empty_deeper_insight");
    if (state.companionSession?.sessionId === session.sessionId) {
      state.companionSession = {
        ...state.companionSession,
        deepInsight: insight,
        isDeepening: false,
        lastDeepenedUserTurnCount: state.companionSession.messages
          .filter((message) => message.role === "user").length,
      };
      _render();
    }
  } catch (failure) {
    if (state.companionSession?.sessionId === session.sessionId) {
      const code = failure instanceof Error ? failure.message : "";
      state.companionSession = {
        ...state.companionSession,
        isDeepening: false,
        error: code === "companion_allowance_exhausted" ? "Your allowance is used for now. You can return when it resets." : code === "companion_session_complete" ? "Finish this session, then start a new conversation." : "Couldn’t go deeper right now. You can keep talking.",
      };
      _render();
    }
  }
}

export async function prepareCompanionMemory(): Promise<void> {
  const session = state.companionSession;
  const currentUser = auth.currentUser;
  if (!session || !currentUser || session.isPreparingMemory) return;
  state.companionSession = { ...session, isPreparingMemory: true, error: null };
  _render();
  try {
    const token = await currentUser.getIdToken();
    const response = await fetch(summarizeCompanionMemoryURL, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({
        entryId: session.entryId,
        sessionId: session.sessionId,
        requestId: makeId(),
        entryText: session.entryText.slice(0, 3000),
        openingMessage: session.openingMessage.slice(0, 800),
        messages: session.messages.slice(-10).map((message) => ({
          role: message.role,
          content: message.content.slice(0, 500),
        })),
        companionMode: session.selectedMode,
        supportPattern: session.supportPattern || undefined,
        outputLanguage: currentAnalysisLanguage(),
      }),
    });
    if (auth.currentUser?.uid !== currentUser.uid) throw new Error("Account changed.");
    if (!response.ok) throw new Error(`memory_${response.status}`);
    const json = await response.json() as Record<string, unknown>;
    const suggestedModeRaw = String(json.suggestedMode || "");
    const suggestedMode = (
      suggestedModeRaw === "reflect" ||
      suggestedModeRaw === "clarify" ||
      suggestedModeRaw === "ground" ||
      suggestedModeRaw === "pattern" ||
      suggestedModeRaw === "question"
    ) ? suggestedModeRaw : null;
    const draft: CompanionMemoryDraft = {
      summary: String(json.summary || "").trim(),
      afterglow: String(json.afterglow || "").trim() || null,
      nextQuestion: String(json.nextQuestion || "").trim() || null,
      suggestedMode,
      supportPatternId: String(json.supportPatternId || "").trim() || null,
      supportPatternLabel: String(json.supportPatternLabel || "").trim() || null,
      rememberable: json.rememberable === true,
    };
    if (state.companionSession?.sessionId === session.sessionId) {
      state.companionSession = {
        ...state.companionSession,
        memoryDraft: state.companionSession.finishing && draft.rememberable ? draft : null,
        shouldOfferMemory: draft.rememberable,
        isPreparingMemory: false,
      };
      _render();
    }
  } catch {
    if (state.companionSession?.sessionId === session.sessionId) {
      state.companionSession = {
        ...state.companionSession,
        isPreparingMemory: false,
        error: "Couldn’t prepare a private memory right now.",
      };
      _render();
    }
  }
}

export async function savePreparedCompanionMemory(): Promise<void> {
  const session = state.companionSession;
  const draft = session?.memoryDraft ? { ...session.memoryDraft, ...(session.finishing ? { afterglow: null } : {}) } : null;
  const currentUser = auth.currentUser;
  const rawKey = getUnlockedCrossPlatformKey();
  if (!session || !draft?.rememberable || !draft.summary || !currentUser || !rawKey) {
    showToast("Unlock note encryption before saving Companion memory.");
    return;
  }
  _setBusy("save-companion-memory", true);
  try {
    const encryptedOptional = async (text: string | null | undefined) => {
      const trimmed = String(text || "").trim();
      return trimmed ? encryptCombinedString(trimmed, rawKey) : null;
    };
    const memoryRef = doc(
      collection(db, "users", currentUser.uid, "companionMemories"),
    );
    const now = new Date();
    const payload: Record<string, unknown> = {
      schemaVersion: 1,
      encryptedSummary: await encryptCombinedString(draft.summary, rawKey),
      encryptionVersion: 2,
      keyMode: "cross_platform",
      sourceEntryId: session.entryId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
    const encryptedAfterglow = await encryptedOptional(draft.afterglow);
    const encryptedNextQuestion = await encryptedOptional(draft.nextQuestion);
    if (encryptedAfterglow) payload.encryptedAfterglow = encryptedAfterglow;
    if (encryptedNextQuestion) payload.encryptedNextQuestion = encryptedNextQuestion;
    if (draft.suggestedMode) payload.suggestedMode = draft.suggestedMode;
    if (draft.supportPatternId) payload.supportPatternId = draft.supportPatternId;
    if (draft.supportPatternLabel) payload.supportPatternLabel = draft.supportPatternLabel;
    await setDoc(memoryRef, payload);
    if (auth.currentUser?.uid !== currentUser.uid) throw new Error("Account changed.");

    state.companionContext.memories.unshift({
      id: memoryRef.id,
      summary: draft.summary,
      afterglow: draft.afterglow,
      nextQuestion: draft.nextQuestion,
      suggestedMode: draft.suggestedMode,
      supportPatternId: draft.supportPatternId,
      supportPatternLabel: draft.supportPatternLabel,
      sourceEntryId: session.entryId,
      createdAt: now,
      updatedAt: now,
    });
    state.companionContext.memories = state.companionContext.memories.slice(0, 24);
    state.companionSession = {
      ...session,
      memoryDraft: null,
      shouldOfferMemory: false,
    };
    await rebuildCompanionPortrait();
    showToast("Remembered privately.");
    if (session.finishing) closeCompanionSession();
  } catch {
    showToast("Could not save Companion memory.");
  } finally {
    _setBusy("save-companion-memory", false);
    _render();
  }
}

export function dismissCompanionMemoryOffer(): void {
  if (!state.companionSession) return;
  state.companionSession = {
    ...state.companionSession,
    memoryDraft: null,
    shouldOfferMemory: false,
  };
  _render();
}

export async function prepareCompanionJournal(): Promise<void> {
  const session = state.companionSession;
  const currentUser = auth.currentUser;
  if (!session || !currentUser || session.isPreparingJournal) return;
  state.companionSession = { ...session, isPreparingJournal: true, error: null };
  _render();
  try {
    const token = await currentUser.getIdToken();
    const response = await fetch(generateCompanionJournalURL, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({
        entryId: session.entryId,
        sessionId: session.sessionId,
        requestId: makeId(),
        entryText: session.entryText.slice(0, 3000),
        openingMessage: session.openingMessage.slice(0, 800),
        messages: session.messages.slice(-16).map((message) => ({
          role: message.role,
          content: message.content.slice(0, 800),
        })),
        portraitSummary: String(state.companionContext.portrait?.summary || "").slice(0, 1200),
        outputLanguage: currentAnalysisLanguage(),
      }),
    });
    if (auth.currentUser?.uid !== currentUser.uid) throw new Error("Account changed.");
    if (!response.ok) throw await companionResponseFailure(response, session);
    const json = await response.json() as Record<string, unknown>;
    const body = String(json.body || "").trim();
    if (!body) throw new Error("empty_journal");
    const draft: CompanionJournalDraft = {
      body,
      language: String(json.language || currentAnalysisLanguage()),
      factualConfidence: Number(json.factualConfidence || 0.5),
      requiresCarefulReview: json.requiresCarefulReview !== false,
    };
    if (state.companionSession?.sessionId === session.sessionId) {
      state.companionSession = {
        ...state.companionSession,
        journalDraft: draft,
        isPreparingJournal: false,
      };
      _render();
    }
  } catch {
    if (state.companionSession?.sessionId === session.sessionId) {
      state.companionSession = {
        ...state.companionSession,
        isPreparingJournal: false,
        error: "Couldn’t put this into your words right now.",
      };
      _render();
    }
  }
}

export function updateCompanionJournalDraft(body: string): void {
  if (!state.companionSession?.journalDraft) return;
  state.companionSession.journalDraft = {
    ...state.companionSession.journalDraft,
    body,
  };
}

export function discardCompanionJournalDraft(): void {
  if (!state.companionSession) return;
  state.companionSession = { ...state.companionSession, journalDraft: null };
  _render();
}

export async function saveCompanionJournalDraft(): Promise<void> {
  const session = state.companionSession;
  const currentUser = auth.currentUser;
  const rawKey = getUnlockedCrossPlatformKey();
  const text = String(session?.journalDraft?.body || "").trim();
  if (!session || !currentUser || !rawKey || !text) {
    showToast("Unlock note encryption before saving this reflection.");
    return;
  }
  _setBusy("save-companion-journal", true);
  try {
    const entryId = makeId();
    await setDoc(
      doc(db, "users", currentUser.uid, "entries", entryId),
      await encryptedEntryPayload(text, rawKey, "private"),
      { merge: false },
    );
    state.companionSession = { ...session, journalDraft: null };
    await fetchNotes();
    showToast("Saved as a private note.");
    runInBackground(async () => {
      await analyzeNoteIfEnabled(state.notes.find((note) => note.id === entryId));
      await fetchThinkingTimeline({ refresh: true });
      await rebuildCompanionPortrait();
      _render();
    });
  } catch {
    showToast("Could not save this reflection.");
  } finally {
    _setBusy("save-companion-journal", false);
    _render();
  }
}

export async function rebuildCompanionPortrait(): Promise<void> {
  const currentUser = auth.currentUser;
  const rawKey = getUnlockedCrossPlatformKey();
  if (!currentUser || !rawKey) return;
  const existing = state.companionContext.portrait;
  const sources = state.notes
    .map((note) => {
      const insight = state.noteInsights[note.id];
      const summary = reflectionSummaryForPortrait(insight?.reflection);
      return summary ? {
        id: note.id,
        visibility: note.visibility || "private",
        summary,
        patternLabel: insight?.supportPattern?.label || "",
        updatedAt: note.updatedAt,
      } : null;
    })
    .filter(Boolean)
    .sort((left, right) => right!.updatedAt.getTime() - left!.updatedAt.getTime())
    .slice(0, 12) as Array<{
      id: string;
      visibility: string;
      summary: string;
      patternLabel: string;
      updatedAt: Date;
    }>;
  const sourceLines = sources.map((source) => {
    const pattern = source.patternLabel ? ` [${source.patternLabel}]` : "";
    return `${source.visibility}${pattern}: ${source.summary.slice(0, 220)}`;
  });
  const memoryLines = state.companionContext.memories
    .slice(0, 4)
    .map((memory) => `Remembered: ${memory.summary.slice(0, 220)}`);
  const summary = [...sourceLines, ...memoryLines].join("\n").trim();
  if (!summary) {
    if (state.notes.length > 0 && existing) return;
    return;
  }
  const sourceEntryIDs = sources.map((source) => source.id);
  if (
    existing?.summary === summary &&
    JSON.stringify(existing.sourceEntryIDs) === JSON.stringify(sourceEntryIDs)
  ) return;

  const version = Number(existing?.version || 0) + 1;
  const payload: Record<string, unknown> = {
    schemaVersion: 1,
    version,
    encryptedSummary: await encryptCombinedString(summary, rawKey),
    encryptionVersion: 2,
    keyMode: "cross_platform",
    sourceEntryIDs,
    updatedAt: serverTimestamp(),
  };
  if (existing?.userGuidance) {
    payload.encryptedUserGuidance = await encryptCombinedString(
      existing.userGuidance,
      rawKey,
    );
  }
  await setDoc(
    doc(db, "users", currentUser.uid, "companionMemory", "portrait"),
    payload,
    { merge: true },
  );
  state.companionContext.portrait = {
    summary,
    userGuidance: existing?.userGuidance || null,
    version,
    sourceEntryIDs,
    updatedAt: new Date(),
    relationshipStage: companionRelationshipStage(sourceEntryIDs.length),
  };
}

function reflectionSummaryForPortrait(reflection: AIReflection | undefined): string {
  return String(
    reflection?.summary ||
    reflection?.tension ||
    reflection?.logic ||
    reflection?.themes?.[0] ||
    "",
  ).trim();
}

export async function updateCompanionPortraitGuidance(guidance: string): Promise<void> {
  const currentUser = auth.currentUser;
  const rawKey = getUnlockedCrossPlatformKey();
  const portrait = state.companionContext.portrait;
  if (!currentUser || !rawKey || !portrait) return;
  const trimmed = guidance.trim();
  const payload: Record<string, unknown> = {
    encryptedSummary: await encryptCombinedString(portrait.summary, rawKey),
    encryptionVersion: 2,
    keyMode: "cross_platform",
    version: portrait.version + 1,
    sourceEntryIDs: portrait.sourceEntryIDs,
    updatedAt: serverTimestamp(),
    encryptedUserGuidance: trimmed
      ? await encryptCombinedString(trimmed, rawKey)
      : deleteField(),
  };
  await setDoc(
    doc(db, "users", currentUser.uid, "companionMemory", "portrait"),
    payload,
    { merge: true },
  );
  state.companionContext.portrait = {
    ...portrait,
    userGuidance: trimmed || null,
    version: portrait.version + 1,
    updatedAt: new Date(),
  };
  showToast("Companion understanding updated.");
  _render();
}

export async function deleteCompanionMemory(memoryId: string): Promise<void> {
  const currentUser = auth.currentUser;
  if (!currentUser) return;
  await deleteDoc(doc(db, "users", currentUser.uid, "companionMemories", memoryId));
  state.companionContext.memories = state.companionContext.memories
    .filter((memory) => memory.id !== memoryId);
  await rebuildCompanionPortrait();
  _render();
}

export async function deleteCompanionPortrait(): Promise<void> {
  const currentUser = auth.currentUser;
  if (!currentUser) return;
  await deleteDoc(doc(db, "users", currentUser.uid, "companionMemory", "portrait"));
  state.companionContext.portrait = null;
  showToast("Companion understanding deleted.");
  _render();
}

async function filterValidSimilarThoughts(thoughts: SimilarThought[]): Promise<SimilarThought[]> {
  const results = await Promise.all(thoughts.map(async (thought) => ({
    thought,
    valid: await isThoughtCurrentlyVisible(thought),
  })));
  return results.filter((item) => item.valid).map((item) => item.thought);
}

async function isThoughtCurrentlyVisible(thought: SimilarThought): Promise<boolean> {
  try {
    if (thought.scope === "sharedPost" || thought.scope === "shared") {
      const snap = await getDoc(doc(db, "sharedPosts", thought.postId));
      return snap.exists() && String(snap.data().visibility || "public") === "public";
    }
    if (thought.scope === "communityPost" || thought.scope === "community") {
      if (!thought.groupId) return false;
      const snap = await getDoc(doc(db, "groups", thought.groupId, "posts", thought.postId));
      return snap.exists();
    }
    return false;
  } catch {
    return false;
  }
}

// ── Feed ────────────────────────────────────────────────────────────────────
function asDocPath(segments: string[]): [string, string, ...string[]] {
  if (segments.length < 2 || segments.length % 2 !== 0 || segments.some((segment) => !segment)) {
    throw new Error(`Invalid Firestore document path: ${segments.join("/")}`);
  }
  return segments as [string, string, ...string[]];
}

async function fetchMaterializedFeedRoot(rootSegments: string[]): Promise<{ activeIds: string[]; newIds: string[]; discoveryIds: string[]; orderedIds: string[]; cards: Record<string, unknown>[] } | null> {
  const rootRef = doc(db, ...asDocPath(rootSegments));
  const rootSnap = await getDoc(rootRef);
  if (!rootSnap.exists()) return null;
  const rootData = rootSnap.data() || {};
  const pageIds: string[] = Array.isArray(rootData.pageIds) && rootData.pageIds.length > 0 ? rootData.pageIds.filter(Boolean) : ["0001"];
  const pageSnaps = await Promise.all(pageIds.map((pid) => getDoc(doc(db, ...asDocPath([...rootSegments, "pages", pid])))));
  const cards: Record<string, unknown>[] = [];
  for (const ps of pageSnaps) {
    if (!ps.exists()) continue;
    const c = Array.isArray(ps.data().cards) ? ps.data().cards : [];
    cards.push(...c);
  }
  return {
    activeIds: Array.isArray(rootData.activeIds) ? rootData.activeIds.filter(Boolean) : [],
    newIds: Array.isArray(rootData.newIds) ? rootData.newIds.filter(Boolean) : [],
    discoveryIds: Array.isArray(rootData.discoveryIds) ? rootData.discoveryIds.filter(Boolean) : [],
    orderedIds: Array.isArray(rootData.orderedIds) ? rootData.orderedIds.filter(Boolean) : [],
    cards,
  };
}

function duplicateIdCount(items: Array<{ id?: string }>): number {
  const seen = new Set<string>();
  let duplicates = 0;
  for (const item of items) {
    const id = String(item?.id || "").trim();
    if (!id) continue;
    if (seen.has(id)) duplicates += 1;
    else seen.add(id);
  }
  return duplicates;
}

function normalizeDiscoveryDirection(raw: Record<string, unknown>): DiscoveryDirection | null {
  const id = String(raw.id || "").trim();
  const label = String(raw.label || "").trim();
  if (!id || !label || label.split(/\s+/).length > 3) return null;
  const terms = Array.isArray(raw.terms)
    ? raw.terms.map((term) => String(term || "").trim()).filter(Boolean).slice(0, 12)
    : undefined;
  const countText = String(raw.countText || "").trim() || null;
  return { id, label, terms, countText };
}

type DiscoveryFetchResult = {
  directions: DiscoveryDirection[];
  rankedIds: string[];
  relatedIds: string[];
  tagsByPostId: Record<string, DiscoveryTag>;
};

async function fetchDiscoveryResult(directionId: string | null = null): Promise<DiscoveryFetchResult | null> {
  const headers: Record<string, string> = {};
  const token = await auth.currentUser?.getIdToken().catch(() => null);
  if (token) headers.Authorization = `Bearer ${token}`;

  const fetchGenerated = async () => {
    const url = directionId
      ? `${discoveryFeedURL}&directionId=${encodeURIComponent(directionId)}`
      : discoveryFeedURL;
    const response = await fetch(url, { headers });
    if (!response.ok) throw new Error(`Discovery feed failed: ${response.status}`);
    const payload = await response.json() as {
      activeDirections?: Record<string, unknown>[];
      items?: Array<{ sharedPostId?: string; directionId?: string; directionLabel?: string }>;
    };
    // Same as iOS: every ranked post carries the shelf it belongs on most.
    const tagsByPostId: Record<string, DiscoveryTag> = {};
    (payload.items || []).forEach((item) => {
      const id = String(item.sharedPostId || "").trim();
      const tagId = String(item.directionId || "").trim();
      const label = String(item.directionLabel || "").trim();
      if (id && tagId && label) tagsByPostId[id] = { id: tagId, label };
    });
    return {
      directions: (payload.activeDirections || [])
        .map(normalizeDiscoveryDirection)
        .filter(Boolean) as DiscoveryDirection[],
      rankedIds: (payload.items || [])
        .map((item) => String(item.sharedPostId || "").trim())
        .filter(Boolean),
      relatedIds: [],
      tagsByPostId,
    };
  };

  const fetchLegacy = async () => {
    const response = await fetch(discoveryDirectionsURL, { headers });
    if (!response.ok) throw new Error(`Discovery directions failed: ${response.status}`);
    const payload = await response.json() as {
      directions?: Record<string, unknown>[];
      ranked?: string[];
      related?: string[];
    };
    return {
      directions: (payload.directions || [])
        .map(normalizeDiscoveryDirection)
        .filter(Boolean) as DiscoveryDirection[],
      rankedIds: (payload.ranked || []).map((id) => String(id || "").trim()).filter(Boolean),
      relatedIds: (payload.related || []).map((id) => String(id || "").trim()).filter(Boolean),
      tagsByPostId: {},
    };
  };

  try {
    return await fetchGenerated();
  } catch (generatedError) {
    console.warn("[LOT discovery] generated feed fallback", generatedError);
    try {
      return await fetchLegacy();
    } catch (legacyError) {
      console.warn("[LOT discovery] directions unavailable", legacyError);
      return null;
    }
  }
}

async function hydrateMissingDiscoveryPosts(rankedIds: string[], knownPosts: SharedPost[]): Promise<SharedPost[]> {
  const knownIds = new Set(knownPosts.map((post) => post.id));
  const missingIds = rankedIds.filter((id) => id && !knownIds.has(id)).slice(0, 30);
  if (missingIds.length === 0) return [];
  const fetched = await Promise.all(missingIds.map((id) => fetchSharedPostById(id).catch(() => null)));
  return fetched.filter((post): post is SharedPost => Boolean(post && post.visibility === "public"));
}

export async function fetchPublicFeedAsGuest(): Promise<void> {
  try {
    console.debug("[LOT feed] refresh", { reason: "guest_public_feed" });
    const discovery = await fetchDiscoveryResult();
    if (discovery) {
      state.discoveryDirections = discovery.directions;
      state.discoveryTagsByPostId = { ...state.discoveryTagsByPostId, ...discovery.tagsByPostId };
      state.discoveryRankedIds = discovery.rankedIds;
      state.discoveryRelatedIds = discovery.relatedIds;
    }
    const publicRoot = await fetchMaterializedFeedRoot(["feeds", "public_v2"]);
    if (!publicRoot) return;
    const posts: import("./types").SharedPost[] = [];
    for (const card of publicRoot.cards) {
      const parsed = parseSharedPostFromCard(card);
      if (parsed) posts.push(parsed);
    }
    if (discovery?.rankedIds.length) {
      posts.push(...await hydrateMissingDiscoveryPosts(discovery.rankedIds, posts));
    }
    const duplicateCount = duplicateIdCount(posts);
    state.feedPosts = dedupeById(posts);
    console.debug("[LOT feed] loaded", {
      source: "public_v2",
      cards: publicRoot.cards.length,
      posts: state.feedPosts.length,
      duplicatesPrevented: duplicateCount,
      discoveryRankedIds: state.discoveryRankedIds.length,
      discoveryDirections: state.discoveryDirections.length,
      newIds: publicRoot.newIds.length,
    });
    state.feedSectionIds.all = {
      active: publicRoot.activeIds.map((id) => posts.find((p) => p.id === id)).filter(Boolean) as import("./types").SharedPost[],
      newThoughts: publicRoot.newIds.map((id) => posts.find((p) => p.id === id)).filter(Boolean) as import("./types").SharedPost[],
      discovery: publicRoot.discoveryIds.map((id) => posts.find((p) => p.id === id)).filter(Boolean) as import("./types").SharedPost[],
    };
    _render();
  } catch (err) {
    console.warn("fetchPublicFeedAsGuest failed", err);
  } finally {
    if (!state.feedInitialLoadDone) {
      state.feedInitialLoadDone = true;
      _render();
    }
  }
}

export async function fetchFeed(): Promise<void> {
  if (!state.user) return;
  const uid = state.user.uid;
  console.debug("[LOT feed] refresh", { reason: "signed_in_feed", uid });
  await Promise.allSettled([loadResonancedPosts(), loadSavedPosts(false)]);

  const discovery = await fetchDiscoveryResult();
  if (discovery) {
    state.discoveryDirections = discovery.directions;
      state.discoveryTagsByPostId = { ...state.discoveryTagsByPostId, ...discovery.tagsByPostId };
    state.discoveryRankedIds = discovery.rankedIds;
    state.discoveryRelatedIds = discovery.relatedIds;
  }

  let publicRoot: Awaited<ReturnType<typeof fetchMaterializedFeedRoot>> = null;
  let friendsRoot: Awaited<ReturnType<typeof fetchMaterializedFeedRoot>> = null;
  const materialized: SharedPost[] = [];

  try {
    publicRoot = await fetchMaterializedFeedRoot(["feeds", "public_v2"]);
    if (publicRoot) {
      for (const card of publicRoot.cards) {
        const parsed = parseSharedPostFromCard(card);
        if (parsed) materialized.push(parsed);
      }
    }
  } catch (error) {
    console.warn("public_v2 read fallback", error);
  }

  try {
    friendsRoot = await fetchMaterializedFeedRoot(["feeds", "users", uid, "friends_v2"]);
    if (friendsRoot) {
      for (const card of friendsRoot.cards) {
        const parsed = parseSharedPostFromCard(card);
        if (parsed) materialized.push(parsed);
      }
    }
  } catch (error) {
    console.warn("friends_v2 read fallback", error);
  }

  let combined = dedupeById(materialized);
  const materializedDuplicateCount = duplicateIdCount(materialized);

  const mapRawSharedPost = (row: Record<string, unknown>): SharedPost | null => {
    const id = String(row.id || "").trim();
    const authorUid = String(row.authorUid || "").trim();
    const text = String(row.text || "").trim();
    const visibility = String(row.visibility || "").trim();
    if (!id || !authorUid || !text || !visibility) return null;
    const meta = row.cardMeta as Record<string, unknown> | undefined;
    return {
      id,
      authorUid,
      authorUsername: "",
      text,
      visibility,
      attribution: ((row.attribution as string) || "anonymous") as "anonymous" | "username",
      createdAt: timestampToDate(row.createdAt as never),
      sharedAt: timestampToDate((row.sharedAt || row.createdAt) as never),
      replyCount: Number(row.replyCount || 0),
      resonanceCount: Number(row.resonanceCount || 0),
      uniqueReplierCount: Number(row.uniqueReplierCount || 0),
      uniqueViewerCount: Number(row.uniqueViewerCount || 0),
      maxReplyDepth: Number(row.maxReplyDepth || 0),
      heatScore: Number(row.heatScore || 0),
      replyRate: Number(row.replyRate || 0),
      cardResponseLabel: (meta?.responseLabel as string) || null,
      cardDiscussingText: (meta?.discussingText as string) || null,
      allowsAnonymousReplies: row.allowsAnonymousReplies === true || ((row.attribution as string) || "anonymous") === "anonymous",
      anonymousColorIndex: row.anonymousColorIndex == null ? null : Number(row.anonymousColorIndex),
    };
  };

  if (combined.length < 12) {
    const fallbackRows: Record<string, unknown>[] = [];
    const acceptedFriendUids = state.friends
      .filter((friend) => friend.status === "accepted")
      .map((friend) => friend.friendUid);

    const publicSnap = await safeRun(async () => {
      const q = query(
        collection(db, "sharedPosts"),
        where("visibility", "==", "public"),
        orderBy("sharedAt", "desc"),
        limit(120)
      );
      return await getDocs(q);
    });
    if (publicSnap) {
      fallbackRows.push(...publicSnap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
    }

    const ownSnap = await safeRun(async () => {
      const q = query(collection(db, "sharedPosts"), where("authorUid", "==", uid), orderBy("sharedAt", "desc"), limit(80));
      return await getDocs(q);
    });
    if (ownSnap) {
      fallbackRows.push(...ownSnap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
    }

    for (let i = 0; i < acceptedFriendUids.length; i += 10) {
      const batch = acceptedFriendUids.slice(i, i + 10);
      if (batch.length === 0) continue;
      const friendSnap = await safeRun(async () => {
        const q = query(
          collection(db, "sharedPosts"),
          where("visibility", "==", "friends"),
          where("authorUid", "in", batch),
          orderBy("sharedAt", "desc"),
          limit(80)
        );
        return await getDocs(q);
      });
      if (friendSnap) {
        fallbackRows.push(...friendSnap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
      }
    }

    const fallbackPosts = fallbackRows
      .map((row) => mapRawSharedPost(row))
      .filter(Boolean) as SharedPost[];
    const beforeFallbackDedupe = [...combined, ...fallbackPosts];
    combined = dedupeById(beforeFallbackDedupe);
    console.debug("[LOT feed] fallback merged", {
      materializedPosts: materialized.length,
      fallbackRows: fallbackRows.length,
      fallbackPosts: fallbackPosts.length,
      duplicatesPrevented: duplicateIdCount(beforeFallbackDedupe),
    });
  }

  if (discovery?.rankedIds.length) {
    const missingDiscoveryPosts = await hydrateMissingDiscoveryPosts(discovery.rankedIds, combined);
    combined = dedupeById([...combined, ...missingDiscoveryPosts]);
  }

  combined = combined
    .filter((post) => Boolean(post.id && post.authorUid && post.text && post.visibility))
    .sort((a, b) => timestampToDate(b.sharedAt).getTime() - timestampToDate(a.sharedAt).getTime());

  state.hiddenFeedPostIds = loadHiddenFeedPostIds(uid);
  const hiddenPostIds = new Set(state.hiddenFeedPostIds);
  const hydrated = await hydrateUsernamesForPosts(combined.filter((post) => !hiddenPostIds.has(post.id)));
  state.feedPosts = hydrated;
  state.feedInitialLoadDone = true;
  // Reconcile optimistic publishes: once the server feed includes a thought,
  // drop it from the buffer so it never renders twice.
  if (state.recentlyPublishedPosts.length > 0) {
    const serverIds = new Set(hydrated.map((post) => post.id));
    state.recentlyPublishedPosts = state.recentlyPublishedPosts.filter((post) => !serverIds.has(post.id));
  }

  const buildServerSections = (posts: SharedPost[], roots: Array<Awaited<ReturnType<typeof fetchMaterializedFeedRoot>>>, friendOnly: boolean) => {
    const byId = new Map(posts.map((post) => [post.id, post]));
    const visiblePost = (id: string): SharedPost | null => {
      const post = byId.get(id);
      if (!post) return null;
      if (friendOnly && !(post.visibility === "friends" || post.authorUid === uid)) return null;
      return post;
    };
    const activeIds = dedupeIds(roots.filter(Boolean).flatMap((root) => root?.activeIds || []));
    const newIds = dedupeIds(roots.filter(Boolean).flatMap((root) => root?.newIds || []));
    const discoveryIds = dedupeIds(roots.filter(Boolean).flatMap((root) => root?.discoveryIds || []));
    const active = activeIds.map(visiblePost).filter(Boolean) as SharedPost[];
    const newThoughts = newIds.filter((id) => !activeIds.includes(id)).map(visiblePost).filter(Boolean) as SharedPost[];
    const used = new Set([...active, ...newThoughts].map((post) => post.id));
    const discovery = discoveryIds.filter((id) => !used.has(id)).map(visiblePost).filter(Boolean) as SharedPost[];
    return { active, newThoughts, discovery };
  };

  const fallbackSections = (posts: SharedPost[]) => {
    const nowMs = Date.now();
    const recentWindowMs = 24 * 60 * 60 * 1000;
    const withMeta = posts.map((post) => ({
      post,
      ageMs: Math.max(0, nowMs - timestampToDate(post.sharedAt || post.createdAt).getTime()),
      rank: Number(post.heatScore || 0) * 0.6 + Number(post.replyRate || 0) * 0.4,
    }));
    const active = withMeta
      .filter((entry) => Number(entry.post.replyCount || 0) > 0)
      .sort((a, b) => {
        if (b.rank !== a.rank) return b.rank - a.rank;
        return timestampToDate(b.post.sharedAt || b.post.createdAt).getTime() - timestampToDate(a.post.sharedAt || a.post.createdAt).getTime();
      })
      .map((entry) => entry.post);
    const newThoughts = withMeta
      .filter((entry) => Number(entry.post.replyCount || 0) === 0 && entry.ageMs <= recentWindowMs)
      .sort((a, b) => timestampToDate(b.post.sharedAt || b.post.createdAt).getTime() - timestampToDate(a.post.sharedAt || a.post.createdAt).getTime())
      .map((entry) => entry.post);
    const used = new Set([...active, ...newThoughts].map((post) => post.id));
    const discovery = withMeta
      .filter((entry) => !used.has(entry.post.id))
      .sort((a, b) => timestampToDate(b.post.sharedAt || b.post.createdAt).getTime() - timestampToDate(a.post.sharedAt || a.post.createdAt).getTime())
      .map((entry) => entry.post);
    return { active, newThoughts, discovery };
  };

  const allSections = buildServerSections(hydrated, [publicRoot, friendsRoot], false);
  const friendsSections = buildServerSections(hydrated, [friendsRoot], true);
  const hasServerSections = allSections.active.length + allSections.newThoughts.length + allSections.discovery.length > 0;
  state.feedSectionIds = hasServerSections
    ? { all: allSections, friends: friendsSections }
    : {
        all: fallbackSections(hydrated),
        friends: fallbackSections(hydrated.filter((post) => post.visibility === "friends" || post.authorUid === uid)),
      };
  console.debug("[LOT feed] loaded", {
    source: hasServerSections ? "materialized_sections" : "fallback_sections",
    materializedPosts: materialized.length,
    materializedDuplicatesPrevented: materializedDuplicateCount,
    discoveryRankedIds: state.discoveryRankedIds.length,
    discoveryDirections: state.discoveryDirections.length,
    visiblePosts: hydrated.length,
    hiddenPosts: hiddenPostIds.size,
    allSections: {
      active: state.feedSectionIds.all.active.length,
      newThoughts: state.feedSectionIds.all.newThoughts.length,
      discovery: state.feedSectionIds.all.discovery.length,
    },
    friendsSections: {
      active: state.feedSectionIds.friends.active.length,
      newThoughts: state.feedSectionIds.friends.newThoughts.length,
      discovery: state.feedSectionIds.friends.discovery.length,
    },
  });
}

// ── Sharing ─────────────────────────────────────────────────────────────────
export async function createSharedPost(): Promise<void> {
  if (!state.user) { showToast("Please sign in first."); return; }
  const unlockedKey = getUnlockedCrossPlatformKey();
  if (!unlockedKey) { showEncryptionWriteGate(); return; }
  const text = state.composer.text.trim();
  if (!text) { showToast("Write something first."); return; }
  const visibility: "private" | "public" | "friends" =
    state.composer.visibility === "public"
      ? "public"
      : state.composer.visibility === "friends"
        ? "friends"
        : "private";
  if (visibility !== "private" && containsObjectionableContent(text)) {
    showToast("This post contains language blocked by safety filters. Please revise and try again.");
    return;
  }
  const uid = state.user.uid;
  const postId = makeId();
  const attribution = visibility === "private"
    ? "anonymous"
    : state.composer.attribution === "username" && state.profile?.username
      ? "username"
      : "anonymous";
  _setBusy("create-post", true);
  try {
    const now = serverTimestamp();
    const batch = writeBatch(db);
    batch.set(doc(db, "users", uid, "entries", postId), await encryptedEntryPayload(text, unlockedKey, visibility, attribution), { merge: false });
    if (visibility !== "private") {
      batch.set(doc(db, "sharedPosts", postId), {
        id: postId,
        authorUid: uid,
        text,
        visibility,
        attribution,
        createdAt: now,
        sharedAt: now,
        updatedAt: now,
        replyCount: 0,
        resonanceCount: 0,
        uniqueReplierCount: 0,
        maxReplyDepth: 0,
        viewCount: 0,
        uniqueViewerCount: 0,
        heatScore: 0,
        replyRate: 0,
        explorationStatus: "exploring",
        explorationTargetViews: 30,
        ...(attribution === "anonymous" ? { allowsAnonymousReplies: true, anonymousColorIndex: 0 } : {}),
      }, { merge: false });
    }
    await batch.commit();
  } catch {
    // Nothing was persisted — safe to show a plain failure using the
    // visibility the user actually attempted (a local const, not
    // state.composer.visibility, which follow-up code below may have
    // already reset by the time a later step fails).
    _setBusy("create-post", false);
    showToast(visibility === "private" ? "Couldn't save your note. Please check your connection and try again." : "Couldn't publish your thought. Please check your connection and try again.", "error");
    return;
  }

  // The post is now durably saved. Everything below is best-effort
  // follow-up (local state, feed refresh, background analysis) — failures
  // here must never surface a "couldn't save" toast, since that would
  // contradict the success the user is about to see.
  let shouldPrepareExcerpt = false;
  try {
    state.composerResonance = null;
    // Optimistic insertion: the materialized public feed is rebuilt on the
    // backend's schedule, so a just-published thought can lag. Show the
    // author's own thought at the top of Discovery instantly; it's deduped by
    // id and pruned once the server feed includes it (see fetchFeed).
    if (visibility !== "private") {
      const optimistic: SharedPost = {
        id: postId,
        authorUid: uid,
        authorUsername: attribution === "username" ? (state.profile?.username || "") : "",
        text,
        visibility,
        attribution,
        createdAt: new Date(),
        sharedAt: new Date(),
        replyCount: 0,
        resonanceCount: 0,
        uniqueReplierCount: 0,
        uniqueViewerCount: 0,
        maxReplyDepth: 0,
        heatScore: 0,
        replyRate: 0,
        cardResponseLabel: null,
        cardDiscussingText: null,
        allowsAnonymousReplies: attribution === "anonymous",
        anonymousColorIndex: attribution === "anonymous" ? 0 : null,
      };
      state.recentlyPublishedPosts = [
        optimistic,
        ...state.recentlyPublishedPosts.filter((p) => p.id !== postId),
      ].slice(0, 10);
      // Take the author straight to Discovery so they SEE the thought land.
      state.activeTab = "feed";
      state.selectedDiscoveryDirection = null;
      state.feedVisibleLimit = 15;
    }
    showToast(visibility === "private" ? "Note saved." : visibility === "public" ? "Thought published." : "Thought shared with friends.");
    state.composer.text = "";
    state.composer.placeholder = "";
    shouldPrepareExcerpt = state.composer.matchConsent && visibility === "friends";
    state.composer.visibility = "private";
    state.composer.attribution = "anonymous";
    state.composer.matchConsent = true;
    state.composer.shareExpanded = false;
    state.composer.safeReview = null;
    _render();
    if (visibility !== "private") await fetchFeed();
    await fetchNotes();
  } catch (error) {
    console.warn("[LOT] post-publish follow-up failed (post itself was saved)", error);
  } finally {
    _setBusy("create-post", false);
  }

  runInBackground(async () => {
    if (shouldPrepareExcerpt) {
      try {
        await prepareThoughtMatchExcerpt(postId, visibility, text);
      } catch (error) {
        showToast((error as Error).message);
      }
    }
    await analyzeNoteIfEnabled(state.notes.find((note) => note.id === postId));
    await fetchThinkingTimeline({ refresh: true });
    await rebuildCompanionPortrait();
    _render();
  });
}

export async function deleteSharedPost(postId: string): Promise<void> {
  if (!state.user) return;
  _setBusy(`delete-post-${postId}`, true);
  try {
    const batch = writeBatch(db);
    batch.delete(doc(db, "sharedPosts", postId));
    batch.set(doc(db, "users", state.user.uid, "entries", postId), {
      deleted: true,
      updatedAt: serverTimestamp(),
      schemaVersion: 2,
      ciphertextBase64: deleteField(),
      ciphertext: deleteField(),
      text: deleteField(),
    }, { merge: true });
    await batch.commit();
    state.feedPosts = state.feedPosts.filter((p) => p.id !== postId);
    state.recentlyPublishedPosts = state.recentlyPublishedPosts.filter((p) => p.id !== postId);
    state.notes = state.notes.filter((note) => note.id !== postId);
    delete state.noteInsights[postId];
    showToast("Post deleted.");
    _render();
    runInBackground(async () => {
      await rebuildCompanionPortrait();
      _render();
    });
  } catch { showToast("Could not delete post."); }
  finally { _setBusy(`delete-post-${postId}`, false); }
}

export async function hideSharedPost(postId: string): Promise<void> {
  if (!state.user) return;
  const hidden = new Set([...state.hiddenFeedPostIds, postId]);
  state.hiddenFeedPostIds = Array.from(hidden);
  saveHiddenFeedPostIds(state.user.uid, state.hiddenFeedPostIds);
  removePostFromFeedState(postId);
  showToast("Post hidden.");
  _render();
}

export async function reportSharedPost(postId: string): Promise<void> {
  if (!state.user) return;
  const post = state.feedPosts.find((p) => p.id === postId) || (state.threadModal?.post.id === postId ? state.threadModal.post : null);
  if (!post) return;
  _setBusy(`report-post-${postId}`, true);
  try {
    await addDoc(collection(db, "reports"), {
      reportedBy: state.user.uid,
      contentType: "shared_post",
      contentId: post.id,
      reason: "objectionable_content",
      createdAt: serverTimestamp(),
      status: "pending",
      authorUid: post.authorUid,
      postId,
    });
    await hideSharedPost(postId);
    showToast("Report submitted. We review reports within 24 hours.");
  } catch {
    showToast("Could not report post.");
  } finally {
    _setBusy(`report-post-${postId}`, false);
  }
}

export async function blockSharedPostAuthor(postId: string): Promise<void> {
  if (!state.user) return;
  const post = state.feedPosts.find((p) => p.id === postId) || (state.threadModal?.post.id === postId ? state.threadModal.post : null);
  if (!post || !post.authorUid || post.authorUid === state.user.uid) return;
  _setBusy(`block-user-${post.authorUid}`, true);
  try {
    const now = serverTimestamp();
    const batch = writeBatch(db);
    batch.set(doc(db, "users", state.user.uid, "blockedUsers", post.authorUid), {
      uid: post.authorUid,
      blockedAt: now,
      updatedAt: now,
    }, { merge: true });
    batch.set(doc(db, "users", state.user.uid, "friends", post.authorUid), {
      friendUid: post.authorUid,
      status: "blocked",
      updatedAt: now,
    }, { merge: true });
    batch.set(doc(collection(db, "reports")), {
      reportedBy: state.user.uid,
      contentType: "user",
      contentId: post.authorUid,
      reason: "blocked_user",
      createdAt: now,
      status: "pending",
      blockedUid: post.authorUid,
      sourcePostId: postId,
    });
    await batch.commit();
    state.hiddenFeedPostIds = [...new Set([...state.hiddenFeedPostIds, postId])];
    saveHiddenFeedPostIds(state.user.uid, state.hiddenFeedPostIds);
    state.feedPosts
      .filter((item) => item.authorUid === post.authorUid)
      .forEach((item) => removePostFromFeedState(item.id));
    showToast("User blocked.");
    _render();
  } catch {
    showToast("Could not block user.");
  } finally {
    _setBusy(`block-user-${post.authorUid}`, false);
  }
}

// ── Threads ─────────────────────────────────────────────────────────────────

export async function fetchReplies(scope: ThreadScope, postId: string, communityId: string | null = null): Promise<Reply[]> {
  let replies: Reply[] = [];

  try {
    const snapshotRef = scope === "shared"
      ? doc(db, "sharedPosts", postId, "threadSnapshots", "thread_v2")
      : doc(db, "groups", communityId!, "posts", postId, "threadSnapshots", "thread_v2");
    const snapV2 = await getDoc(snapshotRef);
    if (snapV2.exists() && Array.isArray(snapV2.data().replies)) {
      replies = (snapV2.data().replies as Record<string, unknown>[]).map((item) => ({
        id: item.id as string,
        postId,
        parentReplyId: (item.parentReplyId as string) || null,
        authorUid: (item.authorUid as string) || "",
        authorUsername: (item.authorUsername as string) || "",
        text: item.text as string,
        attribution: (((item.attribution as string) || "username") as "anonymous" | "username"),
        createdAt: timestampToDate(item.createdAt as never),
        depth: Number(item.depth || 1),
        path: (item.path as string) || (item.id as string),
        anonymousColorIndex: item.anonymousColorIndex == null ? null : Number(item.anonymousColorIndex),
      }));
    }
  } catch { /* fallback */ }

  if (replies.length === 0) {
    const collPath = scope === "shared" ? collection(db, "sharedPosts", postId, "replies") : collection(db, "groups", communityId!, "posts", postId, "replies");
    const q = query(collPath, orderBy("createdAt", "asc"), limit(220));
    const snap = await getDocs(q);
    replies = snap.docs.map((item) => ({
      id: item.id,
      postId,
      parentReplyId: (item.data().parentReplyId as string) || null,
      authorUid: (item.data().authorUid as string) || "",
      authorUsername: (item.data().authorUsername as string) || "",
      text: item.data().text as string,
      attribution: (((item.data().attribution as string) || "username") as "anonymous" | "username"),
      createdAt: timestampToDate(item.data().createdAt),
      depth: Number(item.data().depth || 1),
      path: (item.data().path as string) || item.id,
      anonymousColorIndex: item.data().anonymousColorIndex == null ? null : Number(item.data().anonymousColorIndex),
    }));
  }

  return hydrateUsernamesForReplies(replies, scope === "community");
}

export async function fetchSharedPostById(postId: string): Promise<SharedPost | null> {
  const snap = await safeRun(() => getDoc(doc(db, "sharedPosts", postId)));
  if (!snap || !snap.exists()) return null;
  const data = snap.data();
  const post: SharedPost = {
    id: postId,
    authorUid: data.authorUid as string,
    authorUsername: "",
    text: data.text as string,
    visibility: (data.visibility as string) || "public",
    attribution: ((data.attribution as string) || "anonymous") as "anonymous" | "username",
    createdAt: timestampToDate(data.createdAt),
    sharedAt: timestampToDate(data.sharedAt || data.createdAt),
    replyCount: Number(data.replyCount || 0),
    resonanceCount: Number(data.resonanceCount || 0),
    uniqueReplierCount: Number(data.uniqueReplierCount || 0),
    uniqueViewerCount: Number(data.uniqueViewerCount || 0),
    maxReplyDepth: Number(data.maxReplyDepth || 0),
    heatScore: Number(data.heatScore || 0),
    replyRate: Number(data.replyRate || 0),
    cardResponseLabel: (data?.cardMeta as Record<string, unknown>)?.responseLabel as string || null,
    cardDiscussingText: (data?.cardMeta as Record<string, unknown>)?.discussingText as string || null,
    allowsAnonymousReplies: data.allowsAnonymousReplies === true || ((data.attribution as string) || "anonymous") === "anonymous",
    anonymousColorIndex: data.anonymousColorIndex == null ? null : Number(data.anonymousColorIndex),
  };
  return (await hydrateUsernamesForPosts([post]))[0];
}

export async function fetchCommunityPostById(communityId: string, postId: string): Promise<CommunityPost | null> {
  const snap = await safeRun(() => getDoc(doc(db, "groups", communityId, "posts", postId)));
  if (!snap || !snap.exists()) return null;
  const data = snap.data();
  const post: CommunityPost = {
    id: postId,
    authorUid: data.authorUid as string,
    authorUsername: "",
    text: data.text as string,
    attribution: ((data.attribution as string) || "anonymous") as "anonymous" | "username",
    visibility: "community",
    createdAt: timestampToDate(data.createdAt),
    sharedAt: timestampToDate(data.createdAt),
    replyCount: Number(data.replyCount || 0),
    uniqueReplierCount: Number(data.uniqueReplierCount || 0),
    uniqueViewerCount: Number(data.uniqueViewerCount || 0),
    maxReplyDepth: Number(data.maxReplyDepth || 0),
    heatScore: Number(data.heatScore || 0),
    replyRate: Number(data.replyRate || 0),
    cardResponseLabel: (data?.cardMeta as Record<string, unknown>)?.responseLabel as string || null,
    cardDiscussingText: (data?.cardMeta as Record<string, unknown>)?.discussingText as string || null,
    allowsAnonymousReplies: data.allowsAnonymousReplies === true || ((data.attribution as string) || "anonymous") === "anonymous",
    anonymousColorIndex: data.anonymousColorIndex == null ? null : Number(data.anonymousColorIndex),
  };
  return (await hydrateUsernamesForCommunityPosts([post]))[0];
}

async function refreshThreadPostState(
  scope: ThreadScope,
  postId: string,
  communityId: string | null,
  expectedReplyCount?: number
): Promise<void> {
  const delays = [180, 420, 900];
  let latest: SharedPost | CommunityPost | null = null;

  for (let attempt = 0; attempt < delays.length; attempt += 1) {
    latest = scope === "shared"
      ? await fetchSharedPostById(postId)
      : await fetchCommunityPostById(communityId!, postId);

    if (!latest) break;
    if (typeof expectedReplyCount !== "number" || Number(latest.replyCount || 0) === expectedReplyCount || attempt === delays.length - 1) {
      break;
    }
    await new Promise((resolve) => window.setTimeout(resolve, delays[attempt]));
  }

  if (!latest) return;

  if (scope === "shared") {
    state.feedPosts = state.feedPosts.map((post) => post.id === postId ? latest as SharedPost : post);
  } else {
    state.selectedCommunityPosts = state.selectedCommunityPosts.map((post) => post.id === postId ? latest as CommunityPost : post);
  }

  if (state.threadModal && state.threadModal.scope === scope && state.threadModal.postId === postId) {
    state.threadModal = { ...state.threadModal, post: latest };
  }
}

// ── Seen: web writes the exact same view events iOS does ──────────────────
// One counting pipeline for every platform: a deterministic
// viewEvents/{postId_viewerId} doc that the backend drains into
// viewCount/uniqueViewerCount. Guests count too, under a stable device id,
// mirroring iOS's "device-<id>" viewers.

function webViewerId(): string {
  if (state.user) return state.user.uid;
  const key = "lot_device_id";
  let deviceId = localStorage.getItem(key) || "";
  if (!deviceId) {
    deviceId = `device-web-${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`;
    localStorage.setItem(key, deviceId);
  }
  return deviceId;
}

async function recordPostView(post: SharedPost): Promise<void> {
  const viewerId = webViewerId();
  if (!viewerId || viewerId === post.authorUid) return;
  const seenKey = `viewedPosts_${viewerId}`;
  let seen: string[] = [];
  try { seen = JSON.parse(localStorage.getItem(seenKey) || "[]"); } catch { seen = []; }
  if (seen.includes(post.id)) return;
  try {
    await setDoc(doc(db, "viewEvents", `${post.id}_${viewerId}`), {
      postId: post.id,
      viewerId,
      createdAt: serverTimestamp(),
      isSignedIn: Boolean(state.user),
    }, { merge: true });
    localStorage.setItem(seenKey, JSON.stringify([...seen.slice(-499), post.id]));
  } catch {
    // Best-effort — a failed view event must never surface to the reader.
  }
}

// ── Live counts while a thread is open ─────────────────────────────────────
// The writer watching their own thought should see felt/seen tick, not a
// snapshot from page load. One doc listener, attached only while the modal
// is open, patching count fields in place (never reordering anything).
let threadPostUnsub: (() => void) | null = null;

function stopThreadPostListener(): void {
  threadPostUnsub?.();
  threadPostUnsub = null;
}

function startThreadPostListener(postId: string): void {
  stopThreadPostListener();
  threadPostUnsub = onSnapshot(doc(db, "sharedPosts", postId), (snap) => {
    const data = snap.data();
    if (!data || !state.threadModal || state.threadModal.postId !== postId) return;
    const counts = {
      resonanceCount: Number(data.resonanceCount || 0),
      uniqueViewerCount: Number(data.uniqueViewerCount || 0),
      viewCount: Number(data.viewCount || 0),
      replyCount: Number(data.replyCount || 0),
    };
    const current = state.threadModal.post as SharedPost;
    if (
      Number(current.resonanceCount || 0) === counts.resonanceCount &&
      Number(current.uniqueViewerCount || 0) === counts.uniqueViewerCount &&
      Number(current.replyCount || 0) === counts.replyCount
    ) {
      return;
    }
    state.threadModal = { ...state.threadModal, post: { ...current, ...counts } };
    state.feedPosts = state.feedPosts.map((p) => p.id === postId ? { ...p, ...counts } : p);
    _render();
  }, () => { /* listener errors are non-fatal; refresh still reconciles */ });
}

// Guards openSharedThread/openCommunityThread/closeThreadModal against races:
// tapping post A then B before A's reply fetch resolves must not let A's
// late response overwrite state.threadReplies/threadInsight for thread B.
let threadOpenToken = 0;

/** iOS parity: a shelf's membership is the server's decision, not a client
 * term match. Fetched when a chip is chosen; null while unknown so the
 * renderer can fall back to local matching. */
export async function loadDiscoveryShelf(directionId: string | null): Promise<void> {
  if (!directionId) {
    state.discoveryShelfPostIds = null;
    _render();
    return;
  }
  const requested = directionId;
  const discovery = await fetchDiscoveryResult(requested).catch(() => null);
  if (state.selectedDiscoveryDirection !== requested) return;
  if (!discovery) {
    state.discoveryShelfPostIds = null;
    _render();
    return;
  }
  state.discoveryShelfPostIds = new Set(discovery.rankedIds);
  state.discoveryTagsByPostId = { ...state.discoveryTagsByPostId, ...discovery.tagsByPostId };
  _render();
}

/** Saved thoughts that aren't in the feed are fetched by id so the Saved
 * destination shows everything, not just what Discovery happened to load. */
export async function loadSavedPostDetails(): Promise<void> {
  if (!state.user) return;
  const inFeed = new Set(state.feedPosts.map((post) => post.id));
  const missing = Array.from(state.savedPostIds)
    .filter((id) => !inFeed.has(id) && !state.savedPostsById[id])
    .slice(0, 60);
  if (missing.length === 0) return;
  const fetched = await Promise.all(missing.map((id) => fetchSharedPostById(id).catch(() => null)));
  fetched.forEach((post) => {
    if (post) state.savedPostsById[post.id] = post;
  });
  _render();
}

type EmbeddedSharedPost = {
  status?: string;
  id?: string;
  text?: string;
  attribution?: string;
  authorUid?: string;
  authorUsername?: string;
  replyCount?: number;
  resonanceCount?: number;
  createdAtMs?: number | null;
  sharedAtMs?: number | null;
};

/** The thought the server tucked into the shell for this /t/{id} URL, if
 * it matches. Lets the thread open the instant the app boots. */
function embeddedSharedPost(postId: string): SharedPost | null {
  const raw = (window as unknown as { __LOT_SHARED_POST__?: EmbeddedSharedPost }).__LOT_SHARED_POST__;
  if (!raw || raw.status !== "public" || String(raw.id || "") !== postId || !raw.text) return null;
  const created = raw.createdAtMs ? new Date(raw.createdAtMs) : new Date();
  const shared = raw.sharedAtMs ? new Date(raw.sharedAtMs) : created;
  return {
    id: postId,
    authorUid: String(raw.authorUid || ""),
    authorUsername: String(raw.authorUsername || ""),
    text: String(raw.text),
    visibility: "public",
    attribution: (raw.attribution === "username" ? "username" : "anonymous"),
    createdAt: created,
    sharedAt: shared,
    replyCount: Number(raw.replyCount || 0),
    resonanceCount: Number(raw.resonanceCount || 0),
    uniqueReplierCount: 0,
    uniqueViewerCount: 0,
    maxReplyDepth: 0,
    heatScore: 0,
    replyRate: 0,
    cardResponseLabel: null,
    cardDiscussingText: null,
    allowsAnonymousReplies: raw.attribution !== "username",
    anonymousColorIndex: null,
  };
}

function embeddedSharedPostStatus(postId: string): string | null {
  const raw = (window as unknown as { __LOT_SHARED_POST__?: EmbeddedSharedPost }).__LOT_SHARED_POST__;
  if (!raw || String(raw.id || "") !== postId) return null;
  return String(raw.status || "");
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

export async function openSharedThread(postId: string, initialReplyId: string | null = null): Promise<void> {
  state.sharedThoughtNotice = null;
  // Fastest honest source first: the server-embedded thought, then the feed,
  // then Firestore. A link opened seconds after publishing can arrive before
  // the write is readable, so the read retries briefly instead of giving up.
  let post: SharedPost | null = embeddedSharedPost(postId)
    || state.feedPosts.find((p) => p.id === postId)
    || null;
  if (!post) {
    const attempts = embeddedSharedPostStatus(postId) === "unavailable" ? 3 : 1;
    for (let attempt = 0; attempt < attempts && !post; attempt += 1) {
      if (attempt > 0) await wait(1500);
      post = await fetchSharedPostById(postId).catch(() => null);
    }
  }
  if (!post) {
    // Friends-only thoughts are readable by signed-in friends only; a guest
    // gets the honest reason and a way in, never a silent toast.
    state.sharedThoughtNotice = { postId, reason: state.user ? "unavailable" : "signin" };
    _render();
    return;
  }
  const openToken = ++threadOpenToken;
  state.threadModal = { scope: "shared", postId, post, communityId: null, initialReplyId };
  state.replyText = "";
  state.replyAttribution = "username";
  state.replyingToId = null;
  state.threadLoading = true;
  state.threadInsight = null;
  state.threadInsightLoading = true;
  _render();
  void recordPostView(post as SharedPost);
  startThreadPostListener(postId);
  // An embedded thought has no username yet; resolve it without blocking.
  if (post.attribution === "username" && !post.authorUsername) {
    void hydrateUsernamesForPosts([post]).then(() => {
      if (openToken === threadOpenToken) _render();
    }).catch(() => {});
  }
  try {
    const [replies, insight] = await Promise.all([fetchReplies("shared", postId), fetchThreadInsight("shared", postId)]);
    if (openToken !== threadOpenToken) return; // a different thread was opened (or this one closed) meanwhile
    state.threadReplies = replies;
    state.threadInsight = insight as typeof state.threadInsight;
  } catch {
    if (openToken !== threadOpenToken) return;
    state.threadReplies = [];
    showToast("Could not load responses.");
  } finally {
    if (openToken === threadOpenToken) {
      state.threadLoading = false;
      state.threadInsightLoading = false;
      _render();
    }
  }
}

export async function openCommunityThread(communityId: string, postId: string, initialReplyId: string | null = null): Promise<void> {
  const post = await fetchCommunityPostById(communityId, postId) || state.selectedCommunityPosts.find((p) => p.id === postId);
  if (!post) { showToast("Post unavailable."); return; }
  const openToken = ++threadOpenToken;
  stopThreadPostListener();
  state.threadModal = { scope: "community", postId, post, communityId, initialReplyId };
  state.replyText = "";
  state.replyAttribution = "username";
  state.replyingToId = null;
  state.threadLoading = true;
  state.threadInsight = null;
  state.threadInsightLoading = true;
  _render();
  try {
    const [replies, insight] = await Promise.all([fetchReplies("community", postId, communityId), fetchThreadInsight("community", postId, communityId)]);
    if (openToken !== threadOpenToken) return;
    state.threadReplies = replies;
    state.threadInsight = insight as typeof state.threadInsight;
  } catch {
    if (openToken !== threadOpenToken) return;
    state.threadReplies = [];
    showToast("Could not load responses.");
  } finally {
    if (openToken === threadOpenToken) {
      state.threadLoading = false;
      state.threadInsightLoading = false;
      _render();
    }
  }
}

export async function closeThreadModal(): Promise<void> {
  threadOpenToken += 1; // invalidate any in-flight open*Thread fetch for the thread being closed
  stopThreadPostListener();
  state.threadModal = null;
  state.threadReplies = [];
  state.threadInsight = null;
  state.threadInsightLoading = false;
  state.replyText = "";
  state.replyAttribution = "username";
  state.replyingToId = null;
  _render();
}

export async function createReply(): Promise<void> {
  if (!state.user || !state.threadModal) return;
  if (!canWriteWithEncryption()) { showEncryptionWriteGate(); return; }
  const text = state.replyText.trim();
  if (!text) { showToast("Write a response first."); return; }
  if (containsObjectionableContent(text)) {
    showToast("This response contains language blocked by safety filters. Please revise and try again.");
    return;
  }
  const targetReply = state.replyingToId ? state.threadReplies.find((r) => r.id === state.replyingToId) || null : null;
  const depth = targetReply ? Number(targetReply.depth || 1) + 1 : 1;
  if (depth > 2) { showToast("Depth limit reached for this version."); return; }
  const uid = state.user.uid;
  const { scope, postId, communityId } = state.threadModal;
  const canReplyAnonymously = (scope === "shared" || scope === "community")
    && (state.threadModal.post.attribution === "anonymous" || (state.threadModal.post as SharedPost).allowsAnonymousReplies === true);
  const attribution = canReplyAnonymously && state.replyAttribution === "anonymous" ? "anonymous" : "username";
  _setBusy("create-reply", true);
  try {
    let reply: Reply;
    if (scope === "shared" || scope === "community") {
      const token = await state.user.getIdToken();
      const response = await fetch(scope === "shared" ? createSharedPostReplyURL : createCommunityPostReplyURL, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...(scope === "community" ? { groupId: communityId } : {}),
          postId,
          text,
          attribution,
          parentReplyId: targetReply?.id || null,
        }),
      });
      if (!response.ok) throw new Error("create_reply_failed");
      const raw = await response.json() as Record<string, unknown>;
      reply = {
        id: String(raw.id || ""),
        postId: String(raw.postId || postId),
        parentReplyId: (raw.parentReplyId as string) || null,
        authorUid: (raw.authorUid as string) || "",
        authorUsername: (raw.authorUsername as string) || "",
        text: String(raw.text || text),
        attribution: (((raw.attribution as string) || attribution) as "anonymous" | "username"),
        createdAt: raw.createdAt ? new Date(String(raw.createdAt)) : new Date(),
        depth: Number(raw.depth || depth),
        path: String(raw.path || ""),
        anonymousColorIndex: raw.anonymousColorIndex == null ? null : Number(raw.anonymousColorIndex),
        authorAvatarURL: attribution === "username"
          ? state.profile?.profile?.avatarURL || profileAvatarCache.get(uid)
          : undefined,
      };
    } else {
      const replyRef = doc(collection(db, "groups", communityId!, "posts", postId, "replies"));
      const replyId = replyRef.id;
      const path = targetReply ? `${targetReply.path}.${replyId}` : replyId;
      const payload = { id: replyId, postId, parentReplyId: targetReply?.id || null, authorUid: uid, text, attribution, createdAt: serverTimestamp(), depth, path };
      await setDoc(replyRef, payload, { merge: false });
      reply = {
        id: replyId,
        postId,
        parentReplyId: targetReply?.id || null,
        authorUid: uid,
        authorUsername: state.profile?.username || shortUid(uid),
        text,
        attribution,
        createdAt: new Date(),
        depth,
        path,
        authorAvatarURL: attribution === "username"
          ? state.profile?.profile?.avatarURL || profileAvatarCache.get(uid)
          : undefined,
      };
    }
    state.threadReplies = [...state.threadReplies, reply];
    state.replyText = "";
    state.replyAttribution = "username";
    state.replyingToId = null;
    await refreshThreadPostState(scope, postId, communityId, state.threadReplies.length);
    state.threadInsight = (await fetchThreadInsight(scope, postId, communityId)) as typeof state.threadInsight;
    _render();
  } catch (err) {
    const code = String((err as { code?: string })?.code || "");
    showToast(code.includes("permission-denied") ? "No permission to respond." : "Could not send response.");
  } finally { _setBusy("create-reply", false); }
}

export async function deleteReply(replyId: string): Promise<void> {
  if (!state.user || !state.threadModal) return;
  const reply = state.threadReplies.find((r) => r.id === replyId);
  if (!reply || reply.authorUid !== state.user.uid) return;
  const { scope, postId, communityId } = state.threadModal;
  _setBusy(`delete-reply-${replyId}`, true);
  try {
    const replRef = scope === "shared" ? doc(db, "sharedPosts", postId, "replies", replyId) : doc(db, "groups", communityId!, "posts", postId, "replies", replyId);
    await deleteDoc(replRef);
    state.threadReplies = state.threadReplies.filter((r) => r.id !== replyId);
    if (state.replyingToId === replyId) state.replyingToId = null;
    await refreshThreadPostState(scope, postId, communityId, state.threadReplies.length);
    state.threadInsight = (await fetchThreadInsight(scope, postId, communityId)) as typeof state.threadInsight;
    showToast("Response deleted.");
    _render();
  } catch { showToast("Could not delete response."); }
  finally { _setBusy(`delete-reply-${replyId}`, false); }
}

export async function reportReply(replyId: string): Promise<void> {
  if (!state.user || !state.threadModal) return;
  const reply = state.threadReplies.find((r) => r.id === replyId);
  if (!reply) return;
  const { scope, postId, communityId } = state.threadModal;
  _setBusy(`report-reply-${replyId}`, true);
  try {
    await addDoc(collection(db, "reports"), {
      reportedBy: state.user.uid,
      contentType: scope === "shared" ? "shared_reply" : "community_reply",
      contentId: reply.id,
      reason: "other",
      createdAt: serverTimestamp(),
      status: "pending",
      authorUid: reply.authorUid,
      postId,
      groupId: communityId || "",
    });
    showToast("Report submitted.");
  } catch { showToast("Could not report response."); }
  finally { _setBusy(`report-reply-${replyId}`, false); }
}

// ── Friends ──────────────────────────────────────────────────────────────────
type ProfileSummary = {
  displayName?: string;
  bio?: string;
  avatarURL?: string;
};

function profileSummaryFromData(data: Record<string, unknown> | undefined): ProfileSummary {
  const profile = (data?.profile as ProfileSummary | undefined) || {};
  return {
    displayName: String(profile.displayName || "").trim() || undefined,
    bio: String(profile.bio || "").trim() || undefined,
    avatarURL: String(profile.avatarURL || "").trim() || undefined,
  };
}

async function hydrateFriendSuggestionProfiles(suggestions: FriendSuggestion[]): Promise<FriendSuggestion[]> {
  return Promise.all(suggestions.map(async (suggestion) => {
    if (suggestion.avatarURL && suggestion.displayName && suggestion.bio) return suggestion;
    const snap = await safeRun(() => getDoc(doc(db, "users", suggestion.uid)));
    if (!snap?.exists()) return suggestion;
    const data = snap.data() as Record<string, unknown>;
    const profile = profileSummaryFromData(data);
    const username = String(data.username || suggestion.username || "").trim();
    return {
      ...suggestion,
      username: username || suggestion.username,
      displayName: profile.displayName || suggestion.displayName || "",
      bio: profile.bio || suggestion.bio,
      avatarURL: profile.avatarURL || suggestion.avatarURL,
    };
  }));
}

export async function fetchFriends(): Promise<void> {
  if (!state.user) return;
  const uid = state.user.uid;
  const snap = await safeRun(async () => getDocs(collection(db, "users", uid, "friends")));
  if (!snap) { state.friends = []; return; }
  const friends = await Promise.all(snap.docs.map(async (d) => {
    const data = d.data();
    const friendUid = d.id;
    let username = usernameCache.get(friendUid) || "";
    let profile = data.profile as ProfileSummary | undefined;
    const needsProfileHydration = !profile?.avatarURL || !profile?.bio || !profile?.displayName;
    if (!username || needsProfileHydration) {
      const uSnap = await safeRun(() => getDoc(doc(db, "users", friendUid)));
      if (uSnap?.exists()) {
        const userData = uSnap.data() as Record<string, unknown>;
        username = String(userData.username || username || shortUid(friendUid));
        profile = profileSummaryFromData(userData);
        usernameCache.set(friendUid, username);
      }
    }
    return { friendUid, username, status: data.status as typeof state.friends[0]["status"], profile };
  }));
  state.friends = friends;
}

export async function fetchFriendSuggestions(): Promise<void> {
  if (!state.user) return;
  const uid = state.user.uid;
  const targetLimit = 8;
  const excluded = new Set(state.friends.map((f) => f.friendUid));
  excluded.add(uid);
  const suggestions: FriendSuggestion[] = [];

  const pushSuggestion = (candidate: FriendSuggestion) => {
    if (!candidate.uid || !candidate.username || excluded.has(candidate.uid)) return;
    if (suggestions.some((item) => item.uid === candidate.uid)) return;
    suggestions.push(candidate);
  };

  const commitSuggestions = async () => {
    state.friendSuggestions = await hydrateFriendSuggestionProfiles(suggestions.slice(0, targetLimit));
  };

  try {
    const materializedSnap = await getDocs(
      query(collection(db, "users", uid, "suggestions"), orderBy("score", "desc"), limit(targetLimit))
    );
    (materializedSnap.docs
      .map((item) => ({ id: item.id, ...(item.data() as Record<string, unknown>) })) as Array<Record<string, unknown> & { id: string }>)
      .filter((item) => item.uid && item.username && !excluded.has(String(item.uid)))
      .map((item) => ({
        uid: String(item.uid),
        username: String(item.username),
        displayName: String(item.displayName || ""),
        bio: String(item.bio || ""),
        avatarURL: String(item.avatarURL || ""),
      }))
      .forEach(pushSuggestion);
    if (suggestions.length >= targetLimit) {
      await commitSuggestions();
      return;
    }
  } catch {
    // Fall back to a bounded heuristic below.
  }

  try {
    const snap = await getDocs(
      query(collection(db, "sharedPosts"), where("visibility", "==", "public"), orderBy("sharedAt", "desc"), limit(80))
    );
    const candidateCounts = new Map<string, number>();
    for (const row of snap.docs) {
      const authorUid = String(row.data().authorUid || "");
      if (!authorUid || excluded.has(authorUid)) continue;
      candidateCounts.set(authorUid, (candidateCounts.get(authorUid) || 0) + 1);
    }

    for (const [candidateUid] of [...candidateCounts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 10)) {
      const userSnap = await safeRun(() => getDoc(doc(db, "users", candidateUid)));
      if (!userSnap?.exists()) continue;
      const data = userSnap.data();
      const username = String(data.username || "").trim();
      const profile = profileSummaryFromData(data as Record<string, unknown>);
      if (!username) continue;
      pushSuggestion({
        uid: candidateUid,
        username,
        displayName: profile.displayName || "",
        bio: profile.bio,
        avatarURL: profile.avatarURL,
      });
      if (suggestions.length >= targetLimit) break;
    }
  } catch {
    // Fall through to the generic user fallback.
  }

  if (suggestions.length < targetLimit) {
    try {
      const userSnap = await getDocs(
        query(collection(db, "users"), orderBy("updatedAt", "desc"), limit(80))
      );
      for (const item of userSnap.docs) {
        const candidateUid = item.id;
        if (excluded.has(candidateUid)) continue;
        const data = item.data();
        const username = String(data.username || "").trim();
        const profile = profileSummaryFromData(data as Record<string, unknown>);
        if (!username) continue;
        pushSuggestion({
          uid: candidateUid,
          username,
          displayName: profile.displayName || "",
          bio: profile.bio,
          avatarURL: profile.avatarURL,
        });
        if (suggestions.length >= targetLimit) break;
      }
    } catch {
      // Keep whatever valid suggestions we already have.
    }
  }

  await commitSuggestions();
}

export async function searchUsers(prefix: string): Promise<void> {
  if (!state.user || !prefix.trim()) { state.friendSearchResults = []; _render(); return; }
  const lower = prefix.toLowerCase().trim();
  try {
    const q = query(collection(db, "usernames"), where("__name__", ">=", lower), where("__name__", "<=", lower + "\uf8ff"), limit(20));
    const snap = await getDocs(q);
    const results = await Promise.all(snap.docs.map(async (d) => {
      const friendUid = d.data().uid as string;
      if (!friendUid || friendUid === state.user!.uid) return null;
      const uSnap = await safeRun(() => getDoc(doc(db, "users", friendUid)));
      if (!uSnap?.exists()) return null;
      return { uid: friendUid, username: d.id, profile: profileSummaryFromData(uSnap.data() as Record<string, unknown>) };
    }));
    state.friendSearchResults = results.filter(Boolean) as typeof state.friendSearchResults;
    _render();
  } catch { state.friendSearchResults = []; _render(); }
}

export async function openPublicProfile(uid: string): Promise<void> {
  if (!uid) return;
  _setBusy(`public-profile-${uid}`, true);
  try {
    const userSnap = await safeRun(() => getDoc(doc(db, "users", uid)));
    if (!userSnap?.exists()) {
      showToast("Profile not found.");
      return;
    }
    const userData = userSnap.data() as Record<string, unknown>;
    const username = String(userData.username || "").trim();
    if (!username) {
      showToast("Profile is not public yet.");
      return;
    }
    const profile = profileSummaryFromData(userData);
    usernameCache.set(uid, username);

    const postsSnap = await safeRun(() => getDocs(
      query(
        collection(db, "sharedPosts"),
        where("authorUid", "==", uid),
        where("visibility", "==", "public"),
        limit(60)
      )
    ));

    const posts = (postsSnap?.docs || [])
      .map((docSnap): SharedPost | null => {
        const row = docSnap.data() as Record<string, unknown>;
        const text = String(row.text || "").trim();
        if (!text || row.attribution !== "username") return null;
        const meta = row.cardMeta as Record<string, unknown> | undefined;
        return {
          id: docSnap.id,
          authorUid: uid,
          authorUsername: username,
          text,
          visibility: String(row.visibility || "public"),
          attribution: "username",
          createdAt: timestampToDate(row.createdAt as never),
          sharedAt: timestampToDate((row.sharedAt || row.createdAt) as never),
          replyCount: Number(row.replyCount || 0),
          resonanceCount: Number(row.resonanceCount || 0),
          uniqueReplierCount: Number(row.uniqueReplierCount || 0),
          uniqueViewerCount: Number(row.uniqueViewerCount || 0),
          maxReplyDepth: Number(row.maxReplyDepth || 0),
          heatScore: Number(row.heatScore || 0),
          replyRate: Number(row.replyRate || 0),
          cardResponseLabel: (meta?.responseLabel as string) || null,
          cardDiscussingText: (meta?.discussingText as string) || null,
        };
      })
      .filter(Boolean) as SharedPost[];

    state.publicProfile = {
      uid,
      username,
      displayName: profile.displayName || username,
      bio: profile.bio || "",
      avatarURL: profile.avatarURL || "",
      posts: posts.sort((a, b) => timestampToDate(b.sharedAt || b.createdAt).getTime() - timestampToDate(a.sharedAt || a.createdAt).getTime()),
    };
    _render();
  } catch {
    showToast("Could not open profile.");
  } finally {
    _setBusy(`public-profile-${uid}`, false);
  }
}

export function closePublicProfile(): void {
  state.publicProfile = null;
  _render();
}

export async function sendFriendRequest(targetUid: string): Promise<void> {
  if (!state.user) return;
  const uid = state.user.uid;
  _setBusy(`friend-send-${targetUid}`, true);
  try {
    const myFriendRef = doc(db, "users", uid, "friends", targetUid);
    const theirFriendRef = doc(db, "users", targetUid, "friends", uid);
    const existing = await getDoc(myFriendRef);
    const existingStatus = existing.exists() ? String(existing.data().status || "") : "";
    if (existingStatus === "pending_incoming") {
      await acceptFriendRequest(targetUid);
      return;
    }
    if (existingStatus === "accepted") {
      showToast("You're already friends.");
      return;
    }
    if (existingStatus === "pending_outgoing") {
      showToast("Friend request already sent.");
      return;
    }

    const batch = writeBatch(db);
    batch.set(myFriendRef, {
      friendUid: targetUid,
      status: "pending_outgoing",
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }, { merge: true });
    batch.set(theirFriendRef, {
      friendUid: uid,
      status: "pending_incoming",
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }, { merge: true });
    await batch.commit();
    showToast("Friend request sent.");
    await fetchFriends();
    await safeRun(() => refreshFriendSuggestionsNow());
    await fetchFriendSuggestions();
  } catch { showToast("Could not send friend request."); }
  finally { _setBusy(`friend-send-${targetUid}`, false); }
}

export async function acceptFriendRequest(senderUid: string): Promise<void> {
  if (!state.user) return;
  const uid = state.user.uid;
  _setBusy(`friend-accept-${senderUid}`, true);
  try {
    const myFriendRef = doc(db, "users", uid, "friends", senderUid);
    const theirFriendRef = doc(db, "users", senderUid, "friends", uid);
    const [myExisting, theirExisting] = await Promise.all([
      getDoc(myFriendRef),
      getDoc(theirFriendRef),
    ]);
    const myCreatedAt = myExisting.exists()
      ? (myExisting.data().createdAt || serverTimestamp())
      : serverTimestamp();
    const theirCreatedAt = theirExisting.exists()
      ? (theirExisting.data().createdAt || serverTimestamp())
      : serverTimestamp();
    const batch = writeBatch(db);
    batch.set(myFriendRef, {
      friendUid: senderUid,
      status: "accepted",
      createdAt: myCreatedAt,
      updatedAt: serverTimestamp(),
    }, { merge: true });
    batch.set(theirFriendRef, {
      friendUid: uid,
      status: "accepted",
      createdAt: theirCreatedAt,
      updatedAt: serverTimestamp(),
    }, { merge: true });
    await batch.commit();
    showToast("Friend request accepted.");
    await fetchFriends();
    await safeRun(() => refreshFriendSuggestionsNow());
    await fetchFriendSuggestions();
  } catch { showToast("Could not accept friend request."); }
  finally { _setBusy(`friend-accept-${senderUid}`, false); }
}

export async function declineFriendRequest(senderUid: string): Promise<void> {
  if (!state.user) return;
  const uid = state.user.uid;
  _setBusy(`friend-decline-${senderUid}`, true);
  try {
    const batch = writeBatch(db);
    batch.delete(doc(db, "users", uid, "friends", senderUid));
    batch.delete(doc(db, "users", senderUid, "friends", uid));
    await batch.commit();
    showToast("Friend request declined.");
    await fetchFriends();
    await safeRun(() => refreshFriendSuggestionsNow());
    await fetchFriendSuggestions();
  } catch { showToast("Could not decline friend request."); }
  finally { _setBusy(`friend-decline-${senderUid}`, false); }
}

export async function removeFriend(friendUid: string): Promise<void> {
  if (!state.user) return;
  const uid = state.user.uid;
  _setBusy(`friend-remove-${friendUid}`, true);
  try {
    const batch = writeBatch(db);
    batch.delete(doc(db, "users", uid, "friends", friendUid));
    batch.delete(doc(db, "users", friendUid, "friends", uid));
    await batch.commit();
    state.friends = state.friends.filter((f) => f.friendUid !== friendUid);
    await safeRun(() => refreshFriendSuggestionsNow());
    await fetchFriendSuggestions();
    showToast("Friend removed.");
    _render();
  } catch { showToast("Could not remove friend."); }
  finally { _setBusy(`friend-remove-${friendUid}`, false); }
}

// ── Communities ──────────────────────────────────────────────────────────────
export async function fetchCommunities(): Promise<void> {
  if (!state.user) return;
  const uid = state.user.uid;
  const mySnap = await safeRun(async () => getDocs(query(collectionGroup(db, "members"), where("uid", "==", uid), limit(120))));
  const myGroupIds = new Set<string>();
  if (mySnap) for (const d of mySnap.docs) { const gid = d.ref.parent.parent?.id; if (gid) myGroupIds.add(gid); }
  const groups = (await Promise.all([...myGroupIds].map(async (gid) => { const s = await getDoc(doc(db, "groups", gid)); return s.exists() ? { id: gid, ...s.data() } : null; }))).filter(Boolean) as typeof state.communitiesMy;
  state.communitiesMy = groups.sort((a, b) => timestampToDate(b.updatedAt).getTime() - timestampToDate(a.updatedAt).getTime());
  const pubSnap = await safeRun(async () => getDocs(query(collection(db, "groups"), where("visibility", "==", "public"), orderBy("createdAt", "desc"), limit(80))));
  state.communitiesPublic = pubSnap ? pubSnap.docs.map((d) => ({ id: d.id, ...d.data() } as typeof state.communitiesPublic[0])).filter((g) => !myGroupIds.has(g.id)) : [];
  const invSnap = await safeRun(async () => getDocs(query(collectionGroup(db, "invites"), where("inviteeUid", "==", uid), where("status", "==", "pending"), limit(80))));
  const invGroupIds = new Set<string>();
  if (invSnap) for (const d of invSnap.docs) { const gid = d.ref.parent.parent?.id; if (gid) invGroupIds.add(gid); }
  state.communitiesInvites = (await Promise.all([...invGroupIds].map(async (gid) => { const s = await getDoc(doc(db, "groups", gid)); return s.exists() ? { id: gid, ...s.data() } : null; }))).filter(Boolean) as typeof state.communitiesInvites;
  if (state.selectedCommunityId && !state.communitiesMy.some((g) => g.id === state.selectedCommunityId)) {
    state.selectedCommunityId = null; state.selectedCommunity = null; state.selectedCommunityPosts = []; state.selectedCommunityMembers = []; state.selectedCommunityRole = null;
  }
  if (!state.selectedCommunityId && state.communitiesMy.length > 0) await openCommunity(state.communitiesMy[0].id);
}

export async function createCommunity(form: { name: string; purpose: string; visibility: string }): Promise<boolean> {
  if (!state.user) return false;
  const name = form.name.trim();
  const purpose = form.purpose.trim();
  if (!name || name.length > 30) { showToast("Community name must be 1-30 characters."); return false; }
  if (!purpose || purpose.length > 120) { showToast("Purpose must be 1-120 characters."); return false; }
  _setBusy("create-community", true);
  try {
    const uid = state.user.uid;
    const communityRef = doc(collection(db, "groups"));
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    await setDoc(communityRef, { name, purpose, visibility: form.visibility, ownerUid: uid, memberCount: 1, createdAt: serverTimestamp(), updatedAt: serverTimestamp(), timezone, codeJoinEnabled: false, joinCode: "", joinCodeUpdatedAt: serverTimestamp() });
    await setDoc(doc(db, "groups", communityRef.id, "members", uid), { uid, role: "owner", joinedAt: serverTimestamp() });
    showToast("Community created.");
    await fetchCommunities();
    await openCommunity(communityRef.id);
    return true;
  } catch (error) {
    console.warn("createCommunity failed", error);
    showToast("Could not create community.");
    return false;
  }
  finally { _setBusy("create-community", false); }
}

export async function openCommunity(communityId: string): Promise<void> {
  if (!state.user) return;
  state.selectedCommunityId = communityId;
  state.selectedCommunity = null;
  state.selectedCommunityPosts = [];
  state.selectedCommunityMembers = [];
  state.selectedCommunityRole = null;
  _render();
  const groupSnap = await safeRun(() => getDoc(doc(db, "groups", communityId)), "Could not open community.");
  if (!groupSnap || !groupSnap.exists()) return;
  state.selectedCommunity = { id: communityId, ...groupSnap.data() } as NonNullable<typeof state.selectedCommunity>;
  const roleSnap = await safeRun(() => getDoc(doc(db, "groups", communityId, "members", state.user!.uid)));
  state.selectedCommunityRole = (roleSnap?.exists() ? roleSnap.data().role : null) as typeof state.selectedCommunityRole;
  const membersSnap = await safeRun(async () => getDocs(query(collection(db, "groups", communityId, "members"), orderBy("joinedAt", "asc"), limit(100))));
  if (membersSnap) {
    state.selectedCommunityMembers = membersSnap.docs.map((d) => ({ id: d.id, ...d.data() } as typeof state.selectedCommunityMembers[0]));
    await Promise.all(state.selectedCommunityMembers.map(async (m) => {
      if (usernameCache.has(m.uid)) return;
      const s = await getDoc(doc(db, "users", m.uid));
      if (s.exists() && s.data().username) usernameCache.set(m.uid, s.data().username as string);
    }));
  }
  await fetchCommunityPosts(communityId);
  _render();
}

export async function fetchCommunityPosts(communityId: string): Promise<void> {
  if (!state.user) return;
  const posts: CommunityPost[] = [];
  try {
    const feedSnap = await getDoc(doc(db, "groups", communityId, "feeds", "feed_v2", "pages", "0001"));
    if (feedSnap.exists()) {
      const cards = (feedSnap.data().cards || []) as Record<string, unknown>[];
      for (const card of cards) { const p = parseCommunityPostFromCard(card); if (p) posts.push(p); }
    }
  } catch { /* fallback */ }
  if (posts.length === 0) {
    const snap = await safeRun(async () => getDocs(query(collection(db, "groups", communityId, "posts"), orderBy("createdAt", "desc"), limit(220))));
    if (snap) {
      for (const item of snap.docs) {
        const data = item.data();
        posts.push({ id: item.id, authorUid: data.authorUid as string, authorUsername: "", text: data.text as string, attribution: ((data.attribution as string) || "anonymous") as "anonymous" | "username", visibility: "community", createdAt: timestampToDate(data.createdAt), sharedAt: timestampToDate(data.createdAt), replyCount: Number(data.replyCount || 0), uniqueReplierCount: Number(data.uniqueReplierCount || 0), uniqueViewerCount: Number(data.uniqueViewerCount || 0), maxReplyDepth: Number(data.maxReplyDepth || 0), heatScore: Number(data.heatScore || 0), replyRate: Number(data.replyRate || 0), cardResponseLabel: (data?.cardMeta as Record<string, unknown>)?.responseLabel as string || null, cardDiscussingText: (data?.cardMeta as Record<string, unknown>)?.discussingText as string || null, allowsAnonymousReplies: data.allowsAnonymousReplies === true || ((data.attribution as string) || "anonymous") === "anonymous", anonymousColorIndex: data.anonymousColorIndex == null ? null : Number(data.anonymousColorIndex) });
      }
    }
  }
  let hydrated = await hydrateUsernamesForCommunityPosts(posts);
  if (state.communitySort === "active") {
    hydrated.sort((a, b) => activeCommunityScore(b) - activeCommunityScore(a));
  } else {
    hydrated.sort((a, b) => timestampToDate(b.createdAt).getTime() - timestampToDate(a.createdAt).getTime());
  }
  state.selectedCommunityPosts = hydrated;
}

export async function createCommunityPost(communityId: string, textRaw: string, attribution: string): Promise<void> {
  if (!state.user) return;
  if (!canWriteWithEncryption()) { showEncryptionWriteGate(); return; }
  const text = textRaw.trim();
  if (!text) { showToast("Write something first."); return; }
  if (containsObjectionableContent(text)) {
    showToast("This post contains language blocked by safety filters. Please revise and try again.");
    return;
  }
  _setBusy("create-community-post", true);
  try {
    const postRef = doc(collection(db, "groups", communityId, "posts"));
    await setDoc(postRef, { authorUid: state.user.uid, text, attribution, createdAt: serverTimestamp(), replyCount: 0, uniqueReplierCount: 0, uniqueViewerCount: 0, maxReplyDepth: 0, heatScore: 0, replyRate: 0, explorationStatus: "exploring", explorationTargetViews: 30, ...(attribution === "anonymous" ? { allowsAnonymousReplies: true, anonymousColorIndex: 0 } : {}) });
    showToast("Community post published.");
    state.communityPostDraft = { text: "", attribution: "anonymous" };
    await fetchCommunityPosts(communityId);
    _render();
  } catch { showToast("Could not publish community post."); }
  finally { _setBusy("create-community-post", false); }
}

export async function deleteCommunityPost(communityId: string, postId: string): Promise<void> {
  if (!state.user) return;
  _setBusy(`delete-community-post-${postId}`, true);
  try {
    await deleteDoc(doc(db, "groups", communityId, "posts", postId));
    state.selectedCommunityPosts = state.selectedCommunityPosts.filter((p) => p.id !== postId);
    showToast("Community post deleted.");
    _render();
  } catch { showToast("Could not delete community post."); }
  finally { _setBusy(`delete-community-post-${postId}`, false); }
}

async function deleteDocumentRefs(refs: DocumentReference[]): Promise<void> {
  for (let i = 0; i < refs.length; i += 400) {
    const batch = writeBatch(db);
    refs.slice(i, i + 400).forEach((ref) => batch.delete(ref));
    await batch.commit();
  }
}

export async function deleteCommunity(communityId: string): Promise<void> {
  if (!state.user) return;
  const uid = state.user.uid;
  _setBusy(`delete-community-${communityId}`, true);
  try {
    const groupRef = doc(db, "groups", communityId);
    const groupSnap = await getDoc(groupRef);
    if (!groupSnap.exists()) {
      showToast("Community no longer exists.");
      return;
    }

    const memberSnap = await getDoc(doc(db, "groups", communityId, "members", uid));
    const isOwner = groupSnap.data().ownerUid === uid || memberSnap.data()?.role === "owner";
    if (!isOwner) {
      showToast("Only the owner can delete this community.");
      return;
    }

    const refsToDelete: DocumentReference[] = [];
    const joinCode = String(groupSnap.data().joinCode || "").trim();
    if (joinCode) refsToDelete.push(doc(db, "communityJoinCodes", normalizeJoinCode(joinCode)));

    const [membersSnap, invitesSnap, postsSnap, feedPagesSnap] = await Promise.all([
      getDocs(collection(db, "groups", communityId, "members")),
      getDocs(collection(db, "groups", communityId, "invites")),
      getDocs(collection(db, "groups", communityId, "posts")),
      getDocs(collection(db, "groups", communityId, "feeds", "feed_v2", "pages")),
    ]);

    membersSnap.docs.forEach((item) => refsToDelete.push(item.ref));
    invitesSnap.docs.forEach((item) => refsToDelete.push(item.ref));
    feedPagesSnap.docs.forEach((item) => refsToDelete.push(item.ref));
    refsToDelete.push(doc(db, "groups", communityId, "feeds", "feed_v2"));

    for (const postDoc of postsSnap.docs) {
      const [repliesSnap, snapshotsSnap] = await Promise.all([
        getDocs(collection(db, "groups", communityId, "posts", postDoc.id, "replies")),
        getDocs(collection(db, "groups", communityId, "posts", postDoc.id, "threadSnapshots")),
      ]);
      repliesSnap.docs.forEach((item) => refsToDelete.push(item.ref));
      snapshotsSnap.docs.forEach((item) => refsToDelete.push(item.ref));
      refsToDelete.push(doc(db, "groups", communityId, "posts", postDoc.id, "ai", "v1"));
      refsToDelete.push(postDoc.ref);
    }

    refsToDelete.push(groupRef);
    await deleteDocumentRefs(refsToDelete);

    state.communitiesMy = state.communitiesMy.filter((group) => group.id !== communityId);
    state.communitiesPublic = state.communitiesPublic.filter((group) => group.id !== communityId);
    state.communitiesInvites = state.communitiesInvites.filter((group) => group.id !== communityId);
    if (state.selectedCommunityId === communityId) {
      state.selectedCommunityId = null;
      state.selectedCommunity = null;
      state.selectedCommunityPosts = [];
      state.selectedCommunityMembers = [];
      state.selectedCommunityRole = null;
      state.communitySubPage = "browse";
      state.communityDetailTab = "posts";
    }
    showToast("Community deleted.");
    _render();
  } catch (error) {
    console.warn("deleteCommunity failed", error);
    showToast("Could not delete community.");
  } finally {
    _setBusy(`delete-community-${communityId}`, false);
  }
}

export async function joinCommunity(group: typeof state.communitiesPublic[0]): Promise<void> {
  if (!state.user) return;
  const uid = state.user.uid;
  const communityId = group.id;
  _setBusy(`join-community-${communityId}`, true);
  try {
    if (group.visibility === "private") { showToast(group.codeJoinEnabled ? "Use Join by Code for this private community." : "This community is invite-only."); return; }
    const memberRef = doc(db, "groups", communityId, "members", uid);
    if (!(await getDoc(memberRef)).exists()) {
      await setDoc(memberRef, { uid, role: "member", joinedAt: serverTimestamp() });
      await safeRun(async () => updateDoc(doc(db, "groups", communityId), { memberCount: increment(1), updatedAt: serverTimestamp() }));
    }
    showToast("Joined community.");
    await fetchCommunities();
    await openCommunity(communityId);
  } catch { showToast("Could not join community."); }
  finally { _setBusy(`join-community-${communityId}`, false); }
}

export async function joinByCode(inputCode: string): Promise<void> {
  if (!state.user) return;
  const uid = state.user.uid;
  const code = normalizeJoinCode(inputCode);
  if (!code) { showToast("Enter a valid code."); return; }
  _setBusy("join-by-code", true);
  try {
    let groupId: string | null = null;
    const codeSnap = await getDoc(doc(db, "communityJoinCodes", code));
    if (codeSnap.exists()) groupId = codeSnap.data().groupId as string;
    if (!groupId) {
      const fbSnap = await getDocs(query(collection(db, "groups"), where("codeJoinEnabled", "==", true), where("joinCode", "==", code), limit(1)));
      if (!fbSnap.empty) groupId = fbSnap.docs[0].id;
    }
    if (!groupId) { showToast("Invalid join code."); return; }
    const groupSnap = await getDoc(doc(db, "groups", groupId));
    if (!groupSnap.exists()) { showToast("Community not found."); return; }
    const group = { id: groupId, ...groupSnap.data() } as {
      id: string;
      codeJoinEnabled?: boolean;
      joinCode?: string;
      name?: string;
    };
    if (!group.codeJoinEnabled) { showToast("Code join is disabled for this community."); return; }
    if ((group.joinCode as string || "").toUpperCase() !== code) { showToast("Invalid join code."); return; }
    const memberRef = doc(db, "groups", groupId, "members", uid);
    if (!(await getDoc(memberRef)).exists()) {
      const inviteRef = doc(db, "groups", groupId, "invites", uid);
      await setDoc(inviteRef, { inviteeUid: uid, invitedByUid: uid, createdAt: serverTimestamp(), status: "pending", joinCode: code }, { merge: false });
      await setDoc(memberRef, { uid, role: "member", joinedAt: serverTimestamp() });
      await deleteDoc(inviteRef);
      await safeRun(async () => updateDoc(doc(db, "groups", groupId!), { memberCount: increment(1), updatedAt: serverTimestamp() }));
    }
    showToast(`Joined ${group.name as string}.`);
    state.joinCodeInput = "";
    await fetchCommunities();
    await openCommunity(groupId);
  } catch { showToast("Could not join with this code."); }
  finally { _setBusy("join-by-code", false); }
}

export async function leaveCommunity(communityId: string): Promise<void> {
  if (!state.user) return;
  const uid = state.user.uid;
  _setBusy(`leave-community-${communityId}`, true);
  try {
    const memberSnap = await getDoc(doc(db, "groups", communityId, "members", uid));
    if (memberSnap.exists()) {
      if (memberSnap.data().role === "owner") { showToast("Owners cannot leave their own community."); return; }
      await deleteDoc(doc(db, "groups", communityId, "members", uid));
      await safeRun(async () => updateDoc(doc(db, "groups", communityId), { memberCount: increment(-1), updatedAt: serverTimestamp() }));
    }
    if (state.selectedCommunityId === communityId) {
      state.selectedCommunityId = null; state.selectedCommunity = null; state.selectedCommunityPosts = []; state.selectedCommunityMembers = [];
    }
    await fetchCommunities();
    showToast("Left community.");
  } catch { showToast("Could not leave community."); }
  finally { _setBusy(`leave-community-${communityId}`, false); }
}

export async function acceptInvite(communityId: string): Promise<void> {
  if (!state.user) return;
  const uid = state.user.uid;
  _setBusy(`invite-accept-${communityId}`, true);
  try {
    const inviteRef = doc(db, "groups", communityId, "invites", uid);
    if (!(await getDoc(inviteRef)).exists()) { showToast("Invite no longer exists."); return; }
    const memberRef = doc(db, "groups", communityId, "members", uid);
    if (!(await getDoc(memberRef)).exists()) {
      await setDoc(memberRef, { uid, role: "member", joinedAt: serverTimestamp() });
      await deleteDoc(inviteRef);
      await safeRun(async () => updateDoc(doc(db, "groups", communityId), { memberCount: increment(1), updatedAt: serverTimestamp() }));
    } else { await deleteDoc(inviteRef); }
    showToast("Invite accepted.");
    await fetchCommunities();
    await openCommunity(communityId);
  } catch { showToast("Could not accept invite."); }
  finally { _setBusy(`invite-accept-${communityId}`, false); }
}

export async function denyInvite(communityId: string): Promise<void> {
  if (!state.user) return;
  _setBusy(`invite-deny-${communityId}`, true);
  try {
    await deleteDoc(doc(db, "groups", communityId, "invites", state.user.uid));
    showToast("Invite declined.");
    await fetchCommunities();
  } catch { showToast("Could not decline invite."); }
  finally { _setBusy(`invite-deny-${communityId}`, false); }
}

export async function inviteByUsername(communityId: string, usernameRaw: string): Promise<void> {
  if (!state.user) return;
  const username = usernameRaw.trim().toLowerCase();
  if (!username) return;
  _setBusy("invite-user", true);
  try {
    const uSnap = await getDoc(doc(db, "usernames", username));
    if (!uSnap.exists()) { showToast("User not found."); return; }
    const inviteeUid = uSnap.data().uid as string;
    if (!inviteeUid || inviteeUid === state.user.uid) { showToast("Cannot invite this user."); return; }
    const group = state.selectedCommunity;
    if (!group) return;
    if (group.visibility === "private") {
      await setDoc(doc(db, "groups", communityId, "invites", inviteeUid), { inviteeUid, invitedByUid: state.user.uid, createdAt: serverTimestamp(), status: "pending" }, { merge: true });
    } else {
      await setDoc(doc(db, "groups", communityId, "members", inviteeUid), { uid: inviteeUid, role: "member", joinedAt: serverTimestamp() }, { merge: true });
      await safeRun(async () => updateDoc(doc(db, "groups", communityId), { memberCount: increment(1), updatedAt: serverTimestamp() }));
    }
    state.communityInviteSearch = "";
    showToast("Invite sent.");
  } catch { showToast("Could not send invite."); }
  finally { _setBusy("invite-user", false); _render(); }
}

export async function removeCommunityMember(communityId: string, memberUid: string): Promise<void> {
  if (!state.user || !communityId || !memberUid) return;
  if (memberUid === state.user.uid) {
    showToast("Use Leave to remove yourself.");
    return;
  }
  _setBusy(`remove-community-member-${memberUid}`, true);
  try {
    const callerSnap = await getDoc(doc(db, "groups", communityId, "members", state.user.uid));
    const callerRole = String(callerSnap.data()?.role || "");
    if (!(callerRole === "owner" || callerRole === "mod")) {
      showToast("Only owners or moderators can remove members.");
      return;
    }

    const targetRef = doc(db, "groups", communityId, "members", memberUid);
    const targetSnap = await getDoc(targetRef);
    if (!targetSnap.exists()) {
      showToast("Member already removed.");
      return;
    }
    if (String(targetSnap.data()?.role || "") === "owner") {
      showToast("Owners cannot be removed.");
      return;
    }

    await deleteDoc(targetRef);
    await safeRun(async () => updateDoc(doc(db, "groups", communityId), {
      memberCount: increment(-1),
      updatedAt: serverTimestamp(),
    }));

    state.selectedCommunityMembers = state.selectedCommunityMembers.filter((member) => member.uid !== memberUid);
    if (state.selectedCommunity) {
      state.selectedCommunity = {
        ...state.selectedCommunity,
        memberCount: Math.max(0, Number(state.selectedCommunity.memberCount || 0) - 1),
      };
    }
    await fetchCommunities();
    showToast("Member removed.");
  } catch {
    showToast("Could not remove member.");
  } finally {
    _setBusy(`remove-community-member-${memberUid}`, false);
    _render();
  }
}

export async function updateJoinCodeSettings(settings: { enabled: boolean; code: string; expiresAt: string }): Promise<void> {
  if (!state.user || !state.selectedCommunity) return;
  const communityId = state.selectedCommunity.id;
  const enabled = settings.enabled;
  const normalizedCode = normalizeJoinCode(settings.code || "");
  const expiresAt = settings.expiresAt ? new Date(settings.expiresAt) : null;
  if (enabled) {
    if (normalizedCode.length < 4 || normalizedCode.length > 16) { showToast("Join code must be 4-16 characters."); return; }
    if (expiresAt && isNaN(expiresAt.getTime())) { showToast("Invalid expiry date."); return; }
  }
  _setBusy("update-join-code", true);
  try {
    const groupRef = doc(db, "groups", communityId);
    const cur = (await getDoc(groupRef)).data();
    if (!cur) { showToast("Community not found."); return; }
    const previousCode = normalizeJoinCode((cur.joinCode as string) || "");
    const batch = writeBatch(db);
    if (enabled) {
      const existing = await getDoc(doc(db, "communityJoinCodes", normalizedCode));
      if (existing.exists() && (existing.data().groupId as string) !== communityId) { showToast("This code is already used."); return; }
      batch.set(doc(db, "communityJoinCodes", normalizedCode), { groupId: communityId, updatedAt: serverTimestamp() }, { merge: true });
    }
    if (previousCode && previousCode !== normalizedCode) batch.delete(doc(db, "communityJoinCodes", previousCode));
    batch.update(groupRef, { codeJoinEnabled: enabled, joinCode: enabled ? normalizedCode : "", joinCodeExpiresAt: enabled && expiresAt ? expiresAt : deleteField(), joinCodeUpdatedAt: serverTimestamp(), updatedAt: serverTimestamp() });
    await batch.commit();
    showToast("Join code settings updated.");
    await openCommunity(communityId);
  } catch { showToast("Could not update join code settings."); }
  finally { _setBusy("update-join-code", false); }
}

// ── Notes ────────────────────────────────────────────────────────────────────
export async function fetchNotes(): Promise<void> {
  if (!state.user) return;
  let snap = await safeRun(async () => getDocs(query(collection(db, "users", state.user!.uid, "entries"), orderBy("updatedAt", "desc"), limit(120))));
  if (!snap || snap.empty) snap = await safeRun(async () => getDocs(query(collection(db, "users", state.user!.uid, "entries"), orderBy("createdAt", "desc"), limit(120))));
  if (!snap || snap.empty) snap = await safeRun(async () => getDocs(query(collection(db, "users", state.user!.uid, "entries"), limit(120))));
  if (!snap) { state.notes = []; return; }
  const unlockedKey = getUnlockedCrossPlatformKey();
  let hasLegacyIOSNotes = false;
  state.notes = (await Promise.all(snap.docs.map(async (item) => {
    const d = item.data() as Record<string, unknown>;
    const hasPlain = typeof d.text === "string" && String(d.text).trim().length > 0;
    const ciphertextBase64 = typeof d.ciphertextBase64 === "string" ? String(d.ciphertextBase64) : "";
    const legacyCiphertext = !ciphertextBase64 ? readFirestoreBytes(d.ciphertext) : null;
    const createdAt = timestampToDate(d.createdAt as never);
    const updatedAt = timestampToDate((d.updatedAt || d.createdAt) as never);
    const visibility = String(d.visibility || "private");
    const attribution: "username" | "anonymous" =
      d.attribution === "username" ? "username" : "anonymous";

    if (hasPlain) {
      return {
        id: item.id,
        text: String(d.text),
        isEncrypted: false,
        visibility,
        attribution,
        createdAt,
        updatedAt,
      };
    }

    if (ciphertextBase64 && unlockedKey) {
      try {
        const decrypted = await decryptCombinedString(base64ToBytes(ciphertextBase64), unlockedKey);
        return {
          id: item.id,
          text: decrypted,
          isEncrypted: false,
          visibility,
          attribution,
          createdAt,
          updatedAt,
        };
      } catch {
        return {
          id: item.id,
          text: "Unlock to read this note. Encrypted with your personal key.",
          isEncrypted: true,
          visibility,
          attribution,
          createdAt,
          updatedAt,
        };
      }
    }

    if (legacyCiphertext && unlockedKey) {
      try {
        const decrypted = await decryptCombinedString(legacyCiphertext, unlockedKey);
        return {
          id: item.id,
          text: decrypted,
          isEncrypted: false,
          visibility,
          attribution,
          createdAt,
          updatedAt,
        };
      } catch {
        hasLegacyIOSNotes = true;
      }
    } else if (legacyCiphertext || d.keyMode === "legacy_ios") {
      hasLegacyIOSNotes = true;
    }

    const encrypted = Boolean(ciphertextBase64 || legacyCiphertext);
    return {
      id: item.id,
      text: encrypted ? "Unlock to read this note. Encrypted with your personal key." : "",
      isEncrypted: encrypted,
      visibility,
      attribution,
      createdAt,
      updatedAt,
    };
  })))
    .filter((n) => !((snap!.docs.find((d) => d.id === n.id)?.data().deleted) || (snap!.docs.find((d) => d.id === n.id)?.data().isDeleted)))
    .filter((n) => n.text.length > 0)
    .sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime());
  state.noteEncryption.hasLegacyIOSNotes = hasLegacyIOSNotes;
}

export async function saveNote(): Promise<void> {
  if (!state.user) return;
  const text = state.noteDraft.text.trim();
  if (!text) { showToast("Write your note first."); return; }
  const unlockedKey = getUnlockedCrossPlatformKey();
  if (!unlockedKey) {
    showToast("Set up or unlock cross-platform note encryption in Settings first.");
    return;
  }
  _setBusy("save-note", true);
  try {
    let targetId = state.noteDraft.id;
    if (state.noteDraft.id) {
      const ciphertextBase64 = await encryptCombinedString(text, unlockedKey);
      await updateDoc(doc(db, "users", state.user.uid, "entries", state.noteDraft.id), {
        ciphertextBase64,
        encryptionVersion: 2,
        keyMode: "cross_platform",
        schemaVersion: 2,
        deleted: false,
        visibility: "private",
        attribution: "anonymous",
        updatedAt: serverTimestamp(),
        text: deleteField(),
        ciphertext: deleteField(),
      });
      showToast("Note updated.");
    } else {
      targetId = makeId();
      await setDoc(
        doc(db, "users", state.user.uid, "entries", targetId),
        await encryptedEntryPayload(text, unlockedKey, "private"),
        { merge: false }
      );
      showToast("Note saved.");
    }
    state.noteDraft = { id: null, text: "" };
    await fetchNotes();
    if (targetId) {
      state.noteInsights[targetId] = {
        ...(state.noteInsights[targetId] || { id: "v1" }),
        status: state.aiPrefs.notesAiEnabled && state.aiPrefs.notesAIDisclosureAccepted ? "pending" : undefined,
      };
      _render();
      runInBackground(async () => {
        await analyzeNoteIfEnabled(state.notes.find((n) => n.id === targetId));
        await fetchThinkingTimeline({ refresh: true });
        await rebuildCompanionPortrait();
      });
    } else {
      runInBackground(async () => {
        await fetchNoteInsights();
        await fetchThinkingTimeline({ refresh: true });
        await rebuildCompanionPortrait();
        _render();
      });
    }
  } catch { showToast("Could not save note."); }
  finally { _setBusy("save-note", false); }
}

export async function deleteNote(noteId: string): Promise<void> {
  if (!state.user) return;
  _setBusy(`delete-note-${noteId}`, true);
  try {
    const batch = writeBatch(db);
    batch.set(doc(db, "users", state.user.uid, "entries", noteId), {
      deleted: true,
      updatedAt: serverTimestamp(),
      schemaVersion: 2,
      ciphertextBase64: deleteField(),
      ciphertext: deleteField(),
      text: deleteField(),
    }, { merge: true });
    batch.delete(doc(db, "sharedPosts", noteId));
    await batch.commit();
    state.notes = state.notes.filter((n) => n.id !== noteId);
    delete state.noteInsights[noteId];
    if (state.noteDraft.id === noteId) state.noteDraft = { id: null, text: "" };
    showToast("Note deleted.");
    _render();
    runInBackground(async () => {
      await rebuildCompanionPortrait();
      _render();
    });
  } catch { showToast("Could not delete note."); }
  finally { _setBusy(`delete-note-${noteId}`, false); }
}

// ── Activity ─────────────────────────────────────────────────────────────────
export async function fetchActivity(): Promise<void> {
  if (!state.user) return;
  const snap = await safeRun(async () => getDocs(query(collection(db, "users", state.user!.uid, "activity"), orderBy("createdAt", "desc"), limit(120))));
  state.activity = snap ? snap.docs.map((d) => ({ id: d.id, ...d.data() } as typeof state.activity[0])) : [];
}

function activityCategory(item: typeof state.activity[number]): string {
  if (item.category) return String(item.category);
  if (item.type === "friend_request") return "friends";
  if (item.type === "community_invite" || item.type === "community_post" || item.type === "community_reply") return "communities";
  return "feed";
}

async function markActivityItemsRead(ids: string[]): Promise<void> {
  if (!state.user || ids.length === 0) return;
  const uniqueIds = [...new Set(ids.filter(Boolean))];
  if (uniqueIds.length === 0) return;

  state.activity = state.activity.map((item) => uniqueIds.includes(item.id) ? { ...item, read: true } : item);
  _render();

  const chunkSize = 50;
  for (let index = 0; index < uniqueIds.length; index += chunkSize) {
    const slice = uniqueIds.slice(index, index + chunkSize);
    await safeRun(async () => {
      const batch = writeBatch(db);
      for (const activityId of slice) {
        batch.set(
          doc(db, "users", state.user!.uid, "activity", activityId),
          { read: true, readAt: serverTimestamp() },
          { merge: true }
        );
      }
      await batch.commit();
    });
  }
}

export async function markActivityRead(activityId: string): Promise<void> {
  await markActivityItemsRead([activityId]);
}

export async function openActivity(activityId: string, setTab: (t: string, push: boolean) => void): Promise<void> {
  const item = state.activity.find((a) => a.id === activityId);
  if (!item) return;
  const category = activityCategory(item);
  let relatedIds = [activityId];

  if (category === "feed") {
    relatedIds = state.activity
      .filter((entry) => !entry.read && activityCategory(entry) === "feed")
      .map((entry) => entry.id);
  } else if (category === "friends") {
    relatedIds = state.activity
      .filter((entry) => !entry.read && activityCategory(entry) === "friends")
      .map((entry) => entry.id);
  } else if (category === "communities") {
    relatedIds = state.activity
      .filter((entry) => !entry.read && activityCategory(entry) === "communities" && (!item.groupId || entry.groupId === item.groupId))
      .map((entry) => entry.id);
  }

  await markActivityItemsRead(relatedIds);
  if (item.type === "friend_request") { setTab("friends", true); _render(); return; }
  if (item.type === "community_invite") { setTab("communities", true); _render(); if (item.groupId) await openCommunity(item.groupId); return; }
  if ((item.type === "friend_post" || item.type === "thought_reply") && item.postId) { setTab("feed", true); _render(); await openSharedThread(item.postId, item.replyId || null); return; }
  if ((item.type === "community_post" || item.type === "community_reply") && item.groupId) { setTab("communities", true); _render(); await openCommunity(item.groupId); if (item.postId) await openCommunityThread(item.groupId, item.postId, item.replyId || null); }
}

async function uploadProfileAvatar(file: File, uid: string): Promise<{ url: string; storagePath: string }> {
  if (!file.type.startsWith("image/")) throw new Error("invalid-image");
  if (file.size > 3 * 1024 * 1024) throw new Error("image-too-large");
  const extension = file.type.includes("png") ? "png" : file.type.includes("webp") ? "webp" : "jpg";
  const storagePath = `profileAvatars/${uid}/${Date.now()}-${makeId()}.${extension}`;
  const avatarRef = ref(storage, storagePath);
  await uploadBytes(avatarRef, file, { contentType: file.type || "image/jpeg" });
  const url = await getDownloadURL(avatarRef);
  return { url, storagePath };
}

async function removeProfileAvatar(storagePath: string | undefined): Promise<void> {
  if (!storagePath) return;
  try {
    await deleteObject(ref(storage, storagePath));
  } catch {
    // Best-effort cleanup; profile fields are still removed below.
  }
}

// ── Profile settings ─────────────────────────────────────────────────────────
export async function saveProfileSettings(form: { username: string; displayName: string; bio: string; avatarFile?: File | null; removeAvatar?: boolean }): Promise<void> {
  if (!state.user) return;
  _setBusy("save-profile", true);
  try {
    const uid = state.user.uid;
    const nextUsername = form.username.trim();
    if (nextUsername && !/^[A-Za-z0-9][A-Za-z0-9_]{2,19}$/.test(nextUsername)) {
      showToast("Username must be 3-20 chars, letters/numbers/underscore."); return;
    }
    const currentLower = state.profile?.usernameLower || "";
    const nextLower = nextUsername ? nextUsername.toLowerCase() : currentLower;
    const batch = writeBatch(db);
    const profileRef = doc(db, "users", uid);
    if (nextUsername && nextLower !== currentLower) {
      const des = await getDoc(doc(db, "usernames", nextLower));
      if (des.exists() && (des.data().uid as string) !== uid) { showToast("Username is already taken."); return; }
      batch.set(doc(db, "usernames", nextLower), { uid, reservedAt: serverTimestamp() }, { merge: true });
      if (currentLower) batch.delete(doc(db, "usernames", currentLower));
    }
    if (nextUsername) {
      batch.set(profileRef, { username: nextUsername, usernameLower: nextLower, updatedAt: serverTimestamp() }, { merge: true });
    }
    await batch.commit();
    const profilePatch: Record<string, unknown> = {
      updatedAt: serverTimestamp(),
      "profile.displayName": form.displayName.trim() ? form.displayName.trim() : deleteField(),
      "profile.bio": form.bio.trim() ? form.bio.trim() : deleteField(),
    };
    const previousAvatarPath = state.profile?.profile?.avatarStoragePath;
    let cleanupAvatarPath: string | undefined;
    if (form.avatarFile) {
      const uploaded = await uploadProfileAvatar(form.avatarFile, uid);
      profilePatch["profile.avatarURL"] = uploaded.url;
      profilePatch["profile.avatarStoragePath"] = uploaded.storagePath;
      profilePatch["profile.avatarUpdatedAt"] = serverTimestamp();
      cleanupAvatarPath = previousAvatarPath && previousAvatarPath !== uploaded.storagePath ? previousAvatarPath : undefined;
    } else if (form.removeAvatar) {
      profilePatch["profile.avatarURL"] = deleteField();
      profilePatch["profile.avatarStoragePath"] = deleteField();
      profilePatch["profile.avatarUpdatedAt"] = deleteField();
      cleanupAvatarPath = previousAvatarPath;
    }
    await updateDoc(profileRef, profilePatch);
    await removeProfileAvatar(cleanupAvatarPath);
    showToast("Profile updated.");
    await fetchProfile();
  } catch (error) {
    const message = error instanceof Error && error.message === "image-too-large"
      ? "Profile image must be under 3 MB."
      : "Could not save profile.";
    showToast(message);
  }
  finally { _setBusy("save-profile", false); }
}

export async function deleteAccountFlow(): Promise<void> {
  if (!state.user) return;
  if (!confirm("Delete account permanently? This cannot be undone.")) return;
  _setBusy("delete-account", true);
  try {
    await safeRun(async () => deleteDoc(doc(db, "users", state.user!.uid)));
    try { await deleteUser(state.user); showToast("Account deleted."); }
    catch { showToast("Account data removed. Please re-authenticate to delete Auth account."); }
  } finally { _setBusy("delete-account", false); }
}

// ── Share ────────────────────────────────────────────────────────────────────
export function sharePost(post: SharedPost | CommunityPost): void {
  const url = `https://lotapp.ai/t/${encodeURIComponent(post.id)}`;
  const text = post.text.length > 130 ? `${post.text.slice(0, 127)}...` : post.text;
  if (navigator.share) {
    navigator.share({ title: "A thought from LOT", text, url }).catch(() => { navigator.clipboard?.writeText(url).catch(() => {}); });
  } else {
    navigator.clipboard?.writeText(url).catch(() => {});
    showToast("Link copied.");
  }
}

// ── Hydrate all ───────────────────────────────────────────────────────────────
export async function hydrateAppData(): Promise<void> {
  if (!state.user) return;
  state.loadingPage = true;
  state.profileResolved = false;
  state.noteEncryptionResolved = false;
  _render();
  try {
    await fetchProfile();
    state.profileResolved = true;
    if (!state.profile?.username) return;
    await Promise.all([
      fetchNotificationPreferences(),
      fetchAIPreferences(),
      fetchDailyPrompt(),
      fetchFriends(),
      fetchNoteEncryptionState(),
      fetchNotes(),
      fetchFeed(),
    ]);
  } catch (err) {
    console.error(err);
    state.profileResolved = true;
    showToast("Some data could not load.");
  } finally {
    state.loadingPage = false;
    _render();
  }
  runInBackground(async () => {
    await Promise.allSettled([
      fetchActivity(),
      fetchFriendSuggestions(),
      fetchCommunities(),
      fetchUserMatches(),
      fetchEchoItems(),
      fetchNoteInsights(),
      fetchThinkingTimeline(),
      loadCompanionContext(),
      startThoughtMatching(),
    ]);
    _render();
    if (state.aiPrefs.notesAiEnabled && state.aiPrefs.notesAIDisclosureAccepted) {
      await backfillRecentNoteInsights(12);
      await fetchThinkingTimeline({ refresh: true });
      await rebuildCompanionPortrait();
    }
  });
}
